import { Connection } from './rpc.js';
import { t, getLocale, setLocale, onLocaleChange, applyTranslations, LOCALES } from './i18n.js';
import { RoomMedia } from './media.js';
import { collectFingerprint, fingerprintWithin } from './fingerprint.js';
import { THEMES, getTheme, setTheme, applyTheme, effectiveTheme, onSystemThemeChange } from './theme.js';
import {
  setStageLayout,
  applyStageLayout,
  effectiveStageLayout,
  onAutoStageChange,
} from './stage.js';
import { JukeboxPlayer } from './jukebox.js';
import { renderStats, statsSummary } from './stats.js';
import { playNotification } from './notify.js';
import { splitLinks, mentionsNickname, insertMention, insertEmote } from './text.js';
import { saveRooms, loadRooms, forgetRoom, clearRooms } from './session-rooms.js';
import {
  isUserHidden,
  hiddenUsers,
  hideUser,
  unhideUser,
  toggleUser,
  clearHidden,
} from './hidden-users.js';
import {
  splitEmotes,
  emoteNode,
  emoteFor,
  codeFor,
  onlyEmotes,
  applyEmoteMetrics,
  EMOTES,
} from './emotes.js';
import { recallVolume, rememberVolume } from './volumes.js';
import { gainForSlider, sliderForGain, JUKEBOX_MIN_DB } from './gain.js';
import { uploadFile, previewKind, ACCEPTED_TYPES } from './uploads.js';
import {
  sortMembers,
  applyJoin as joinMember,
  applyLeave as leaveMember,
  applyUpdate as updateMember,
  applyRename as renameMember,
} from './roster.js';
import { SpeakingMonitor } from './speaking.js';
import { setupFluid, fluidEnabled, setFluidEnabled, fluidSupported } from './fluid.js';
import {
  getSettings,
  updateSettings,
  pruneMissingDevices,
  canChooseOutput,
  canSetVolume,
  masterMuted,
  audioConstraints,
  videoConstraints,
  applyOutput,
  effectiveVolume,
  requestDeviceAccess,
  clearDevice,
  retryWithoutSavedDevice,
  wantsPortraitCapture,
  publisherOrientation,
  rotationForFrame,
} from './devices.js';

const $ = (id) => document.getElementById(id);
const TOKEN_KEY = 'webchat.token';

/**
 * The client build this page was served with.
 *
 * Written into the document by the server (see src/graph.js). Compared against
 * what the server reports over the socket: when they stop matching, the files
 * this tab is running are no longer the ones being served, and the reader is
 * offered a refresh rather than having it done to them.
 */
const CLIENT_BUILD = document.querySelector('meta[name="client-build"]')?.content ?? null;
/** Smallest a camera tile is allowed to get before the stage gives up fitting. */
const MIN_TILE_HEIGHT = 56;

/** Application state. */
const state = {
  conn: null,
  session: null,
  info: null,
  activeRoom: null,
  /**
   * The client build the server is serving, once it has said. Compared against
   * CLIENT_BUILD, which is the one this page loaded; a difference is what puts
   * the update banner up.
   */
  serverBuild: null,
  /** The build the reader has already said "not now" to. */
  updateDismissed: null,
  /** room -> { info, presence: [], messages: [], media: RoomMedia|null, tiles: Map } */
  rooms: new Map(),
  roomList: [],
  /**
   * Open conversations with one other person, keyed by folded nickname.
   *
   * A peer of `rooms` rather than something inside one: a private message is
   * addressed to you and not to any channel, and filing it under whichever room
   * happened to be on screen when it arrived - which is what this used to do -
   * meant the reply landed somewhere else the moment you switched rooms.
   *
   * Exactly one of `activeRoom` and `activeDm` is ever set. Everything that
   * belongs to a channel and not to a conversation - the cameras, the jukebox,
   * the member list, the room menu - is already written to stand down when there
   * is no active room, so opening a conversation puts all of it away by itself.
   *
   * @type {Map<string, { nickname: string, messages: object[], unread: number }>}
   */
  dms: new Map(),
  /** The folded nickname of the conversation on screen, or null. */
  activeDm: null,
  /**
   * The channel that was on screen when a conversation was opened.
   *
   * Two things need it. Closing the conversation goes back there rather than to
   * whichever room happens to be first. And a command typed in a conversation
   * runs against it - which is where the same command would have run a moment
   * earlier, since until now a private message was typed from inside a room.
   */
  lastRoom: null,
  /**
   * System messages that belong to no room - for example "you were banned from
   * #yleinen", which arrives just as that room is being torn down. Without this
   * they would be dropped and the user would be left with no explanation.
   */
  orphan: [],
  /**
   * Messages for a room we have not finished joining yet. The server emits its
   * `room:joined` hooks (the welcome notice, for one) before the join reply
   * arrives, so those would otherwise land nowhere. Flushed by applyJoin().
   * @type {Map<string, object[]>}
   */
  pending: new Map(),
  /** How the session was established, so it can be restored after a drop. */
  resume: { mode: null, nickname: null },
  sidebarOpen: false,
  connection: 'connecting',
};

// --------------------------------------------------------------------- utils

function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (key === 'class') node.className = value;
    else if (key === 'text') node.textContent = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value !== null && value !== undefined && value !== false) {
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

const clock = (ts) =>
  new Date(ts ?? Date.now()).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });

/** Localised date-time, used in ban expiry text. */
const stamp = (ts) => new Date(ts).toLocaleString(getLocale());

/** Localised compact duration, mirroring the server's formatter. */
function formatDuration(ms) {
  if (ms === null || ms === undefined) return t('expiry.permanent');
  const parts = [];
  let rest = Math.max(0, Math.floor(ms / 1000));
  for (const [unit, secs] of [['d', 86400], ['h', 3600], ['m', 60], ['s', 1]]) {
    const value = Math.floor(rest / secs);
    if (value > 0) {
      parts.push(t(`duration.${unit}`, { value }));
      rest -= value * secs;
    }
    if (parts.length === 2) break;
  }
  return parts.join(' ') || t('duration.s', { value: 0 });
}

/** "for 15m" / "permanently", in the reader's language. */
function formatExpiry(expiresAt) {
  if (!expiresAt) return t('expiry.permanent');
  const remaining = expiresAt - Date.now();
  if (remaining <= 0) return t('expiry.expired');
  return t('expiry.for', { duration: formatDuration(remaining) });
}

const sigilFor = (role) => (role === 'root' ? '~' : role === 'operator' ? '@' : role === 'user' ? '+' : '');

/** Human name for a role, in the reader's language. */
function roleLabel(role) {
  return t(`ui.role.${role === 'root' || role === 'operator' || role === 'user' ? role : 'guest'}`);
}

function roleClass(member) {
  if (member.role === 'root') return 'root';
  if (member.role === 'operator') return 'operator';
  return member.guest ? 'guest' : 'user';
}

function errorText(err) {
  if (!err) return t('error.generic');
  return err.message ?? String(err);
}

// --------------------------------------------------------------------- modal

/** Keeps Tab cycling inside a dialog while it is open. */
function trapFocus(container) {
  // Links are in the list because a dialog can hold one - the Ko-fi choice in
  // the ⋯ menu - and a link the trap does not know about is a link Tab cycles
  // straight past.
  const selector =
    'button:not([hidden]):not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const handler = (event) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}

/**
 * Opens the shared dialog.
 *
 * `onConfirm` may throw to report a problem: the message is shown *inside* the
 * dialog and it stays open, rather than being written to the chat pane behind it
 * where nobody would see it.
 */
function openModal({ title, body, confirmText = 'OK', onConfirm, hideConfirm = false, hideCancel = false }) {
  $('modal-title').textContent = title;
  const host = $('modal-body');
  host.replaceChildren(body);
  const errorSlot = $('modal-error');
  errorSlot.textContent = '';
  const confirm = $('modal-confirm');
  confirm.textContent = confirmText;
  confirm.hidden = hideConfirm;
  confirm.disabled = false;
  // A dialog whose changes apply as they are made has nothing to cancel, so it
  // can ask for the button to be dropped rather than offer a misleading one.
  const cancel = $('modal-cancel');
  cancel.hidden = hideCancel;
  const backdrop = $('modal-backdrop');
  backdrop.hidden = false;

  const previouslyFocused = document.activeElement;
  const releaseTrap = trapFocus(backdrop);

  const close = () => {
    backdrop.hidden = true;
    errorSlot.textContent = '';
    confirm.onclick = null;
    $('modal-cancel').onclick = null;
    cancel.hidden = false;
    releaseTrap();
    if (previouslyFocused?.focus) previouslyFocused.focus();
  };

  confirm.onclick = async () => {
    errorSlot.textContent = '';
    confirm.disabled = true;
    try {
      const keepOpen = await onConfirm?.();
      if (!keepOpen) close();
      else confirm.disabled = false;
    } catch (err) {
      errorSlot.textContent = errorText(err);
      confirm.disabled = false;
    }
  };
  $('modal-cancel').onclick = close;

  const first = host.querySelector('input, select, textarea, button');
  if (first) first.focus();
  return close;
}

// ------------------------------------------------------------------ rendering

function roomState(room) {
  if (!state.rooms.has(room)) {
    state.rooms.set(room, {
      info: { name: room, topic: null, mediaEnabled: true },
      presence: [],
      messages: [],
      media: null,
      tiles: new Map(),
      /** The room's shared player, as the server last described it, or null. */
      jukebox: null,
      unread: 0,
    });
  }
  return state.rooms.get(room);
}

/**
 * Room state *only* if we are actually in the room. Unlike roomState() this
 * never creates an entry, so a late or stray event cannot resurrect a room we
 * have left - which would leave the sidebar showing it as joined while the
 * server has us out, and clicking it would then only switch the view instead of
 * rejoining.
 */
function existingRoom(room) {
  return room ? (state.rooms.get(room) ?? null) : null;
}

/**
 * A nickname folded to the key a conversation is filed under.
 *
 * Case only: somebody who signs in as "Alice" after talking to you as "alice" is
 * the same person and the same conversation, and the display name is kept
 * separately so the sidebar shows them as they currently write it.
 */
function dmKey(nickname) {
  return String(nickname ?? '').toLowerCase();
}

/** The conversation with `nickname`, opening one if this is the first message. */
function dmState(nickname) {
  const key = dmKey(nickname);
  if (!key) return null;
  const existing = state.dms.get(key);
  if (existing) {
    // Their capitalisation as of the latest message.
    existing.nickname = nickname;
    return existing;
  }
  const fresh = { nickname, messages: [], unread: 0 };
  state.dms.set(key, fresh);
  return fresh;
}

/** The conversation on screen, or null when a channel is. */
function activeDmState() {
  return state.activeDm ? (state.dms.get(state.activeDm) ?? null) : null;
}

/** Total unread count, mirrored into the tab title. */
function refreshUnreadTitle() {
  let total = 0;
  for (const rs of state.rooms.values()) total += rs.unread ?? 0;
  // Conversations count the same as channels: the number in the tab title is
  // "things waiting for you", and a message addressed to you personally is the
  // last one that should be left out of it.
  for (const dm of state.dms.values()) total += dm.unread ?? 0;
  document.title = total > 0 ? `(${total}) Weppis` : 'Weppis';
}

function clearUnread(room) {
  const rs = state.rooms.get(room);
  if (!rs || !rs.unread) return;
  rs.unread = 0;
  refreshUnreadTitle();
  renderRoomList();
}

function clearDmUnread(key) {
  const dm = state.dms.get(key);
  if (!dm || !dm.unread) return;
  dm.unread = 0;
  refreshUnreadTitle();
  renderDmList();
}

function renderRoomList() {
  const list = $('room-list');
  const joined = new Set(state.rooms.keys());
  const known = new Map(state.roomList.map((r) => [r.name, r]));
  for (const name of joined) if (!known.has(name)) known.set(name, { name, members: 0 });

  const items = [...known.values()].sort(
    (a, b) => Number(joined.has(b.name)) - Number(joined.has(a.name)) || b.members - a.members || a.name.localeCompare(b.name),
  );

  list.replaceChildren(
    ...items.map((room) => {
      const isJoined = joined.has(room.name);
      const rs = state.rooms.get(room.name);
      const unread = isJoined && room.name !== state.activeRoom ? (rs?.unread ?? 0) : 0;
      // For rooms we are in, our own presence list is the freshest count;
      // the server figure is only a fallback for rooms we have not joined.
      const members = isJoined ? (rs?.presence.length ?? room.members ?? 0) : (room.members ?? 0);
      const activate = () => (isJoined ? setActiveRoom(room.name) : joinRoom(room.name));

      return el(
        'li',
        {
          class: `${room.name === state.activeRoom ? 'active' : ''}${unread ? ' has-unread' : ''}`,
          title: room.topic ?? '',
          role: 'button',
          tabindex: '0',
          'aria-current': room.name === state.activeRoom ? 'true' : null,
          'aria-label':
            `#${room.name}, ${members} present` +
            `${unread ? `, ${unread} unread` : ''}${isJoined ? '' : ' (not joined)'}`,
          onclick: activate,
          onkeydown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              activate();
            }
          },
        },
        el('span', { class: 'n', text: `#${room.name}` }),
        room.hidden || rs?.info?.hidden
          ? el('span', { class: 'badge', text: t('ui.badge.hidden'), title: t('ui.room.isHidden') })
          : null,
        unread ? el('span', { class: 'unread', text: unread > 99 ? '99+' : String(unread) }) : null,
        el('span', { class: 'c', text: String(members) }),
        isJoined
          ? el('button', {
              type: 'button',
              class: 'x',
              text: '×',
              title: t('ui.room.leaveTitle', { room: room.name }),
              'aria-label': t('ui.room.leaveTitle', { room: room.name }),
              onclick: (event) => {
                event.stopPropagation();
                leaveRoom(room.name);
              },
            })
          : null,
      );
    }),
  );
}

/**
 * The open conversations, drawn as the rooms above are drawn.
 *
 * The section goes away entirely when there are none rather than sitting there
 * empty: a heading over nothing is a promise of something you cannot find, and
 * conversations appear by themselves the moment one is sent or received.
 *
 * Newest first. A channel is a place you go back to, and the room list is sorted
 * to keep it where you left it; a conversation is something that just happened,
 * and the one that just happened is the one being looked for.
 */
function renderDmList() {
  const section = $('dm-section');
  const list = $('dm-list');
  const open = [...state.dms.entries()];
  section.hidden = open.length === 0;
  if (open.length === 0) {
    list.replaceChildren();
    return;
  }

  const lastAt = ([, dm]) => dm.messages[dm.messages.length - 1]?.at ?? 0;
  open.sort((a, b) => lastAt(b) - lastAt(a) || a[1].nickname.localeCompare(b[1].nickname));

  list.replaceChildren(
    ...open.map(([key, dm]) => {
      const isActive = key === state.activeDm;
      const unread = isActive ? 0 : (dm.unread ?? 0);
      const activate = () => setActiveDm(dm.nickname);

      return el(
        'li',
        {
          class: `${isActive ? 'active' : ''}${unread ? ' has-unread' : ''}`,
          role: 'button',
          tabindex: '0',
          'aria-current': isActive ? 'true' : null,
          'aria-label':
            t('ui.dm.itemLabel', { nickname: dm.nickname }) +
            (unread ? `, ${t('ui.dm.unreadLabel', { count: unread })}` : ''),
          onclick: activate,
          onkeydown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              activate();
            }
          },
        },
        el('span', { class: 'n', text: `@${dm.nickname}` }),
        // The same mark the member list uses, for the same reason: somebody you
        // have hidden is still able to write to you, and a conversation whose
        // messages are being dropped should say so rather than look silent.
        isUserHidden(dm.nickname)
          ? el('span', { class: 'badge', text: t('ui.badge.hiddenByYou') })
          : null,
        unread ? el('span', { class: 'unread', text: unread > 99 ? '99+' : String(unread) }) : null,
        el('button', {
          type: 'button',
          class: 'x',
          text: '×',
          title: t('ui.dm.closeTitle', { nickname: dm.nickname }),
          'aria-label': t('ui.dm.closeTitle', { nickname: dm.nickname }),
          onclick: (event) => {
            event.stopPropagation();
            closeDm(key);
          },
        }),
      );
    }),
  );
}

function renderMembers() {
  const room = state.activeRoom;
  const list = $('member-list');
  // A conversation has no membership to show - it is the two of you, and both
  // names are already on screen - so the panel goes rather than sitting there
  // reading zero. Not while merely between rooms, though: an empty member list
  // there is part of the "join a room" state and belongs with it.
  $('members-section').hidden = Boolean(state.activeDm);
  $('members-title').textContent = room ? t('ui.membersIn', { room }) : t('ui.members');
  if (!room) {
    list.replaceChildren();
    $('member-count').textContent = '0';
    return;
  }
  const { presence } = roomState(room);
  $('member-count').textContent = String(presence.length);

  list.replaceChildren(
    ...sortMembers(presence, getLocale()).map((member) => {
      const flags = [
        member.media?.audio ? (member.media.audioMuted ? '🔇' : '🎤') : null,
        member.media?.video ? '🎥' : null,
        member.media?.screen ? '🖥' : null,
      ].filter(Boolean);
      const description =
        t('ui.member.summary', {
          nickname: member.nickname,
          kind: member.guest ? t('ui.member.guest') : t('ui.member.registered'),
          role:
            member.role === 'root'
              ? t('ui.member.roleRoot')
              : member.role === 'operator'
                ? t('ui.member.roleOperator')
                : '',
        }).replace(/\.$/, '') +
        (member.sessions > 1 ? t('ui.member.onDevices', { count: member.sessions }) : '') +
        (member.hidden ? t('ui.member.hiddenFromOthers') : '');

      /*
       * `member.hidden` is the server's idea of hidden - root watching a room
       * without appearing in it - and is a different thing entirely from this,
       * which is this reader having hidden them. Both can be true at once, so
       * they get their own class and their own badge rather than sharing.
       */
      const hiddenByMe = isUserHidden(member.nickname);

      return el(
        'li',
        {
          class:
            `${roleClass(member)}${member.hidden ? ' hidden-member' : ''}` +
            `${hiddenByMe ? ' hidden-by-me' : ''}`,
          title: description,
          role: 'button',
          tabindex: '0',
          'aria-label': t('ui.member.activateForActions', {
            description: hiddenByMe ? `${description} ${t('ui.member.isHiddenByYou')}` : description,
          }),
          onclick: () => openMemberActions(member),
          onkeydown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openMemberActions(member);
            }
          },
        },
        el('span', { class: 'sigil', text: sigilFor(member.role) }),
        el('span', { class: 'name', text: member.nickname }),
        member.hidden ? el('span', { class: 'badge', text: t('ui.badge.hidden') }) : null,
        // So a room that has gone quiet reads as a choice you made rather than
        // as somebody who stopped talking.
        hiddenByMe
          ? el('span', { class: 'badge', title: t('ui.member.isHiddenByYou'), text: t('ui.badge.hiddenByYou') })
          : null,
        member.sessions > 1 ? el('span', { class: 'badge', text: `×${member.sessions}` }) : null,
        el('span', { class: 'mediaflags', text: flags.join('') }),
      );
    }),
  );
}

function renderRoomBar() {
  const room = state.activeRoom;
  const dm = activeDmState();
  $('room-title').textContent = dm ? `@${dm.nickname}` : room ? `#${room}` : t('ui.noRoom');
  const info = room ? roomState(room).info : null;
  const topicText = dm
    ? t('ui.dm.subtitle')
    : (info?.topic ?? (room ? t('ui.noTopic') : t('ui.joinToStart')));
  // Set as nodes rather than text: a topic is where a room keeps its links.
  $('room-topic').replaceChildren(
    ...textNodes(topicText),
    ...(info?.hidden ? [`  ·  ${t('ui.hiddenRoomSuffix')}`] : []),
  );
  $('btn-room-actions').disabled = !room;
  // Attachments need somewhere to go, but not a working media server - and a
  // conversation is somewhere to go, so this is the one composer control that
  // stays live in one. The jukebox switch is not: it belongs to a room's shared
  // player, and there is no such thing between two people.
  $('btn-attach').disabled = !room && !dm;
  $('btn-jukebox').disabled = !room;
  const disabled = !room || info?.mediaEnabled === false || state.info?.media?.enabled === false;
  for (const id of ['btn-mic', 'btn-cam']) {
    $(id).disabled = disabled;
    if (disabled) $(id).title = t('ui.media.unavailable');
  }
  if (disabled) {
    $('btn-mute').disabled = true;
    $('btn-mute').title = t('ui.media.unavailable');
  }
  // Settings stays reachable even where publishing is not, so output device and
  // quality can still be changed.
  $('btn-screen').disabled = disabled;
  renderStageToggle();
}

/**
 * A pane split in two, drawn rather than typed: the box-drawing characters that
 * would say this - ▤ against ▥ - are a pair of small filled squares at the size
 * a button wears them, and tell the two arrangements apart only if you already
 * know which is which. `divider` is the line down or across the middle.
 */
function stageIcon(divider) {
  return (
    '<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false"' +
    ' fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round">' +
    '<rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.5"/>' +
    `<path d="${divider}"/></svg>`
  );
}

/**
 * The room bar's arrangement toggle, which says what it will do rather than
 * where things are now: the icon is the arrangement you get by pressing it, and
 * so is the label a screen reader reads out.
 *
 * Whether it is on screen at all is the stylesheet's business, decided by the
 * width of the window and whether there is a stage to arrange.
 */
function renderStageToggle() {
  const beside = effectiveStageLayout() === 'beside';
  const label = t(beside ? 'ui.stage.above' : 'ui.stage.beside');
  const button = $('btn-stage-layout');
  // Split across for one above the other, down for side by side.
  button.innerHTML = stageIcon(beside ? 'M1.75 6.5h12.5' : 'M6.5 2.75v10.5');
  button.title = label;
  button.setAttribute('aria-label', label);
}

function messageNode(entry) {
  if (entry.kind === 'system') {
    return el(
      'div',
      { class: `msg system ${entry.level ?? 'info'}` },
      el('span', { class: 'time', text: clock(entry.at) }),
      renderBody(entry.body, 'body'),
    );
  }
  if (entry.kind === 'private') {
    /*
     * Read as any other transcript is: who spoke, then what they said.
     *
     * The arrows this used to draw - "→ bob", "bob →" - said which way the
     * message went, which was worth saying only while these lines sat in a
     * room's transcript among messages addressed to nobody in particular. In a
     * conversation of its own, the direction is the two names alternating.
     *
     * `from` is the sender either way: the copy of your own message that the
     * server sends back names you, so nothing has to branch on `outgoing` to
     * work out whose line this is. The role does, since only the incoming copy
     * carries one - your own comes from the session.
     */
    const role = entry.outgoing
      ? (state.session?.role ?? 'guest')
      : (entry.fromRole ?? (entry.guest ? 'guest' : 'user'));
    return el(
      'div',
      { class: 'msg private' },
      el('span', { class: 'time', text: clock(entry.at) }),
      whoButton(entry.from, role, `${sigilFor(role)}${entry.from}`),
      ': ',
      bodySpan(entry.body),
      ...attachmentNodes(entry.body),
    );
  }
  if (entry.kind === 'me') {
    return el(
      'div',
      { class: `msg me ${entry.history ? 'history' : ''} ${mentionsMe(entry) ? 'mention' : ''}` },
      el('span', { class: 'time', text: clock(entry.at) }),
      bodySpan(entry.body, `* ${entry.nickname} `),
      ...attachmentNodes(entry.body),
    );
  }
  return el(
    'div',
    { class: `msg ${entry.history ? 'history' : ''} ${mentionsMe(entry) ? 'mention' : ''}` },
    el('span', { class: 'time', text: clock(entry.at) }),
    whoButton(
      entry.nickname,
      entry.role ?? (entry.guest ? 'guest' : 'user'),
      `${sigilFor(entry.role)}${entry.nickname}`,
    ),
    ': ',
    bodySpan(entry.body),
    ...attachmentNodes(entry.body),
  );
}

/**
 * The children of a piece of message text: plain runs as text nodes, addresses as
 * links that open in a new tab.
 *
 * Only ever nodes - the text came from someone else, so it is never handed to
 * innerHTML. `rel` covers both halves of what target="_blank" would otherwise
 * give the opened page: no window.opener back to this page, and no referrer.
 */
/**
 * A message body as nodes: links become links, shortcodes become emotes, and
 * everything else stays text.
 *
 * Emotes are expanded only inside the runs that splitLinks() has already ruled
 * out as links, which is the whole reason the two passes are in this order: an
 * address like `http://host:3000/a:b:c` is full of things that look like
 * shortcodes, and none of them are.
 */
function textNodes(text) {
  return splitLinks(text).flatMap((run) => {
    if (run.href) {
      return el('a', {
        href: run.href,
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        text: run.text,
      });
    }
    return splitEmotes(run.text).map((piece) =>
      piece.emote ? emoteNode(piece.emote) : document.createTextNode(piece.text),
    );
  });
}

/**
 * Thumbnails for any attachments a message links to, or an empty list.
 *
 * Each one is wrapped in a link so that clicking opens the file in a new tab,
 * which is also what gives it keyboard access for free. Only this app's own
 * uploads are previewed - see previewKind().
 */
function attachmentNodes(text) {
  const origin = location.origin;
  const figures = [];
  for (const run of splitLinks(text)) {
    const kind = run.href ? previewKind(run.href, origin) : null;
    if (!kind) continue;
    const name = run.href.slice(run.href.lastIndexOf('/') + 1);
    const media =
      kind === 'video'
        ? el('video', {
            src: run.href,
            // Enough of the file to show a still frame, not the whole thing.
            preload: 'metadata',
            muted: true,
            playsinline: true,
            tabindex: '-1',
          })
        : el('img', {
            src: run.href,
            alt: t(`ui.attach.${kind}`),
            // Deliberately not loading="lazy". The thumbnail has no intrinsic
            // size until it loads, so its box is 0x0, never intersects the
            // viewport, and a lazy image would therefore never load at all.
            decoding: 'async',
          });
    const figure = el(
      'a',
      {
        class: `attachment ${kind}`,
        href: run.href,
        target: '_blank',
        rel: 'noopener noreferrer',
        title: t('ui.attach.openInNewTab', { name }),
      },
      media,
      kind === 'video' ? el('span', { class: 'play', text: '▶' }) : null,
    );
    // Attachments expire before the messages that link to them, so a thumbnail
    // that cannot load is the normal end of an attachment's life rather than a
    // fault. Say so, instead of leaving an empty frame on the page.
    media.addEventListener('error', () => {
      figure.replaceWith(el('span', { class: 'attachment-gone', text: t('ui.attach.expired') }));
    });
    figures.push(figure);
  }
  return figures;
}

/**
 * The text of a message with its attachment addresses left out, because the
 * thumbnail below is the link: printing the address as well says nothing and is
 * a mouthful of random characters. A caption keeps its words; a message that is
 * nothing but an attachment shows no text at all.
 */
function bodyNodesFor(text) {
  const runs = splitLinks(text);
  const words = runs
    .filter((run) => !(run.href && previewKind(run.href, location.origin)))
    .map((run) => run.text)
    .join('')
    .trim();
  if (!words) return [];
  // Re-split so any ordinary links in the caption are still links.
  return textNodes(words);
}

/**
 * A message's body, as the span that holds it.
 *
 * A message that is nothing but emotes is drawn large, which is what every chat
 * does and the reason the sprite sheet is designed for 16 and 48 pixels rather
 * than one size. The test runs on the text that is actually rendered - after
 * the attachment addresses have been dropped - so a picture with `:hymy:` as
 * its caption is judged on the caption.
 *
 * `prefix` is the "* nickname" an action message opens with, and having one
 * settles the question: there are words, so it is not an emote on its own.
 */
function bodySpan(text, prefix = null) {
  const nodes = bodyNodesFor(text);
  const jumbo = !prefix && nodes.length > 0 && onlyEmotes(splitEmotes(bodyTextOf(text)));
  return el('span', { class: jumbo ? 'body jumbo' : 'body' }, ...(prefix ? [prefix] : []), ...nodes);
}

/** The part of a message that is rendered as words, with attachments removed. */
function bodyTextOf(text) {
  return splitLinks(text)
    .filter((run) => !(run.href && previewKind(run.href, location.origin)))
    .map((run) => run.text)
    .join('')
    .trim();
}

/** Multi-line command output renders as a preformatted block. */
function renderBody(text, className) {
  const value = String(text ?? '');
  if (value.includes('\n')) return el('pre', { class: 'block' }, ...textNodes(value));
  return el('span', { class: className }, ...textNodes(value));
}

/**
 * A nickname in the chat, as a button: clicking it drops the name into the
 * message box. A button rather than a styled span so it can be tabbed to and is
 * announced as something that does anything at all.
 */
function whoButton(nickname, className, label) {
  return el('button', {
    type: 'button',
    class: `who ${className}`,
    text: label,
    'data-i18n-title': 'ui.mentionHint',
    title: t('ui.mentionHint', { nickname }),
    onclick: () => mentionInComposer(nickname),
  });
}

/** Drops a nickname into the message box at the caret and puts focus back there. */
function mentionInComposer(nickname) {
  const input = $('input-message');
  if (!input || input.disabled) return;
  const { value, cursor } = insertMention({
    value: input.value,
    selectionStart: input.selectionStart,
    selectionEnd: input.selectionEnd,
    nickname,
  });
  input.value = value;
  input.focus();
  input.setSelectionRange(cursor, cursor);
}

/** Drops a shortcode into the message box at the caret, as the picker does. */
function insertEmoteInComposer(emote) {
  const input = $('input-message');
  if (!input || input.disabled) return;
  const { value, cursor } = insertEmote({
    value: input.value,
    selectionStart: input.selectionStart,
    selectionEnd: input.selectionEnd,
    code: codeFor(emote),
  });
  input.value = value;
  input.focus();
  input.setSelectionRange(cursor, cursor);
}

/**
 * The emote picker: every emote in the set, animating, pick one to type it.
 *
 * Choosing by appearance is the point - nobody remembers that the burning face
 * is `:perkele:` and not `:raivo:`, and a list of names would be no help at all
 * to someone reading in the language the code is not in. The name is still
 * there as the label and the tooltip, so the picker teaches the shortcode while
 * you use it, and it is this reader's own language's code that gets typed.
 *
 * Built once, on first opening, and kept: ten animating sprites are cheap, and
 * rebuilding the grid on every open would restart every animation.
 */
let emotePickerGrid = null;

function buildEmotePicker() {
  if (emotePickerGrid) return emotePickerGrid;
  emotePickerGrid = el('div', { class: 'emote-grid' });
  for (const emote of EMOTES) {
    const code = `:${codeFor(emote)}:`;
    emotePickerGrid.append(
      el(
        'button',
        {
          type: 'button',
          class: 'emote-choice',
          title: code,
          'aria-label': code,
          onclick: () => {
            insertEmoteInComposer(emote);
            setEmotePicker(false);
          },
        },
        emoteNode(emote),
      ),
    );
  }
  $('emote-picker').append(emotePickerGrid);
  return emotePickerGrid;
}

/** Opens or closes the picker, keeping the button's state honest with it. */
function setEmotePicker(open) {
  const panel = $('emote-picker');
  const button = $('btn-emotes');
  if (open) buildEmotePicker();
  // Focus leaves before the panel is hidden, not after. A browser that finds
  // the focused element inside something newly hidden puts focus back on the
  // document, and it does that after this call returns - which quietly undid a
  // `button.focus()` written on the line below.
  if (!open && panel.contains(document.activeElement)) button.focus();
  panel.hidden = !open;
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!open) return;
  // Straight to the first emote, so the whole thing is reachable from the
  // keyboard without tabbing back through the composer.
  panel.querySelector('.emote-choice')?.focus();
}

/**
 * Opens or closes the paperclip's menu, on the same terms as the emote picker
 * above - including the order of the focus move, for the same reason.
 */
function setAttachMenu(open) {
  const panel = $('attach-menu');
  const button = $('btn-attach');
  if (!open && panel.contains(document.activeElement)) button.focus();
  panel.hidden = !open;
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) panel.querySelector('.attach-choice')?.focus();
}

/**
 * True when someone else's message names you. Your own messages are excluded -
 * signing your own name is not a mention - as are the server's.
 */
function mentionsMe(entry) {
  const mine = state.session?.nickname;
  if (!mine || !entry?.nickname) return false;
  if (entry.nickname.toLowerCase() === mine.toLowerCase()) return false;
  // Somebody you are not reading cannot get your attention by typing your name,
  // which is the first thing anybody would try.
  if (isUserHidden(entry.nickname)) return false;
  return mentionsNickname(entry.body, mine);
}

/**
 * Whether this line belongs to somebody the reader has hidden.
 *
 * Only what a person said: chat, actions and private messages. A system notice
 * naming them still shows - "alice was kicked" is the room's business, not
 * alice's - and so does an announcement, which is an operator addressing
 * everybody rather than one person talking.
 *
 * Applied when the transcript is drawn rather than when a message arrives, and
 * that is deliberate: the line is still kept, so unhiding somebody brings back
 * what they said while they were hidden instead of leaving a hole in the
 * conversation that nothing can fill.
 */
function fromHiddenUser(entry) {
  if (!entry?.nickname) return false;
  if (entry.kind !== 'chat' && entry.kind !== 'me' && entry.kind !== 'private') return false;
  if (entry.outgoing) return false;
  return isUserHidden(entry.nickname);
}

function renderMessages() {
  const list = $('message-list');
  const dm = activeDmState();
  if (dm) {
    list.replaceChildren(
      ...(dm.messages.length === 0
        ? [el('div', { class: 'msg system', text: t('ui.dm.empty', { nickname: dm.nickname }) })]
        : dm.messages.filter((entry) => !fromHiddenUser(entry)).map(messageNode)),
    );
    scrollTranscriptToBottom();
    return;
  }
  const room = state.activeRoom;
  if (!room) {
    list.replaceChildren(
      ...state.orphan.filter((entry) => !fromHiddenUser(entry)).map(messageNode),
      el('div', { class: 'msg system', text: t('ui.joinFromSidebar') }),
    );
    scrollTranscriptToBottom();
    return;
  }
  const { messages } = roomState(room);
  list.replaceChildren(...messages.filter((entry) => !fromHiddenUser(entry)).map(messageNode));
  // Opening a room shows its newest message, not wherever the previous room's
  // scroll position happened to land.
  scrollTranscriptToBottom();
}

/**
 * Whether the transcript is showing the newest message.
 *
 * The scroller is column-reverse, so its origin is the bottom: scrollTop is 0
 * there and grows negative on the way up. A little slack, because fractional
 * scroll positions would otherwise read as "not quite at the bottom".
 */
function transcriptAtBottom(box = $('messages')) {
  return Math.abs(box.scrollTop) < 80;
}

/** Jumps to the newest message and clears the badge. */
function scrollTranscriptToBottom() {
  $('messages').scrollTop = 0;
  state.stickToBottom = true;
  setMissedMessages(0);
}

/**
 * Shows or hides the badge that says how many messages arrived while the reader
 * was looking at something further up.
 */
function setMissedMessages(count) {
  state.missed = Math.max(0, count);
  const badge = $('btn-new-messages');
  badge.hidden = state.missed === 0;
  if (state.missed > 0) {
    badge.textContent = t(state.missed === 1 ? 'ui.newMessage' : 'ui.newMessages', {
      count: state.missed,
    });
  }
}

/**
 * Adds one message to the transcript on screen.
 *
 * Nothing is scrolled: a reader at the bottom stays there because the scroller
 * is anchored at its bottom, and one who has scrolled up keeps the messages they
 * were reading and is told how many arrived behind them instead.
 */
function appendToTranscript(entry) {
  const atBottom = transcriptAtBottom();
  $('message-list').append(messageNode(entry));
  if (!atBottom && !entry.history) setMissedMessages((state.missed ?? 0) + 1);
}

function pushMessage(room, entry) {
  const target = room ?? state.activeRoom;
  if (!target) {
    // No room to file this under (e.g. a ban notice as the room disappears):
    // keep it so the user still sees why, and show it in the empty view.
    state.orphan.push(entry);
    if (state.orphan.length > 100) state.orphan.shift();
    $('message-list').append(messageNode(entry));
    scrollTranscriptToBottom();
    return;
  }
  const rs = existingRoom(target);
  if (!rs) {
    // Named a room we have no state for. Either we are still joining it - the
    // server sends its welcome notice before the join reply - or we have left
    // it. Hold it briefly rather than dropping it or recreating the room; a join
    // flushes the queue and anything else expires with the session.
    if (room) {
      if (entry.kind === 'chat' || entry.kind === 'me') return;
      const queued = state.pending.get(room) ?? [];
      queued.push(entry);
      if (queued.length > 30) queued.shift();
      state.pending.set(room, queued);
      return;
    }

    // Genuinely room-less (a ban notice as the room disappears, say).
    state.orphan.push(entry);
    if (state.orphan.length > 100) state.orphan.shift();
    $('message-list').append(messageNode(entry));
    scrollTranscriptToBottom();
    return;
  }

  rs.messages.push(entry);
  if (rs.messages.length > 500) rs.messages.splice(0, rs.messages.length - 500);

  /*
   * Stored above and then dropped here rather than never stored: unhiding
   * somebody redraws the transcript from `rs.messages`, so what they said while
   * hidden comes back with them. It costs nothing to keep - the log is capped
   * either way - and it is the difference between hiding somebody and losing
   * the half of a conversation they were in.
   */
  if (fromHiddenUser(entry)) return;

  if (target === state.activeRoom) {
    appendToTranscript(entry);
    return;
  }

  // A background room: count anything a person said, or a moderation event.
  const countsAsUnread =
    !entry.history && (entry.kind === 'chat' || entry.kind === 'me' || entry.level === 'moderation' || entry.level === 'announce');
  if (countsAsUnread) {
    rs.unread = (rs.unread ?? 0) + 1;
    refreshUnreadTitle();
    renderRoomList();
  }
}

/**
 * System notice. Repeats of the same text within a few seconds are collapsed
 * into a counter, so a burst (a rate-limited flood, say) does not bury the
 * conversation under identical red lines.
 */
function notice(text, level = 'info', room = null) {
  /*
   * A conversation on screen takes the notices that belong to it - "alice is not
   * online" above all, which is the answer to a message just typed into it.
   * Without this they would be filed under no room, since a conversation leaves
   * `activeRoom` null, and drawn nowhere at all.
   *
   * An explicit room still wins: a notice named for a room is about that room
   * and belongs in it, whatever happens to be on screen.
   */
  const dm = room ? null : activeDmState();
  const target = room ?? state.activeRoom;
  const rs = target ? state.rooms.get(target) : null;
  const log = dm ? dm.messages : (rs?.messages ?? state.orphan);
  const last = log[log.length - 1];

  if (last && last.kind === 'system' && last.baseBody === text && Date.now() - last.at < 5000) {
    last.repeats = (last.repeats ?? 1) + 1;
    last.body = `${text} (×${last.repeats})`;
    last.at = Date.now();
    if (dm || target === state.activeRoom || !state.activeRoom) renderMessages();
    return;
  }

  const entry = { kind: 'system', level, body: text, baseBody: text, at: Date.now() };
  if (dm) {
    dm.messages.push(entry);
    appendToTranscript(entry);
    return;
  }
  pushMessage(target, entry);
}

function renderIdentity() {
  const session = state.session;
  $('me-nick').textContent = session?.nickname ?? '—';
  const badge = $('me-role');
  const role = session?.role ?? 'guest';
  badge.textContent = roleLabel(role);
  badge.className = `badge ${role === 'root' ? 'root' : role === 'operator' ? 'op' : role === 'user' ? 'user' : 'guest'}`;
  $('btn-admin').hidden = !(session?.capabilities?.['operator.list'] || session?.capabilities?.['audit.read']);
  /*
   * The Users tab is root's alone - removing an account cannot be undone - so it
   * is hidden from the operators who can see the rest of the panel. The server
   * checks the capability again on every request behind it; this only keeps a
   * button nobody may use off the screen.
   */
  $('admin-tab-users').hidden = session?.capabilities?.['account.delete'] !== true;

  /**
   * The "+" button creates rooms, so it is only shown to those who may.
   * Creating is not scoped to a room, hence the global capability rather than
   * the per-room one. Anyone can still reach a room they know the name of with
   * `/join <#room>`, and every listed room is one click away in the sidebar.
   */
  const canCreateRooms = Boolean(session?.capabilities?.['room.create']);
  const newRoom = $('btn-newroom');
  newRoom.hidden = !canCreateRooms;
  newRoom.title = canCreateRooms ? 'Join or create a room' : '';
}

function renderMediaButtons() {
  const room = state.activeRoom;
  /*
   * None of this row applies to a conversation - every control on it acts on a
   * channel, and there is no channel on screen - so it goes rather than sitting
   * there greyed out. The rest of the function still runs: the buttons keep
   * their state up to date behind the hidden row, so switching back to a room
   * shows a microphone that is still on rather than one that has to be redrawn.
   */
  $('media-controls').hidden = Boolean(state.activeDm);
  const rs = room ? roomState(room) : null;
  const media = rs?.media ?? null;

  const micOn = Boolean(media?.isPublishing('mic'));
  const micMuted = micOn && (rs?.micMuted || media.isPaused('mic'));

  // Mic: on/off (are you sending audio at all).
  const mic = $('btn-mic');
  mic.classList.toggle('on', micOn && !micMuted);
  mic.classList.toggle('paused', micOn && micMuted);
  mic.title = micOn ? t('ui.mic.stop') : t('ui.mic.start');
  mic.setAttribute('aria-pressed', micOn ? 'true' : 'false');

  /*
   * Muting and push to talk are the same decision made in opposite directions,
   * so exactly one of them is on screen - and neither when there is no
   * microphone running, since there would be nothing for it to act on.
   */
  const pushToTalk = getSettings().pushToTalk === true;

  const mute = $('btn-mute');
  mute.hidden = !micOn || pushToTalk;
  mute.classList.toggle('paused', micMuted);
  mute.querySelector('span').textContent = micMuted ? t('ui.unmute') : t('ui.mute');
  mute.firstChild.textContent = micMuted ? '🔊 ' : '🔇 ';
  mute.title = micMuted ? t('ui.mute.off') : t('ui.mute.on');
  mute.setAttribute('aria-pressed', micMuted ? 'true' : 'false');

  const talk = $('btn-talk');
  talk.hidden = !micOn || !pushToTalk;
  talk.classList.toggle('on', micOn && !micMuted);
  talk.querySelector('span').textContent = t('ui.talk');
  talk.title = micMuted ? t('ui.talk.hint') : t('ui.talk.live');
  talk.setAttribute('aria-pressed', micOn && !micMuted ? 'true' : 'false');

  for (const [id, source] of [
    ['btn-cam', 'camera'],
    ['btn-screen', 'screen'], // lives in the settings dialog
  ]) {
    const button = $(id);
    const on = Boolean(media?.isPublishing(source));
    const paused = on && media.isPaused(source);
    button.classList.toggle('on', on && !paused);
    button.classList.toggle('paused', paused);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  $('btn-cam').title = media?.isPublishing('camera') ? t('ui.camera.off') : t('ui.camera.on');

  /*
   * The jukebox, read the same way as the others: lit when something is playing
   * to you, marked when you have switched it off, and plain when it is simply
   * idle. Unlike the mic and the camera it is not a source this session sends,
   * so what it toggles is a stored preference rather than a track - but from the
   * row it reads identically, which is the point of it being here.
   */
  const jukebox = $('btn-jukebox');
  jukebox.hidden = !jukeboxAvailable();
  const wantsJukebox = wantsYouTube();
  const jukeboxPlaying = wantsJukebox && jukeboxOf(room) !== null;
  jukebox.classList.toggle('on', jukeboxPlaying);
  jukebox.classList.toggle('paused', !wantsJukebox);
  jukebox.title = wantsJukebox ? t('ui.jukebox.hide') : t('ui.jukebox.show');
  jukebox.setAttribute('aria-pressed', wantsJukebox ? 'true' : 'false');
}

// -------------------------------------------------------------------- video

/**
 * Tiles are per participant, not per track: a camera and a microphone from the
 * same person share one tile, so nobody gets a redundant audio-only box beside
 * their video. A screen share is a separate tile, since it is separate content.
 */
/**
 * Watches every tile's audio and rings the one that is talking.
 *
 * Keys are room and tile together, because the same tile key exists in every
 * room the user is in and only the active room is on screen.
 */
const speakingMonitor = new SpeakingMonitor({
  onChange: (id, speaking) => {
    const separator = id.indexOf('\u0000');
    const room = id.slice(0, separator);
    const key = id.slice(separator + 1);
    const tile = state.rooms.get(room)?.tiles.get(key);
    tile?.node.classList.toggle('speaking', speaking);
  },
});

/** Starts or stops watching a tile, depending on whether it carries audio. */
function watchTileAudio(room, key, tile) {
  const id = `${room}\u0000${key}`;
  if (tile?.stream?.getAudioTracks().length > 0) speakingMonitor.watch(id, tile.stream);
  else speakingMonitor.unwatch(id);
}

function tileKey(peerId, source) {
  return `${peerId}:${source === 'screen' ? 'screen' : 'cam'}`;
}

/** Height of an element that is on screen; 0 for one that is not. */
function shownHeight(node) {
  return node && !node.hidden ? node.offsetHeight : 0;
}

/**
 * Picks the largest tile height at which every tile fits the space the room's
 * layout leaves the stage, so the stage never scrolls and no tile is cut off.
 *
 * The budget is measured rather than assumed. The stage and the transcript share
 * one axis - height when they are stacked, width when they are side by side -
 * and the stylesheet states the terms of that split in `--layout`,
 * `--stage-share` and `--chat-min` on `.main`. Reading the terms back keeps the
 * two in step: the arrangement is chosen in one place, the stylesheet, and the
 * tiles are sized to whatever it decided. Taking the stage's own `max-height`
 * for the budget, as this once did, ignored what the composer and the room bar
 * had already taken, so a short window overflowed the stage - tiles clipped -
 * and squeezed the transcript to nothing.
 *
 * Tiles keep a 16:9 shape, so their width follows from the height. Each possible
 * row count is tried and the one giving the biggest tiles wins - with a single
 * tile on a wide screen that is one row at the full available height.
 */
/**
 * The axis the stage and the transcript share, and how much of it there is to
 * divide between them - the terms of the split, read back from the stylesheet
 * that chose the arrangement.
 *
 * Everything is measured from `.main` and its other children, never from the
 * stage itself: the stage's size is layoutVideoGrid()'s own output, and reading
 * it back as an input would feed the resize observer that calls it.
 */
function stagePanels() {
  const main = document.querySelector('.main');
  const layout = getComputedStyle(main);
  const columns = layout.getPropertyValue('--layout').trim() === 'columns';
  const above =
    shownHeight(main.querySelector('.banner')) + shownHeight(main.querySelector('.roombar'));
  return {
    main,
    columns,
    axis: columns ? 'inline' : 'block',
    share: Number.parseFloat(layout.getPropertyValue('--stage-share')) || 0.5,
    chatMin: Number.parseFloat(layout.getPropertyValue('--chat-min')) || 0,
    above,
    // Side by side they divide the room's width; stacked, what is left of its
    // height once the room bar and the composer have taken theirs.
    pool: columns
      ? main.clientWidth
      : main.clientHeight - above - shownHeight(main.querySelector('.composer')),
  };
}

function layoutVideoGrid() {
  const grid = $('video-grid');
  if (grid.hidden) return;
  // Only the room on screen is measured; the other rooms' holders are hidden.
  const count = grid.querySelectorAll('.room-tiles:not([hidden]) > .tile').length;
  if (count === 0) return;

  const main = grid.closest('.main');
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.rowGap) || 8;
  // Padding and border together: every budget below is the room left for tiles
  // inside a border-box the stage's own frame has already spent part of.
  const padX = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
  const padY = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
  const borderX = Number.parseFloat(styles.borderLeftWidth) + Number.parseFloat(styles.borderRightWidth);
  const borderY = Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth);
  const frameX = padX + borderX;
  const frameY = padY + borderY;
  const { columns, share, chatMin, above, pool } = stagePanels();

  // At most its share of the axis the two of them divide, and never so much of
  // it that the transcript drops below its floor.
  const budget = Math.min(pool * share, pool - chatMin);
  let availableWidth;
  let availableHeight;
  if (columns) {
    // A column of its own, the full height beside the transcript - the composer
    // sits under the transcript and costs the stage nothing - and the budget
    // above is a width.
    availableHeight = main.clientHeight - above - frameY;
    availableWidth = budget - frameX;
  } else {
    // Stacked: as wide as the room, and the budget is a height.
    availableWidth = main.clientWidth - frameX;
    availableHeight = budget - frameY;
  }
  if (availableWidth <= 0 || availableHeight <= 0) return;

  // The winning arrangement, keeping the column count that won rather than
  // working it back out of the width afterwards: the two can disagree by a
  // fraction of a pixel, and a stage sized for two tiles a row that the browser
  // then packs one to a row is five rows deep where three were allowed for.
  const ratio = 16 / 9;
  let best = { height: 0, columns: 1 };
  for (let rows = 1; rows <= count; rows += 1) {
    const columns = Math.ceil(count / rows);
    const heightLimit = (availableHeight - (rows - 1) * gap) / rows;
    const widthLimit = (availableWidth - (columns - 1) * gap) / columns / ratio;
    const height = Math.min(heightLimit, widthLimit);
    if (height > best.height) best = { height, columns };
  }
  // Floor to avoid a sub-pixel overflow re-introducing a scrollbar. The lower
  // bound is a last resort for a window too small to hold the tiles at any
  // useful size: small tiles beat clipped ones, and past that point the stage
  // scrolls, which is the only case where it is allowed to.
  const height = Math.max(MIN_TILE_HEIGHT, Math.floor(best.height));
  grid.classList.toggle('overflowing', best.height < MIN_TILE_HEIGHT);
  grid.style.setProperty('--tile-height', `${height}px`);

  // How much width those tiles actually use, so a stage in a column of its own is
  // no wider than the picture it holds and one camera does not leave half a
  // column of empty stage beside the transcript.
  //
  // Rounded up at every step, with the frame added back and a pixel to spare.
  // The width is what makes the tiles wrap where they are meant to, and it is
  // decided here in fractions of a CSS pixel while the browser lays them out in
  // device pixels, under a zoom level and a scrollbar this cannot see. A stage a
  // fraction too narrow drops the last tile of every row onto a row of its own -
  // one tall column, scrolling, instead of a grid - so the error is worth a
  // pixel of slack to make impossible.
  const tileWidth = Math.ceil(height * ratio);
  const perRow = Math.min(count, best.columns);
  const packed = perRow * tileWidth + (perRow - 1) * gap + frameX + 1;
  grid.style.setProperty('--stage-width', `${packed}px`);
}

/*
 * Resizing the stage.
 *
 * The same control as the jukebox's bar, over the other split in the room, and
 * built the same way - but what it moves is different in one respect worth
 * stating. The jukebox is given a share of the grid and fills it. The stage is
 * given a *ceiling*, and the tiles inside grow to whatever fits under it while
 * keeping their shape; the panel then hugs the tiles. So the bar sets how much
 * the cameras may take, and the picture follows in the steps its own aspect
 * ratio allows rather than pixel for pixel with the pointer.
 */

/** Enough stage to be worth looking at: one tile's height, and its width. */
const MIN_STAGE = { block: 90, inline: 140 };

/**
 * The share somebody has dragged the stage to, clamped - storage is editable -
 * or null while the stylesheet's own choice still stands.
 */
function stageShare() {
  // Tested for a number before being read as one: unset is null here, and
  // Number(null) is 0, which would clamp to the narrowest stage allowed and
  // look for all the world like a stored choice.
  const stored = getSettings().stageShare;
  if (typeof stored !== 'number' || !Number.isFinite(stored)) return null;
  return Math.min(0.85, Math.max(0.15, stored));
}

/** Writes the share to the grid, and to the handle for a screen reader. */
function applyStageShare(share = stageShare()) {
  const { main } = stagePanels();
  // Removed rather than set when there is nothing stored: each arrangement
  // states its own share in the stylesheet, and one inline value would flatten
  // all of them into whichever number was written last.
  if (share === null) main.style.removeProperty('--stage-share');
  else main.style.setProperty('--stage-share', String(share));

  // Read back after the write, so this reports the share actually in force
  // whether it came from the drag or from the stylesheet.
  const { axis, share: inForce } = stagePanels();
  const handle = $('stage-resize');
  handle.setAttribute('aria-valuenow', String(Math.round(inForce * 100)));
  // A separator between two columns is itself vertical, and between two rows
  // horizontal - the orientation of the bar, not of the movement.
  handle.setAttribute('aria-orientation', axis === 'inline' ? 'vertical' : 'horizontal');
}

/**
 * Moves the split, keeping both the picture and the conversation usable.
 *
 * The bounds are in pixels for the same reason the jukebox's are: "big enough to
 * be worth having" is a size of picture and a number of lines, not a fraction of
 * however large this window happens to be. The transcript's floor is the one the
 * stylesheet already states for the arrangement in force, so the two agree by
 * construction - it is the same number layoutVideoGrid() clamps to.
 *
 * `null` puts it back to whatever the arrangement chose, which is the reset.
 */
function setStageShare(share, { remember = false } = {}) {
  const { axis, pool, chatMin } = stagePanels();
  let next = share;
  if (next !== null) {
    const floor = MIN_STAGE[axis];
    if (pool > floor + chatMin) {
      next = Math.min(Math.max(next, floor / pool), 1 - chatMin / pool);
    }
    next = Math.min(0.85, Math.max(0.15, next));
  }
  applyStageShare(next);
  if (remember) updateSettings({ stageShare: next });
  layoutVideoGrid();
  return next;
}

/**
 * The drag. Pointer events captured on the handle, so a pointer that slides off
 * it - or off the window - still steers and still finishes.
 *
 * The bar sits after the stage in both arrangements: under it when they are
 * stacked, down its trailing edge when they are side by side. So the stage grows
 * as the pointer moves down in the first case and right in the second, and both
 * are the plain difference - unlike the jukebox's bar, which leads its panel in
 * one arrangement and so has to subtract.
 */
function wireStageResize() {
  const handle = $('stage-resize');
  const main = document.querySelector('.main');
  const grid = $('video-grid');
  let start = null;

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    const { axis } = stagePanels();
    start = {
      axis,
      from: axis === 'inline' ? event.clientX : event.clientY,
      // Anchored to where the bar actually is, not to the ceiling in force. A
      // stage held short of its ceiling by its own width would otherwise take
      // the first stretch of the drag to reach the bar, which reads as a handle
      // that has come loose from the pointer.
      size: axis === 'inline' ? grid.offsetWidth : grid.offsetHeight,
      share: undefined,
    };
    handle.classList.add('dragging');
    main.classList.add('resizing');
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      /* the move and up events still arrive; only the capture was refused */
    }
  });

  handle.addEventListener('pointermove', (event) => {
    if (!start) return;
    const { pool } = stagePanels();
    if (pool <= 0) return;
    const moved =
      start.axis === 'inline' ? event.clientX - start.from : event.clientY - start.from;
    // Kept as it is clamped, for the write at the end of the drag.
    start.share = setStageShare((start.size + moved) / pool);
  });

  const finish = () => {
    if (!start) return;
    const { share } = start;
    start = null;
    handle.classList.remove('dragging');
    main.classList.remove('resizing');
    /*
     * Written once, at the end: a write per pointermove is a write per frame.
     *
     * What is stored is the ceiling the drag asked for rather than the size the
     * tiles settled at. They are the same number until the stage is held short
     * by its own width - two tiles a row can only be so tall in a room only so
     * wide - and storing the shortfall would ratchet the ceiling down to it,
     * leaving the cameras unable to grow back when the window widens.
     *
     * Undefined after a press that never moved, which stores nothing: a click on
     * the bar is not a resize.
     */
    if (share !== undefined) updateSettings({ stageShare: share });
  };
  for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
    handle.addEventListener(type, finish);
  }

  // The keyboard equivalent, and the same two keys for going back: Home or
  // Enter hands the size back to the arrangement rather than to a number of our
  // own, which is what makes the reset survive moving the cameras afterwards.
  handle.addEventListener('keydown', (event) => {
    const sideways = stagePanels().axis === 'inline';
    const steps = sideways
      ? { ArrowLeft: -0.02, ArrowRight: 0.02 }
      : { ArrowUp: -0.02, ArrowDown: 0.02 };
    const step = steps[event.key];
    if (step === undefined) {
      if (event.key !== 'Home' && event.key !== 'Enter') return;
      event.preventDefault();
      setStageShare(null, { remember: true });
      return;
    }
    event.preventDefault();
    // From the ceiling in force - the stylesheet's until somebody has moved it -
    // rather than from the size the tiles reached, for the reason given in
    // finish() above.
    setStageShare(stagePanels().share + step, { remember: true });
  });

  handle.addEventListener('dblclick', () => setStageShare(null, { remember: true }));

  /*
   * The cameras change places - a window crossing the width at which the
   * side-by-side arrangement becomes available, or somebody pressing the room
   * bar's toggle - and the seam turns from horizontal to vertical with them.
   * What the handle tells a screen reader has to follow, and so does the share
   * itself, whose floors are a height in one arrangement and a width in the
   * other.
   *
   * Not remembered, for the same reason the jukebox's is not: a window briefly
   * too narrow for the choice somebody made should re-clamp it, not replace it.
   */
  refreshStageShare = () => setStageShare(stageShare());
  if (typeof matchMedia === 'function') {
    matchMedia('(min-width: 820px)').addEventListener('change', () => refreshStageShare());
  }
  applyStageShare();
}

/**
 * Re-reads the stage share for an arrangement that has changed underneath it.
 * Set by wireStageResize(); a no-op before then.
 */
let refreshStageShare = () => {};

/**
 * Draws the tiles of every joined room, showing only the active room's.
 *
 * Nothing is detached that does not have to be. Taking a media element out of
 * the document pauses it - that is the browser's own rule, not a choice here -
 * so tearing the grid down and rebuilding it, as this used to on every change,
 * silenced the other rooms and left them stopped when you came back. Each room
 * keeps a holder that is merely hidden while another room is on screen; a
 * hidden element goes on playing.
 */
function renderVideoGrid() {
  const grid = $('video-grid');
  const active = state.activeRoom;

  // Holders for rooms that have been left.
  for (const holder of [...grid.children]) {
    if (!state.rooms.has(holder.dataset.room)) holder.remove();
  }

  for (const [name, rs] of state.rooms) {
    let holder = [...grid.children].find((child) => child.dataset.room === name);
    if (!holder) {
      holder = el('div', { class: 'room-tiles', 'data-room': name });
      grid.append(holder);
    }
    // Add what is new and remove what is gone, leaving the rest untouched: even
    // moving a playing element within the document pauses it on the way.
    const wanted = new Set([...rs.tiles.values()].map((tile) => tile.node));
    for (const child of [...holder.children]) {
      if (!wanted.has(child)) child.remove();
    }
    for (const node of wanted) {
      if (node.parentElement !== holder) holder.append(node);
    }
    holder.hidden = name !== active;
  }

  const showing = active ? roomState(active).tiles.size : 0;
  grid.hidden = showing === 0;
  layoutVideoGrid();
}

/**
 * Reflects what a tile currently carries: video presence switches the
 * presentation, audio presence decides whether volume controls are shown.
 */
/**
 * Applies the publisher's rotation to a tile.
 *
 * A quarter turn also swaps the picture's width and height, so the element is
 * sized to the box it will occupy after turning; without that the rotated video
 * would stick out of its tile.
 */
function applyTileRotation(tile) {
  const degrees = tile.rotation ?? 0;
  const video = tile.video;
  if (!video) return;
  const quarterTurn = degrees === 90 || degrees === 270;
  video.style.transform = degrees ? `translate(-50%, -50%) rotate(${degrees}deg)` : '';
  video.classList.toggle('turned', Boolean(degrees));
  video.classList.toggle('rotated', quarterTurn);
  // Containment for the width/height swap is established on the tile, and only
  // while it is needed.
  tile.node?.classList.toggle('has-rotated', quarterTurn);
}

/**
 * Recomputes and applies a tile's rotation from what the publisher reported about
 * how it is holding its camera.
 *
 * A local preview is never turned: the browser already presents your own camera
 * the right way up.
 */
function refreshTileRotation(tile) {
  if (!tile.video) return;
  tile.rotation = tile.local ? 0 : rotationForFrame(tile.orientation ?? {});
  applyTileRotation(tile);
}

function refreshTilePresentation(tile) {
  // Two different questions: whether this person is sending a picture, and
  // whether this viewer wants to look at it. A tile whose picture has been put
  // away is drawn exactly like one from somebody with no camera at all, so it
  // takes that same path - avatar, nickname, their volume - rather than a second
  // one of its own. Hiding the video element leaves the sound alone; that is
  // already how an audio-only peer has always been drawn.
  const hasCamera = tile.stream.getVideoTracks().length > 0;
  const hasVideo = hasCamera && !tile.videoHidden;
  const hasAudio = tile.stream.getAudioTracks().length > 0;
  tile.node.classList.toggle('audio-only', !hasVideo);
  applyTileRotation(tile);
  tile.video.hidden = !hasVideo;
  if (tile.placeholder) tile.placeholder.hidden = hasVideo;

  // The strip is worth showing for either reason now: it carries the volume for
  // somebody sending audio, and the picture toggle for somebody sending video.
  // Local tiles are muted to prevent feedback, so neither would do anything.
  if (tile.controls) tile.controls.hidden = tile.local || (!hasAudio && !hasCamera);
  // ...but each control only appears where it applies, so a strip never offers a
  // volume for a silent feed or a picture toggle for someone with no camera.
  if (tile.volumeButton) tile.volumeButton.hidden = !hasAudio;
  if (tile.slider) tile.slider.hidden = !hasAudio;
  if (tile.videoButton) {
    tile.videoButton.hidden = !hasCamera;
    tile.videoButton.textContent = tile.videoHidden ? '🙈' : '👁';
    const label = tile.videoHidden
      ? t('ui.tile.showVideo', { nickname: tile.nickname })
      : t('ui.tile.hideVideo', { nickname: tile.nickname });
    tile.videoButton.title = label;
    tile.videoButton.setAttribute('aria-label', label);
    tile.videoButton.setAttribute('aria-pressed', tile.videoHidden ? 'true' : 'false');
  }
}

/**
 * Starts playback, coping with the browser's autoplay policy.
 *
 * A refresh reaches this point with no user gesture on record, and browsers
 * refuse to begin *audible* playback in that state - Firefox blocks it by
 * default - so the feed arrives, attaches, and sits on a still frame. Entering
 * picture-in-picture is itself a gesture, which is why that appeared to be the
 * only way to resume.
 *
 * Muted playback is always allowed, so the picture starts immediately and the
 * tile is marked as waiting for a gesture; the next click or key press anywhere
 * gives it its sound back, and the tile carries a button meanwhile so the
 * silence is explained rather than mysterious.
 */
async function playTile(tile) {
  if (!tile?.video) return false;
  try {
    await tile.video.play();
    // A local (muted) tile never needs a gesture, so this also clears the flag
    // after a successful retry.
    markAwaitingGesture(tile, null);
    return true;
  } catch (err) {
    if (err?.name !== 'NotAllowedError' || tile.local || tile.muted) return false;

    // Firefox rejects play() for an *inaudible* stream and then starts it
    // anyway, so a rejection on its own does not mean the picture is stuck.
    if (!tile.video.paused) {
      markAwaitingGesture(tile, null);
      return true;
    }

    tile.video.muted = true;
    try {
      await tile.video.play();
    } catch {
      /* "Block Audio and Video" refuses even this; the button is the way back */
    }
    // Three outcomes, and they need different words: still frozen, playing but
    // silenced, or playing with nothing to hear anyway.
    if (tile.video.paused) markAwaitingGesture(tile, 'play');
    else markAwaitingGesture(tile, tile.stream.getAudioTracks().length > 0 ? 'sound' : null);
    return !tile.video.paused;
  }
}

/**
 * Shows or hides a tile's blocked button and arms the gesture hook.
 * `waiting` is 'play' (frozen), 'sound' (playing but silenced) or null (fine).
 */
function markAwaitingGesture(tile, waiting) {
  tile.awaitingGesture = waiting;
  tile.node?.classList.toggle('needs-gesture', Boolean(waiting));
  if (tile.blockedButton) {
    // Re-read the label each time, so a language change mid-call is picked up.
    if (waiting) {
      tile.blockedButton.textContent = t(
        waiting === 'play' ? 'ui.tile.playBlocked' : 'ui.tile.soundBlocked',
      );
    }
    tile.blockedButton.hidden = !waiting;
  }
  if (waiting) armGestureResume();
}

let gestureResumeArmed = false;

/**
 * Waits for the first user gesture anywhere on the page, then gives every
 * silenced tile its sound back. Capturing listeners, so a click on any control
 * still counts.
 */
function armGestureResume() {
  if (gestureResumeArmed) return;
  gestureResumeArmed = true;
  const resume = () => {
    document.removeEventListener('pointerdown', resume, true);
    document.removeEventListener('keydown', resume, true);
    gestureResumeArmed = false;
    resumeSilencedTiles();
  };
  document.addEventListener('pointerdown', resume, true);
  document.addEventListener('keydown', resume, true);
}

function resumeSilencedTiles() {
  for (const rs of state.rooms.values()) {
    for (const tile of rs.tiles.values()) {
      if (!tile.awaitingGesture) continue;
      applyTileVolume(tile); // restores the tile's own muted state and level
      playTile(tile);
    }
  }
}

/**
 * Applies a tile's own level, scaled by the master volume, and silences it if
 * either its own mute or the master one is on.
 *
 * The button below keeps showing the tile's own state rather than whichever of
 * the two is silencing it: the master's own icon already says that everything is
 * muted, and a per-tile button that will not come back on until something
 * elsewhere is switched off reads as a broken button.
 */
function applyTileVolume(tile) {
  if (tile.local) return;
  tile.video.muted = tile.muted === true || masterMuted();
  tile.video.volume = effectiveVolume(tile.volume ?? 1);
  if (tile.volumeButton) {
    tile.volumeButton.textContent = tile.muted ? '🔇' : tile.volume === 0 ? '🔈' : '🔊';
    tile.volumeButton.setAttribute(
      'aria-label',
      tile.muted
        ? t('ui.settings.unmuteFeed', { nickname: tile.nickname })
        : t('ui.settings.muteFeed', { nickname: tile.nickname }),
    );
    tile.volumeButton.setAttribute('aria-pressed', tile.muted ? 'true' : 'false');
  }
}

/**
 * The master volume control in the media bar: the mute on its icon, the slider
 * position, the figure beside it, and the tooltip that explains its reach.
 *
 * It lives in the bar rather than behind the settings dialog, so it is set from
 * the saved settings once at startup instead of every time a dialog opens.
 */
function renderMasterVolume() {
  const { masterVolume = 1, masterMuted: muted = false } = getSettings();
  // The figure is the slider's position rather than the level behind it. Those
  // are the same number only on a linear slider, and this one is linear in
  // decibels - showing the level would read 6% with the handle at halfway.
  const percent = sliderForGain(masterVolume);
  const slider = $('master-volume');
  slider.value = String(percent);
  $('master-volume-value').textContent = muted ? t('ui.settings.muted') : `${percent}%`;
  // The figure is hidden on a narrow screen, so it is in the tooltip too, along
  // with the reminder of what this covers - everyone at once, and the jukebox,
  // each of which also has one of its own.
  slider.title = `${t('ui.settings.volume')} — ${percent}%\n${t('ui.settings.volumeHint')}`;

  const button = $('btn-master-mute');
  button.textContent = muted ? '🔇' : '🔊';
  const label = muted ? t('ui.settings.unmuteAll') : t('ui.settings.muteAll');
  button.title = label;
  button.setAttribute('aria-label', label);
  button.setAttribute('aria-pressed', muted ? 'true' : 'false');
}

/**
 * Re-applies the master volume and mute to everything coming in: every camera
 * and microphone on screen, and the room's jukebox.
 */
function applyVolumeToAll() {
  for (const rs of state.rooms.values()) {
    for (const tile of rs.tiles.values()) applyTileVolume(tile);
  }
  applyJukeboxVolume();
}

/**
 * Volume slider and per-feed mute, shown on tiles that carry audio.
 *
 * The slider opens where this person was left rather than at full: the tile was
 * built with their remembered level, and every move of either control writes it
 * back, so it survives them going off camera and coming back.
 */
function buildTileControls(tile) {
  const slider = el('input', {
    type: 'range',
    min: '0',
    max: '100',
    // Position, not level: the slider is linear in decibels - see gain.js.
    value: String(sliderForGain(tile.volume ?? 1)),
    class: 'volume',
    'aria-label': t('ui.settings.volumeFor', { nickname: tile.nickname }),
    title: t('ui.settings.volumeFor', { nickname: tile.nickname }),
  });
  slider.addEventListener('input', () => {
    tile.volume = gainForSlider(slider.value);
    if (tile.volume > 0) tile.muted = false;
    applyTileVolume(tile);
    rememberVolume(tile.nickname, tile);
  });
  slider.addEventListener('click', (event) => event.stopPropagation());

  const button = el('button', {
    type: 'button',
    class: 'tile-mute',
    text: '🔊',
    onclick: (event) => {
      event.stopPropagation();
      tile.muted = !tile.muted;
      applyTileVolume(tile);
      rememberVolume(tile.nickname, tile);
    },
  });

  /*
   * Puts this one person's picture away, leaving them audible.
   *
   * Per tile rather than a setting for all of them, because the reason to hide a
   * camera is usually about one of them: somebody's window is glaring, or their
   * screen share is churning, and the rest of the room is still worth looking at.
   * The choice is filed under the nickname next to their volume, so a camera that
   * comes back does not come back unhidden.
   */
  const videoButton = el('button', {
    type: 'button',
    class: 'tile-video',
    onclick: (event) => {
      event.stopPropagation();
      tile.videoHidden = !tile.videoHidden;
      refreshTilePresentation(tile);
      rememberVolume(tile.nickname, tile);
      layoutVideoGrid(); // a tile that has given up its picture is sized as audio-only
    },
  });

  const fullscreen = el('button', {
    type: 'button',
    class: 'tile-fullscreen',
    text: '⛶',
    title: t('ui.tile.fullscreen'),
    'aria-label': t('ui.tile.fullscreen'),
    onclick: (event) => {
      event.stopPropagation();
      toggleTileFullscreen(tile);
    },
  });

  tile.volumeButton = button;
  tile.slider = slider;
  tile.videoButton = videoButton;
  return el('div', { class: 'tile-controls' }, button, slider, videoButton, fullscreen);
}

/** Puts away any tile's volume controls, wherever they are open. */
function closeTileControls() {
  for (const open of document.querySelectorAll('.tile.controls-open')) {
    open.classList.remove('controls-open');
  }
}

/**
 * Shows or hides one tile's volume controls.
 *
 * This is how they are reached without a pointer to hover with: a tap on the
 * tile opens them, a tap on it again - or on anything else - puts them away.
 * One tile's at a time, so a phone is never covered in sliders.
 *
 * The class goes on whatever the device, because the stylesheet only acts on it
 * under `(hover: none)`. Nothing here has to guess what kind of screen it is on,
 * and a hybrid laptop gets whichever way its user reaches for the tile.
 */
function toggleTileControls(tile) {
  const wanted = !tile.node.classList.contains('controls-open');
  closeTileControls();
  tile.node.classList.toggle('controls-open', wanted);
}

/**
 * Puts one tile full screen, or takes it back out.
 *
 * The tile rather than the video element, so the name and the volume controls
 * come with it. iOS Safari has no element fullscreen at all - only a video may
 * go full screen, through its own method - so that is the fallback.
 */
function toggleTileFullscreen(tile) {
  if (document.fullscreenElement) {
    document.exitFullscreen?.().catch(() => {});
    return;
  }
  const node = tile.node;
  if (node.requestFullscreen) {
    node.requestFullscreen().catch(() => tile.video?.webkitEnterFullscreen?.());
    return;
  }
  // iOS: the video knows how, even though its container does not.
  tile.video?.webkitEnterFullscreen?.();
}

/**
 * The button shown when the browser silenced a feed for want of a gesture.
 *
 * Clicking it is the gesture, so it can restore the sound itself; the global
 * hook in armGestureResume() usually gets there first, on whatever the user
 * clicks next.
 */
function buildBlockedButton(tile) {
  return el('button', {
    type: 'button',
    class: 'tile-blocked',
    hidden: true,
    text: t('ui.tile.soundBlocked'),
    onclick: (event) => {
      event.stopPropagation();
      applyTileVolume(tile);
      playTile(tile);
    },
  });
}

/**
 * Adds a track to a participant's tile, creating the tile on first use.
 * A second track of the same kind replaces the first.
 */
function addTile(room, { key, nickname, track, source, local = false, muted = false, orientation = null }) {
  const rs = roomState(room);
  const existing = rs.tiles.get(key);

  if (existing) {
    for (const current of existing.stream.getTracks()) {
      if (current.kind === track.kind) existing.stream.removeTrack(current);
    }
    existing.stream.addTrack(track);
    if (track.kind === 'video') existing.orientation = orientation;
    // Re-attaching is needed for the element to pick up a newly added track.
    existing.video.srcObject = existing.stream;
    refreshTilePresentation(existing);
    applyTileVolume(existing);
    playTile(existing);
    watchTileAudio(room, key, existing);
    if (room === state.activeRoom) renderVideoGrid();
    return existing;
  }

  const stream = new MediaStream([track]);
  const video = el('video', { autoplay: true, playsinline: true });
  video.muted = local || muted;
  video.srcObject = stream;
  // Route remote audio to the user's chosen output device, where supported.
  if (!local) applyOutput(video).catch(() => {});

  const placeholder = el('div', { class: 'avatar', text: '🔊' });
  const node = el(
    'div',
    { class: 'tile' },
    placeholder,
    video,
    el(
      'div',
      { class: 'label' },
      el('span', { text: `${nickname}${local ? ' (you)' : ''}` }),
      source === 'screen' ? el('span', { text: '🖥' }) : null,
    ),
  );

  // Where this person was last set, so a tile that opens again - a camera turned
  // back on, a reconnection, a visit tomorrow - opens at the level you chose.
  // Your own tile is silenced against feedback and has no controls, so it has no
  // level to remember.
  const level = local ? { volume: 1, muted: false, videoHidden: false } : recallVolume(nickname);
  const tile = {
    node,
    stream,
    video,
    placeholder,
    source,
    nickname,
    local,
    volume: level.volume,
    muted: level.muted,
    videoHidden: level.videoHidden === true,
    orientation,
    rotation: 0,
  };
  node.addEventListener('dblclick', () => toggleTileFullscreen(tile));
  // Applied again once the element has a frame to show, so the transform cannot
  // be missed if the orientation arrived before the picture did.
  video.addEventListener('loadedmetadata', () => refreshTileRotation(tile));
  if (!local) {
    tile.controls = buildTileControls(tile);
    tile.blockedButton = buildBlockedButton(tile);
    node.append(tile.controls, tile.blockedButton);
    // The whole tile is the target: on a phone it is small enough that aiming
    // for the strip in its corner is no way to reach a volume. The controls
    // themselves stop their own clicks getting here, so adjusting one does not
    // put it away mid-drag.
    node.addEventListener('click', () => toggleTileControls(tile));
  }
  refreshTilePresentation(tile);
  applyTileVolume(tile);
  playTile(tile);
  watchTileAudio(room, key, tile);
  rs.tiles.set(key, tile);
  if (room === state.activeRoom) renderVideoGrid();
  return tile;
}

/**
 * Stops publishing one of your own sources and takes its track out of your tile.
 *
 * The two belong together. Missing the second half is what left a stale
 * audio-only box on your own screen after the camera was switched off: the
 * camera releases the microphone it brought with it, everyone else saw both
 * producers close and dropped the tile, while here only the video track was
 * taken out and the tile lived on with the audio.
 */
async function stopPublishing(room, media, source) {
  await media.unpublish(source);
  removeLocalTrack(room, source);
}

/**
 * Drops your own preview for a source.
 *
 * Only the track of that kind goes: the microphone shares the camera's tile, so
 * switching the mic off must not tear down a self-view that is still showing
 * video, and vice versa. removeTile() drops the tile once nothing is left in it.
 */
function removeLocalTrack(room, source) {
  removeTile(room, tileKey('self', source), source === 'mic' ? 'audio' : 'video');
}

/**
 * Removes one track from a tile, dropping the tile once it is empty.
 * `kind` narrows it to audio or video; omitted removes the whole tile.
 */
function removeTile(room, key, kind = null) {
  const rs = state.rooms.get(room);
  if (!rs) return;
  const tile = rs.tiles.get(key);
  if (!tile) return;

  if (kind) {
    for (const track of tile.stream.getTracks()) {
      if (track.kind === kind) tile.stream.removeTrack(track);
    }
    if (tile.stream.getTracks().length > 0) {
      tile.video.srcObject = tile.stream;
      refreshTilePresentation(tile);
      // Re-attaching pauses the element in some browsers.
      playTile(tile);
      watchTileAudio(room, key, tile);
      if (room === state.activeRoom) renderVideoGrid();
      return;
    }
  }

  speakingMonitor.unwatch(`${room}\u0000${key}`);
  try {
    tile.video.srcObject = null;
  } catch {
    /* ignore */
  }
  rs.tiles.delete(key);
  if (room === state.activeRoom) renderVideoGrid();
}

// ---------------------------------------------------------------- media flow

function ensureMedia(room) {
  const rs = roomState(room);
  if (rs.media) return rs.media;
  rs.media = new RoomMedia({
    connection: state.conn,
    room,
    onTrack: ({ peerId, nickname, track, source, orientation }) => {
      addTile(room, { key: tileKey(peerId, source), nickname, track, source, orientation });
    },
    onTrackEnded: ({ peerId, source, kind }) => removeTile(room, tileKey(peerId, source), kind),
    onStateChange: ({ direction, state: connState }) => {
      if (connState === 'failed') {
        notice(t('error.mediaFailed', { direction }), 'error', room);
      }
    },
    onError: (err) => notice(errorText(err), 'error', room),
  });
  return rs.media;
}

/**
 * Opens a capture stream for a source using the saved device settings.
 *
 * The webcam takes audio as well as video: turning on your camera in a call is
 * expected to bring your voice with it. The microphone can then be muted
 * independently with the Mic button.
 */
async function captureFor(source) {
  if (source === 'mic') return navigator.mediaDevices.getUserMedia(audioConstraints());
  if (source === 'camera') {
    return navigator.mediaDevices.getUserMedia({ ...videoConstraints(), ...audioConstraints() });
  }
  return navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
}

/**
 * Captures the camera on its own, for when the microphone cannot be opened.
 *
 * Turning on the camera deliberately captures audio as well, and getUserMedia
 * rejects the whole request when either kind is unavailable. So a machine with
 * no reachable microphone - none attached, or the operating system withholding
 * it, which on macOS surfaces as a bare NotFoundError with no device listed -
 * could not publish video at all, and the failure was reported as a camera
 * fault. Video alone is worth having; the caller says so in a notice.
 */
async function captureCameraOnly() {
  try {
    return await navigator.mediaDevices.getUserMedia(videoConstraints());
  } catch {
    return null;
  }
}

/** Publishes the microphone track that came with a webcam stream. */
async function publishCameraAudio(room, media, stream) {
  const audioTrack = stream.getAudioTracks()[0];
  if (!audioTrack) return false;
  if (media.isPublishing('mic')) {
    // Already have a microphone running; keep it and drop the spare track.
    audioTrack.stop();
    return false;
  }
  await media.publish(audioTrack, 'mic');
  addTile(room, {
    key: tileKey('self', 'mic'),
    nickname: state.session?.nickname ?? 'you',
    track: audioTrack,
    source: 'mic',
    local: true,
  });
  const rs = roomState(room);
  if (rs.micMuted) await media.setPaused('mic', true);
  return true;
}

async function toggleSource(source) {
  const room = state.activeRoom;
  if (!room) return;
  const media = ensureMedia(room);

  if (media.isPublishing(source)) {
    await stopPublishing(room, media, source);
    // Turning the webcam off also releases the microphone it brought with it.
    if (source === 'camera' && media.isPublishing('mic')) await stopPublishing(room, media, 'mic');
    renderMediaButtons();
    return;
  }

  let stream;
  let micUnavailable = false;
  try {
    stream = await captureFor(source);
  } catch (err) {
    stream = await retryWithoutSavedDevice(source, err, captureFor);
    if (!stream && source === 'camera') {
      // Last resort: the camera on its own. See captureCameraOnly().
      stream = await captureCameraOnly();
      micUnavailable = Boolean(stream);
    }
    if (!stream) {
      if (err?.name === 'NotAllowedError') {
        notice(t('error.permissionDenied'), 'error', room);
      } else if (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError') {
        await pruneMissingDevices();
        notice(
          t('error.deviceGone', {
            device: source === 'mic' ? t('error.deviceMicrophone') : t('error.deviceCamera'),
          }),
          'error',
          room,
        );
      } else {
        notice(t('error.couldNotStart', { source, error: errorText(err) }), 'error', room);
      }
      return;
    }
  }

  const track = source === 'mic' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
  if (!track) return;

  try {
    await media.publish(track, source, publisherOrientation());
    if (source === 'mic' && roomState(room).micMuted) await media.setPaused('mic', true);

    // Audio gets a tile too, so turning on the microphone shows the same box
    // everyone else's stream gets. The element is muted on local tiles, so
    // attaching your own audio cannot feed back.
    addTile(room, {
      key: tileKey('self', source),
      nickname: state.session?.nickname ?? 'you',
      track,
      source,
      local: true,
    })
    // The webcam brings the microphone along with it.
    if (source === 'camera') await publishCameraAudio(room, media, stream);
    if (micUnavailable) notice(t('notice.cameraWithoutMic'), 'info', room);

    renderMediaButtons();
  } catch (err) {
    for (const t of stream.getTracks()) t.stop();
    notice(t('error.couldNotPublish', { source, error: errorText(err) }), 'error', room);
  }
}

/**
 * Mic button: starts or stops sending audio altogether.
 *
 * This is deliberately separate from muting. Muting keeps you in the call with
 * the microphone paused; stopping releases the device and removes your audio
 * stream from the room - including the microphone the camera brought with it,
 * which otherwise could not be switched off without also killing the video.
 */
async function toggleMicrophone() {
  const room = state.activeRoom;
  if (!room) return;
  const rs = roomState(room);
  const media = ensureMedia(room);

  if (media.isPublishing('mic')) {
    await stopPublishing(room, media, 'mic');
    rs.micMuted = false;
    notice(t('notice.micOff'), 'info', room);
    renderMediaButtons();
    return;
  }

  // Under push to talk the resting state is muted, so the microphone opens that
  // way and the Talk button is what lets anything out.
  rs.micMuted = getSettings().pushToTalk === true;
  await toggleSource('mic');
}

/** Mute button: pauses or resumes the microphone without giving up the device. */
async function toggleMute() {
  const room = state.activeRoom;
  if (!room) return;
  await setMicMuted(room, !roomState(room).micMuted, { announce: true });
}

/**
 * Mutes or unmutes the microphone without giving up the device.
 *
 * Push to talk drives this many times a minute, so it says nothing in the
 * transcript unless asked to: the Mute button announces the change, holding the
 * Talk button does not.
 */
async function setMicMuted(room, muted, { announce = false } = {}) {
  const rs = roomState(room);
  const media = state.rooms.get(room)?.media;
  if (!media?.isPublishing('mic')) return false;
  if (rs.micMuted === muted) return true;

  rs.micMuted = muted;
  renderMediaButtons();
  try {
    await media.setPaused('mic', muted);
    if (announce) notice(muted ? t('notice.micMuted') : t('notice.micUnmuted'), 'info', room);
  } catch (err) {
    rs.micMuted = !muted;
    notice(t('notice.micChangeFailed', { error: errorText(err) }), 'error', room);
    renderMediaButtons();
    return false;
  }
  renderMediaButtons();
  return true;
}

// ----------------------------------------------------------------- settings

/**
 * Restarts a live capture after its device or quality changed, so a new
 * selection takes effect immediately instead of at the next toggle.
 */
/**
 * Re-captures the camera when the device is turned.
 *
 * videoConstraints() asks for portrait dimensions on a phone held upright, so a
 * rotation has to reach the capture pipeline to take effect - the track already
 * running keeps the shape it was opened with. This is the same republish a
 * quality change performs.
 *
 * Rotation fires before the layout has settled, and often twice, so the work is
 * debounced and then skipped unless the portrait/landscape state really changed.
 */
function watchOrientation() {
  let lastPortrait = wantsPortraitCapture();
  let timer = null;

  const settle = () => {
    timer = null;
    const portrait = wantsPortraitCapture();
    if (portrait === lastPortrait) return;
    lastPortrait = portrait;
    const room = state.activeRoom;
    if (!room) return;
    const media = state.rooms.get(room)?.media;
    if (!media?.isPublishing('camera')) return;
    // Reporting the new angle is enough, and unlike re-capturing it does not
    // interrupt the feed. Each viewer turns the picture using the frame size it
    // decoded, so this stays correct whether or not the capture itself changed.
    media
      .setOrientation('camera', publisherOrientation())
      .catch((err) => notice(errorText(err), 'error', room));
  };

  const onChange = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(settle, 400);
  };

  // screen.orientation is the modern event; orientationchange covers browsers
  // that predate it. Both firing just coalesces into one debounce.
  screen.orientation?.addEventListener?.('change', onChange);
  window.addEventListener('orientationchange', onChange);
}

/**
 * Uploads one file and posts it to the room.
 *
 * Whatever is already typed in the box travels with it as a caption, in the same
 * message, so a picture can be sent with a word about it. The address is what the
 * message actually carries; the thumbnail is drawn from it when rendering.
 */
async function attachFile(file) {
  const room = state.activeRoom;
  // A conversation is somewhere to send a picture too. The ticket is asked for
  // without a room, which the server already allows - the room on a ticket is
  // only what the upload is counted against, and a private message is counted
  // against no channel.
  if ((!room && !state.activeDm) || !file) return;
  const status = $('attach-status');
  const input = $('input-message');
  const button = $('btn-attach');

  button.disabled = true;
  status.textContent = t('ui.attach.uploading', { name: file.name, percent: 0 });
  try {
    const stored = await uploadFile({
      connection: state.conn,
      room,
      file,
      onProgress: (fraction) => {
        status.textContent = t('ui.attach.uploading', {
          name: file.name,
          percent: Math.round(fraction * 100),
        });
      },
    });
    const caption = input.value.trim();
    input.value = '';
    // The path is what is stored, so history survives the site changing domain.
    // On its own it would start with a slash and be taken for a command, so it
    // goes through the "//" escape that chat.send already understands - and that
    // deliver() undoes again for a conversation, where the text is not read for
    // commands and the escape would travel as part of the address.
    await sendMessage(caption ? `${caption} ${stored.url}` : `/${stored.url}`);
    status.textContent = '';
  } catch (err) {
    status.textContent = '';
    notice(errorText(err), 'error', room);
  } finally {
    button.disabled = false;
  }
}

/** Files chosen, dropped or pasted, one after another. */
async function attachFiles(files) {
  const list = [...(files ?? [])].filter((file) => file && file.size > 0);
  for (const file of list) {
    // eslint-disable-next-line no-await-in-loop -- each upload spends its own ticket
    await attachFile(file);
  }
}

async function signOut() {
  try {
    await state.conn?.request('auth.logout', {});
  } catch {
    /* the socket may already be gone; the local token still has to go */
  }
  localStorage.removeItem(TOKEN_KEY);
  // Including which rooms were open: on a shared browser the next person should
  // not be walked into them, and a hidden room's name is the whole of the way in.
  clearRooms();
  showGate(t('ui.gate.signedOut'), 'info');
}

async function republish(source) {
  const room = state.activeRoom;
  if (!room) return;
  const media = state.rooms.get(room)?.media;
  if (!media?.isPublishing(source)) return;

  await media.unpublish(source);
  removeLocalTrack(room, source);
  try {
    const stream = await captureFor(source);
    // Pick the track by kind: the camera stream also carries a microphone track,
    // so taking "the first track" could republish audio as the camera and lose
    // the picture entirely.
    const track = source === 'mic' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
    if (!track) {
      for (const spare of stream.getTracks()) spare.stop();
      return;
    }
    // Release anything we are not publishing here; the microphone has its own
    // producer and lifecycle.
    for (const spare of stream.getTracks()) {
      if (spare !== track) spare.stop();
    }

    await media.publish(track, source, publisherOrientation());
    if (source === 'mic' && roomState(room).micMuted) await media.setPaused('mic', true);
    // Audio gets a tile too, so turning on the microphone shows the same box
    // everyone else's stream gets. The element is muted on local tiles, so
    // attaching your own audio cannot feed back.
    addTile(room, {
      key: tileKey('self', source),
      nickname: state.session?.nickname ?? 'you',
      track,
      source,
      local: true,
    })
  } catch (err) {
    notice(t('error.couldNotRestart', { source, error: errorText(err) }), 'error', room);
  }
  renderMediaButtons();
}

/**
 * Whether a session that has never pressed the Jukebox button starts with one.
 *
 * A phone gets no by default. The jukebox takes half of what is left after the
 * room bar and the composer, and half of a phone's remaining height is a few
 * lines of conversation - so a room somebody joins to read arrives with the
 * reading squeezed out of it. Anywhere else it starts on, which is what it has
 * always done.
 *
 * Settled once, when the client loads, rather than read per call: a window
 * dragged wider mid-conversation should not start a video by itself, and one
 * dragged narrower should not stop the one somebody is watching.
 */
const JUKEBOX_STARTS_ON = !(
  typeof matchMedia === 'function' &&
  matchMedia('(pointer: coarse)').matches &&
  matchMedia('(max-width: 820px)').matches
);

/**
 * Whether this session wants the jukebox at all. The Jukebox button, or - until
 * it has been pressed even once - what the device it is running on suggests.
 */
function wantsYouTube() {
  const chosen = getSettings().showYouTube;
  return typeof chosen === 'boolean' ? chosen : JUKEBOX_STARTS_ON;
}


/** Applies the chosen output device to every audio/video element on screen. */
async function applyOutputToAll() {
  if (!canChooseOutput()) return;
  for (const rs of state.rooms.values()) {
    for (const tile of rs.tiles.values()) {
      if (!tile.local) await applyOutput(tile.video);
    }
  }
}

function fillSelect(select, devices, chosen, defaultLabel) {
  select.replaceChildren(
    el('option', { value: '', text: defaultLabel }),
    ...devices.map((device) => el('option', { value: device.deviceId, text: device.label })),
  );
  select.value = devices.some((d) => d.deviceId === chosen) ? chosen : '';
}

let micTest = null;

/** Stops the microphone level test, releasing its stream. */
function stopMicTest() {
  if (!micTest) return;
  cancelAnimationFrame(micTest.frame);
  micTest.stream.getTracks().forEach((track) => track.stop());
  micTest.context.close().catch(() => {});
  micTest = null;
  $('mic-meter').hidden = true;
  $('mic-meter-bar').style.width = '0%';
  $('btn-test-mic').textContent = t('ui.settings.testMic');
}

/** Opens the mic with the selected device and shows a live level meter. */
async function startMicTest() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(audioConstraints());
    const context = new (window.AudioContext ?? window.webkitAudioContext)();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    $('mic-meter').hidden = false;
    $('btn-test-mic').textContent = t('ui.settings.stopTest');

    const tick = () => {
      analyser.getByteFrequencyData(data);
      const peak = data.reduce((max, v) => (v > max ? v : max), 0);
      $('mic-meter-bar').style.width = `${Math.min(100, Math.round((peak / 255) * 140))}%`;
      micTest.frame = requestAnimationFrame(tick);
    };
    micTest = { stream, context, frame: 0 };
    tick();
  } catch (err) {
    $('settings-hint').textContent = t('ui.settings.micFailed', { error: errorText(err) });
  }
}

async function openSettings() {
  const backdrop = $('settings-backdrop');
  backdrop.hidden = false;
  $('settings-hint').textContent = t('ui.settings.loadingDevices');

  const devices = await pruneMissingDevices();
  // Read after the await, never before it: see the controls further down.
  const current = getSettings();

  const systemDefault = t('ui.settings.systemDefault');
  fillSelect($('dev-audio-input'), devices.audioInput, current.audioInput, systemDefault);
  fillSelect($('dev-video-input'), devices.videoInput, current.videoInput, systemDefault);
  fillSelect($('dev-audio-output'), devices.audioOutput, current.audioOutput, systemDefault);

  // The speaker picker stays available whenever the browser supports choosing an
  // output, even with nothing published: playback of *other* people is what it
  // controls. An empty list means the browser is still withholding device ids,
  // which a one-time permission unlocks.
  const supportsOutput = canChooseOutput();
  $('output-row').hidden = !supportsOutput;
  // `hidden.videoInput` rather than an empty camera list: a machine with no
  // camera at all would otherwise show the access button for ever, while a
  // Firefox session holding a microphone-only grant reports cameras present but
  // withheld - which is the case a permission prompt can actually fix.
  const needsAccess =
    devices.labelsHidden ||
    devices.audioInput.length === 0 ||
    devices.hidden?.videoInput > 0 ||
    (supportsOutput && devices.audioOutput.length === 0);
  $('btn-device-access').hidden = !needsAccess;

  // `current`, not the snapshot taken before the await above: enumerating devices
  // takes long enough to reach for a switch in, and reading the older values here
  // put the control back where it had been while the setting stayed changed.
  $('dev-quality').value = current.videoQuality;
  $('dev-ptt').checked = current.pushToTalk === true;
  $('dev-notify').checked = current.notificationSound !== false;

  const screenLive = state.activeRoom
    ? Boolean(state.rooms.get(state.activeRoom)?.media?.isPublishing('screen'))
    : false;
  const screenButton = $('btn-screen');
  screenButton.classList.toggle('on', screenLive);
  screenButton.querySelector('span').textContent = screenLive
    ? t('ui.settings.stopSharing')
    : t('ui.settings.shareScreen');
  screenButton.disabled = !state.activeRoom;

  $('settings-hint').textContent = needsAccess
    ? t('ui.settings.needsAccess')
    : !supportsOutput
      ? t('ui.settings.noOutputChoice')
      : '';
}

function closeSettings() {
  stopMicTest();
  $('settings-backdrop').hidden = true;
}

// ------------------------------------------------------------------ jukebox
/*
 * The room's shared YouTube player.
 *
 * One player for the client, not one per room: it is the room you are looking
 * at that is playing, and two rooms playing at once would be two songs at once.
 * Switching rooms tears it down and builds it again, which is also what stops a
 * room you have wandered out of from carrying on in the background.
 *
 * Everything on screen here is drawn from the server's snapshot and nothing is
 * decided locally - press skip and the button does not change, the next
 * snapshot changes it. That is what keeps every listener's panel saying the
 * same thing as everybody else's.
 */

/** @type {JukeboxPlayer|null} */
let jukeboxPlayer = null;
/** The room the player belongs to, and whether one is being built right now. */
let jukeboxPlayerRoom = null;
let jukeboxMounting = false;
/**
 * Set once YouTube's script has failed to load, and cleared when the panel
 * closes. Without it every re-draw would try again and say so again, which is a
 * stream of the same complaint about a network that is not going to improve
 * between one re-draw and the next.
 */
let jukeboxLoadFailed = false;

/** The active room's jukebox snapshot, or null when there is no jukebox in it. */
function jukeboxOf(room = state.activeRoom) {
  const snapshot = room ? state.rooms.get(room)?.jukebox : null;
  return snapshot?.open ? snapshot : null;
}

/** Whether this deployment has the jukebox at all. */
function jukeboxAvailable() {
  return state.info?.jukebox?.enabled !== false;
}

/**
 * Whether this deployment keeps channel statistics.
 *
 * Read from the server's own policy rather than assumed, so a server with
 * STATS_ENABLED=0 shows no menu entry at all instead of one that opens a panel
 * with nothing behind it.
 */
function statsAvailable() {
  return state.info?.stats?.enabled === true;
}

/**
 * Files a snapshot against its room, stamped with the moment it arrived.
 *
 * The stamp is what the player measures the position from, so it is taken here,
 * once, rather than wherever the snapshot is next read - see follow() in
 * client/jukebox.js.
 */
function applyJukeboxState(snapshot) {
  const room = snapshot?.room;
  const rs = existingRoom(room);
  if (!rs) return;
  rs.jukebox = snapshot?.open ? { ...snapshot, receivedAt: Date.now() } : null;
  if (room === state.activeRoom) renderJukebox();
}

function teardownJukeboxPlayer() {
  jukeboxPlayer?.destroy();
  jukeboxPlayer = null;
  jukeboxPlayerRoom = null;
  jukeboxLoadFailed = false;
  $('jukebox-blocked').hidden = true;
  $('jukebox-stage').replaceChildren();
}

/** Builds the player for `room`, unless one is already on its way. */
async function ensureJukeboxPlayer(room) {
  if (jukeboxMounting || jukeboxLoadFailed) return;
  if (jukeboxPlayer && jukeboxPlayerRoom === room) return;
  jukeboxMounting = true;
  const player = new JukeboxPlayer({
    volume: effectiveVolume(getSettings().jukeboxVolume ?? 1),
    muted: getSettings().jukeboxMuted === true,
    onEnded: (key) => state.conn?.notify('jukebox.ended', { room, key }),
    onDuration: (key, duration) => state.conn?.notify('jukebox.duration', { room, key, duration }),
    onFailed: (key) => state.conn?.notify('jukebox.failed', { room, key }),
    onBlocked: (reason) => {
      const button = $('jukebox-blocked');
      button.hidden = !reason;
      if (reason) button.textContent = t('ui.tile.soundBlocked');
    },
  });
  try {
    await player.mount($('jukebox-stage'));
  } catch {
    jukeboxMounting = false;
    jukeboxLoadFailed = true;
    player.destroy();
    notice(t('ui.jukebox.playerFailed'), 'error', room);
    return;
  }
  jukeboxMounting = false;
  // The room may have been left, or switched away from, while YouTube's script
  // was loading. Whatever was built for it is of no use here - and the room now
  // on screen has been waiting behind this one's mount, so it is drawn again
  // rather than left with no player until something else happens to redraw it.
  if (state.activeRoom !== room || !jukeboxOf(room)) {
    player.destroy();
    renderJukebox();
    return;
  }
  jukeboxPlayer = player;
  jukeboxPlayerRoom = room;
  renderJukebox();
}

/** Applies the jukebox's own level and mute, both under the master ones. */
function applyJukeboxVolume() {
  const { jukeboxVolume = 1, jukeboxMuted = false } = getSettings();
  jukeboxPlayer?.setVolume(effectiveVolume(jukeboxVolume), jukeboxMuted || masterMuted());
}

/**
 * The level and the mute switch.
 *
 * The slider is only on screen where setting a level in software does anything:
 * on a phone it does not, and a control that visibly moves while nothing happens
 * is worse than no control. The mute button stays either way - it works
 * everywhere, and it is the thing people actually want in a hurry.
 */
function renderJukeboxVolume() {
  const { jukeboxVolume = 1, jukeboxMuted = false } = getSettings();
  const slider = $('jukebox-volume');
  const button = $('jukebox-mute');
  const settable = canSetVolume();

  slider.hidden = !settable;
  // Position rather than level, as in renderMasterVolume above.
  // On YouTube's own scale; see JUKEBOX_MIN_DB in gain.js.
  const percent = sliderForGain(jukeboxVolume, JUKEBOX_MIN_DB);
  slider.value = String(percent);
  // Silent either way: muted, or turned all the way down.
  const silent = jukeboxMuted || jukeboxVolume === 0;
  button.textContent = silent ? '🔇' : '🔊';
  const label = silent ? t('ui.jukebox.unmute') : t('ui.jukebox.mute');
  button.title = settable ? `${label} — ${percent}%` : label;
  button.setAttribute('aria-label', label);
  button.setAttribute('aria-pressed', jukeboxMuted ? 'true' : 'false');
}

function renderJukebox() {
  const panel = $('jukebox');
  const room = state.activeRoom;
  const snapshot = jukeboxOf(room);
  const wasShown = !panel.hidden;

  // The composer's Jukebox button reads this same state, and every path that
  // changes it comes through here - a snapshot arriving, a room being switched
  // to, the button itself being pressed. Before the early return below, so that
  // switching it off still leaves the button telling the truth.
  renderMediaButtons();

  // Switched off, or the room has no jukebox: the same outcome either way, and
  // the teardown is what stops a player that was already running. Returning here
  // is also what keeps youtube.com out of it entirely - the frame and its script
  // are only ever fetched from ensureJukeboxPlayer() below.
  if (!snapshot || !wantsYouTube()) {
    panel.hidden = true;
    if (jukeboxPlayer) teardownJukeboxPlayer();
    if (wasShown) layoutVideoGrid(); // the transcript has its half back
    return;
  }
  if (jukeboxPlayer && jukeboxPlayerRoom !== room) teardownJukeboxPlayer();
  panel.hidden = false;
  applyJukeboxSplit();

  const current = snapshot.current;
  $('jukebox-title').textContent = current ? current.title : t('ui.jukebox.nothingPlaying');
  $('jukebox-title').title = current?.title ?? '';
  $('jukebox-by').textContent = current
    ? [current.author, t('ui.jukebox.queuedBy', { nickname: current.by })].filter(Boolean).join(' · ')
    : jukeboxOperator(room)
      ? t('ui.jukebox.addSomething')
      : '';
  $('jukebox-stage').hidden = !current;
  const idle = $('jukebox-idle');
  idle.hidden = Boolean(current);
  idle.textContent = t('ui.jukebox.nothingPlaying');

  renderJukeboxVolume();
  renderJukeboxControls(room, snapshot);
  renderJukeboxQueue(room, snapshot);

  if (current) ensureJukeboxPlayer(room);
  jukeboxPlayer?.follow(snapshot);
  applyJukeboxVolume();
  if (!wasShown) layoutVideoGrid(); // the cameras now share the room with this
}

/**
 * Whether this session runs the jukebox in `room` - the queue is theirs to see
 * and to change. Named for the badge it once required; it is jukebox.control
 * that decides, and any signed-in user holds it.
 */
function jukeboxOperator(room = state.activeRoom) {
  return capsFor(room)['jukebox.control'] === true;
}

function renderJukeboxControls(room, snapshot) {
  const send = (type, data = {}) =>
    state.conn.request(type, { room, ...data }).catch((err) => notice(errorText(err), 'error', room));

  const buttons = [];
  if (jukeboxOperator(room)) {
    buttons.push(
      el('button', {
        type: 'button',
        class: 'ghost',
        title: snapshot.playing ? t('ui.jukebox.pause') : t('ui.jukebox.play'),
        'aria-label': snapshot.playing ? t('ui.jukebox.pause') : t('ui.jukebox.play'),
        text: snapshot.playing ? '⏸' : '▶',
        onclick: () => send(snapshot.playing ? 'jukebox.pause' : 'jukebox.resume'),
      }),
      el('button', {
        type: 'button',
        class: 'ghost',
        title: t('ui.jukebox.skip'),
        'aria-label': t('ui.jukebox.skip'),
        text: '⏭',
        // Nothing playing and nothing queued: there is no next track to reach.
        disabled: !snapshot.current && (snapshot.queue?.length ?? 0) === 0,
        onclick: () => send('jukebox.skip'),
      }),
      // Stopping it is not here: it lives in the room's menu, beside the entry
      // that started it. Closing the jukebox ends it for the whole room, which
      // is a different weight of decision from pausing a track, and it read as
      // the same weight while it sat a few pixels from the skip button.
    );
  }
  $('jukebox-controls').replaceChildren(...buttons);
}

/**
 * The queue, which only those who run the jukebox have.
 *
 * The server leaves it out of everybody else's snapshot rather than sending a
 * list this could decline to draw, so what is coming next is not merely
 * unchangeable to the room but unknown to it. `queue` being absent is therefore
 * the same answer as not holding the capability, and either one hides the
 * column - the picture takes the whole panel instead.
 */
function renderJukeboxQueue(room, snapshot) {
  const queue = jukeboxOperator(room) ? snapshot.queue : null;
  $('jukebox-queue').hidden = !queue;
  if (!queue) return;

  $('jukebox-queue-title').textContent = t('ui.jukebox.queueCount', { count: queue.length });

  const list = $('jukebox-list');
  if (queue.length === 0) {
    list.replaceChildren(el('li', { class: 'jukebox-empty', text: t('ui.jukebox.queueEmpty') }));
    return;
  }

  list.replaceChildren(
    ...queue.map((track, index) =>
      el(
        'li',
        {},
        el('span', { class: 'n', text: `${index + 1}` }),
        el(
          'span',
          { class: 'track', title: `${track.title} — ${t('ui.jukebox.queuedBy', { nickname: track.by })}` },
          track.title,
          el('span', { class: 'queued-by', text: ` · ${track.by}` }),
        ),
        el('button', {
          type: 'button',
          class: 'drop',
          title: t('ui.jukebox.remove'),
          'aria-label': t('ui.jukebox.remove'),
          text: '✕',
          onclick: () =>
            state.conn
              .request('jukebox.remove', { room, key: track.key })
              .catch((err) => notice(errorText(err), 'error', room)),
        }),
      ),
    ),
  );
}

// ---- how the jukebox and the transcript divide the space they share
//
// The stylesheet states the split as two `fr` values on `.main`, and they are
// all that is written here. Which template is in force - the jukebox above the
// transcript, or beside it in a wide window, with or without a column of
// cameras alongside - stays the stylesheet's business, and every one of them
// reads the same pair. So this does not choose the arrangement; it only asks
// which way the seam runs, and the handle answers that by its own shape.

/** Neither panel may be dragged smaller than this, in pixels. */
const MIN_JUKEBOX = { block: 120, inline: 220 };
const MIN_TRANSCRIPT = { block: 90, inline: 260 };
const DEFAULT_SPLIT = 0.5;

/**
 * Which way the two panels are divided, read off the handle rather than out of
 * a media query: a separator taller than it is wide is standing between two
 * columns, and one wider than it is tall lies between two rows. The stylesheet
 * decides the arrangement, this notices what it decided, and neither has to
 * keep a copy of the other's conditions.
 */
function jukeboxAxis() {
  const handle = $('jukebox-resize');
  return handle.offsetHeight > handle.offsetWidth ? 'inline' : 'block';
}

/** The two panels' current sizes along the axis they share. */
function jukeboxPanels() {
  const axis = jukeboxAxis();
  const jukebox = $('jukebox');
  const chat = document.querySelector('.messages-area');
  const size = (node) => (axis === 'inline' ? node.offsetWidth : node.offsetHeight);
  return { axis, jukebox: size(jukebox), chat: size(chat), pool: size(jukebox) + size(chat) };
}

/** The stored share, clamped: storage is editable and 0 would be a hidden panel. */
function jukeboxSplit() {
  const stored = Number(getSettings().jukeboxSplit);
  return Number.isFinite(stored) ? Math.min(0.85, Math.max(0.15, stored)) : DEFAULT_SPLIT;
}

/** Writes the split to the grid, and to the handle for a screen reader. */
function applyJukeboxSplit(share = jukeboxSplit()) {
  const main = document.querySelector('.main');
  main.style.setProperty('--jukebox-share', `${share}fr`);
  main.style.setProperty('--chat-share', `${1 - share}fr`);
  const handle = $('jukebox-resize');
  handle.setAttribute('aria-valuenow', String(Math.round(share * 100)));
  // A separator between two columns is itself vertical, and between two rows
  // horizontal - the orientation of the bar, not of the movement.
  handle.setAttribute('aria-orientation', jukeboxAxis() === 'inline' ? 'vertical' : 'horizontal');
}

/**
 * Moves the split, keeping both panels usable.
 *
 * The bounds are in pixels rather than as a share, because "big enough to be
 * worth having" is a number of lines and a size of picture, not a fraction of
 * however large this window happens to be - and the number is a different one
 * along each axis, since a panel needs more width to be worth a queue than it
 * needs height to be worth a video.
 */
function setJukeboxSplit(share, { remember = false } = {}) {
  const { axis, pool } = jukeboxPanels();
  const floor = MIN_JUKEBOX[axis];
  const ceiling = MIN_TRANSCRIPT[axis];
  let next = share;
  if (pool > floor + ceiling) {
    next = Math.min(Math.max(share, floor / pool), 1 - ceiling / pool);
  }
  next = Math.min(0.85, Math.max(0.15, next));
  applyJukeboxSplit(next);
  if (remember) updateSettings({ jukeboxSplit: next });
  return next;
}

/**
 * The split as it currently stands on screen, rather than as it was last stored.
 *
 * A drag that hit one of the pixel bounds ends somewhere the stored figure does
 * not describe, and the next keypress has to carry on from where the panel
 * actually is - not from where it would have been if nothing had stopped it.
 */
function jukeboxSplitFromLayout() {
  const { jukebox, pool } = jukeboxPanels();
  return pool > 0 ? jukebox / pool : DEFAULT_SPLIT;
}

/**
 * The drag itself. Pointer events, captured on the handle, so a pointer that
 * slides off it - or off the window - still steers and still finishes; and the
 * frame is made click-through for the duration, since an iframe from another
 * origin swallows anything that reaches it.
 *
 * The handle is at the foot of the jukebox when the two are stacked and at its
 * leading edge when they are side by side, so the panel grows as the pointer
 * moves down in the first case and as it moves *left* in the second. Hence the
 * sign: either way the panel follows the bar, which is the only thing anybody
 * dragging it is thinking about.
 */
function wireJukeboxResize() {
  const handle = $('jukebox-resize');
  const main = document.querySelector('.main');
  let start = null;

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    const { axis, jukebox } = jukeboxPanels();
    start = { axis, from: axis === 'inline' ? event.clientX : event.clientY, size: jukebox };
    handle.classList.add('dragging');
    main.classList.add('resizing');
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      /* the move and up events still arrive; only the capture was refused */
    }
  });

  handle.addEventListener('pointermove', (event) => {
    if (!start) return;
    const { pool } = jukeboxPanels();
    if (pool <= 0) return;
    const moved =
      start.axis === 'inline' ? start.from - event.clientX : event.clientY - start.from;
    setJukeboxSplit((start.size + moved) / pool);
  });

  const finish = () => {
    if (!start) return;
    start = null;
    handle.classList.remove('dragging');
    main.classList.remove('resizing');
    // Written once, at the end: a write per pointermove is a write per frame.
    setJukeboxSplit(jukeboxSplitFromLayout(), { remember: true });
    layoutVideoGrid();
  };
  for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
    handle.addEventListener(type, finish);
  }

  // The keyboard equivalent: the arrows along the axis the bar can move, each
  // moving it the way it points. Home (or Enter) is the way back to the middle.
  handle.addEventListener('keydown', (event) => {
    const sideways = jukeboxAxis() === 'inline';
    const steps = sideways
      ? { ArrowLeft: 0.02, ArrowRight: -0.02 }
      : { ArrowUp: -0.02, ArrowDown: 0.02 };
    const step = steps[event.key];
    if (step === undefined) {
      if (event.key !== 'Home' && event.key !== 'Enter') return;
      event.preventDefault();
      setJukeboxSplit(DEFAULT_SPLIT, { remember: true });
      layoutVideoGrid();
      return;
    }
    event.preventDefault();
    setJukeboxSplit(jukeboxSplitFromLayout() + step, { remember: true });
    layoutVideoGrid();
  });

  handle.addEventListener('dblclick', () => {
    setJukeboxSplit(DEFAULT_SPLIT, { remember: true });
    layoutVideoGrid();
  });

  /*
   * A window crossing one of the widths at which the panels change places turns
   * the seam from horizontal to vertical. Two things have to follow it: what a
   * screen reader is told the bar is, and the split itself, whose floors are a
   * width in one arrangement and a height in the other - a share that left room
   * for a video above the chat can leave none for a queue beside it.
   *
   * Not remembered, so a window briefly too narrow for the choice someone made
   * does not overwrite it; the stored share is re-clamped, not replaced.
   */
  refreshJukeboxSplit = () => setJukeboxSplit(jukeboxSplit());
  if (typeof matchMedia === 'function') {
    for (const width of [820, 1020]) {
      matchMedia(`(min-width: ${width}px)`).addEventListener('change', () => refreshJukeboxSplit());
    }
  }
}

/**
 * Re-reads the split for an arrangement that has changed underneath it. Set by
 * wireJukeboxResize(); a no-op before then, and where the cameras move without
 * the window changing size (see the stage toggle in wireUi).
 */
let refreshJukeboxSplit = () => {};

/** Asks for a link and queues it. For whoever runs the jukebox, like the queue. */
function openJukeboxAddDialog(room) {
  const input = el('input', {
    placeholder: 'https://www.youtube.com/watch?v=…',
    maxlength: 500,
    autocomplete: 'off',
    spellcheck: 'false',
  });
  openModal({
    title: t('ui.jukebox.addTitle'),
    body: el(
      'div',
      {},
      el('label', { text: t('ui.jukebox.link') }),
      input,
      el('p', { class: 'hint', text: t('ui.jukebox.addHint') }),
    ),
    confirmText: t('ui.jukebox.addConfirm'),
    onConfirm: async () => {
      const url = input.value.trim();
      if (!url) throw new Error(t('ui.jukebox.linkRequired'));
      const { track } = await state.conn.request('jukebox.add', { room, url });
      notice(t('ui.jukebox.queued', { title: track.title }), 'info', room);
    },
  });
}

// ---------------------------------------------------------------- room flow

/**
 * The room to open when none was named. Taken from the server's policy
 * (DEFAULT_ROOMS) rather than hardcoded, so it is right for whatever this
 * deployment is configured with, and falls back to the first room that exists.
 */
function defaultRoomName() {
  return state.info?.policy?.defaultRoom ?? state.roomList[0]?.name ?? '';
}

async function joinRoom(name, { hidden = false } = {}) {
  const room = String(name).replace(/^#+/, '').toLowerCase();
  try {
    const result = await state.conn.request('room.join', { room, ...(hidden ? { hidden: true } : {}) });
    applyJoin(result);
  } catch (err) {
    notice(t('error.couldNotJoin', { room, error: errorText(err) }), 'error');
  }
}

/**
 * Writes the open rooms down, so a refresh comes back to the same place.
 *
 * Called wherever the set of rooms or the active one changes rather than at some
 * tidy moment later: a browser is closed without warning, and a list saved "on
 * the way out" is a list that is usually a room behind.
 */
function rememberRooms() {
  const nickname = state.session?.nickname;
  if (!nickname) return;
  saveRooms(nickname, [...state.rooms.keys()], state.activeRoom);
}

/**
 * Opens the rooms this browser had open, after a refresh or a sign-in.
 *
 * `first` is the room the caller has already dealt with - the one named on the
 * join form, or the default - and is skipped rather than joined twice. Anything
 * the server refuses is dropped from the stored list instead of being retried on
 * every load: a room that was removed, or that this person has since been banned
 * from, would otherwise produce the same error every time the page opened.
 */
/**
 * Joins one room during a restore.
 *
 * A room that will not open is dropped from the stored list, but only when the
 * *server* refused it - a ban, a room since removed. An error with no code came
 * from the socket rather than from an answer (see rpc.js, which rejects pending
 * requests with a plain Error when the connection closes), and forgetting a room
 * because the network hiccuped would lose it permanently for no reason.
 */
async function restoreRoom(nickname, room, { activate }) {
  try {
    applyJoin(await state.conn.request('room.join', { room }), {
      activate,
      refreshList: activate,
    });
    return true;
  } catch (err) {
    if (err.code) {
      forgetRoom(nickname, room);
      notice(t('error.couldNotRestore', { room, error: errorText(err) }), 'error');
    }
    return false;
  }
}

/**
 * Reopens the rooms this browser had open, the one being read first.
 *
 * The room the reader was last looking at is joined and shown before anything
 * else is attempted, and the others follow behind it without being waited for.
 * That ordering is the whole of this function's purpose: rejoining is several
 * round trips, and doing them in a row before showing anything means a reader
 * with four rooms open waits four times as long to see the one they care about
 * as a reader with one - having already, on a refresh, waited through a page
 * load. Now the wait is one join deep whatever else is open.
 *
 * The rest are joined together rather than in sequence, and none of them
 * switches the view when it arrives; they appear in the room list as they land.
 *
 * @param {object}   options
 * @param {?string}  options.first  a room the caller has in mind - the one named
 *                                  on the join form - which outranks the stored one
 * @param {?string[]} options.rooms the rooms to open, when the caller knows them
 *                                  better than storage does (a reconnection)
 * @param {boolean}  options.force  rejoin even rooms this tab already believes it
 *                                  is in, which is the case after the socket dropped
 */
async function restoreRooms({ first = null, rooms = null, force = false } = {}) {
  const nickname = state.session?.nickname;
  if (!nickname) return null;

  const limit = state.info?.policy?.maxRoomsPerConnection ?? undefined;
  const stored = loadRooms(nickname, limit ? { limit } : {});
  const list = rooms ?? stored.rooms;
  if (list.length === 0) return null;

  /*
   * Which room to put on screen, most specific first: one the caller named, then
   * the one this tab was reading before the socket dropped, then the one stored
   * from the last visit. Each is only honoured if it is actually in the list.
   */
  const wanted =
    [first, state.activeRoom, stored.active].find((name) => name && list.includes(name)) ?? list[0];

  /*
   * The room to show, and the ones to fall back on if it will not open. A stored
   * room can be refused - somebody banned from it since, a room since removed -
   * and leaving the reader looking at nothing because their last room happened
   * to be the unlucky one would be a poor way to come back.
   */
  const order = [wanted, ...list.filter((room) => room !== wanted)];
  const attempted = new Set();
  let target = null;

  for (const room of order) {
    if (!force && state.rooms.has(room)) {
      setActiveRoom(room);
      target = room;
      break;
    }
    attempted.add(room);
    if (await restoreRoom(nickname, room, { activate: true })) {
      target = room;
      break;
    }
  }

  // Anything not yet tried. A room that already refused is not asked again: it
  // has been dropped from the list and has reported itself once, which is enough.
  const rest = order.filter(
    (room) => !attempted.has(room) && room !== target && (force || !state.rooms.has(room)),
  );
  if (rest.length === 0) {
    rememberRooms();
    return target;
  }

  /*
   * Deliberately not awaited. The caller is free to focus the composer and let
   * the reader start typing while these arrive; each one renders itself into the
   * room list as it does.
   */
  Promise.all(rest.map((room) => restoreRoom(nickname, room, { activate: false })))
    .then(() => {
      rememberRooms();
      // One list refresh for the whole batch rather than one per room.
      return refreshRoomList();
    })
    .catch(() => {
      /* restoreRoom() reports its own refusals; nothing here can fail */
    });

  return target;
}

function applyJoin(result, { activate = true, refreshList = true } = {}) {
  const room = result.room.name;
  const rs = roomState(room);
  rs.info = { ...result.room, hidden: Boolean(result.room.hidden) };
  rs.presence = result.presence ?? [];

  // Replayed history is prepended, not assigned: a server-pushed notice (for
  // example a plugin's welcome message) can arrive before this reply, and it
  // must not cost us the history. Ids keep a rejoin from duplicating anything.
  const history = (result.history ?? []).map((m) => ({ ...m, history: true }));
  if (history.length > 0) {
    const known = new Set(rs.messages.filter((m) => m.id !== undefined).map((m) => m.id));
    const fresh = history.filter((m) => !known.has(m.id));
    if (fresh.length > 0) rs.messages = [...fresh, ...rs.messages];
  }

  // Anything that arrived while the join was still in flight (the welcome
  // notice, for instance) belongs after the history.
  if (result.capabilities && state.session) {
    state.session.roomCapabilities = { ...(state.session.roomCapabilities ?? {}), [room]: result.capabilities };
  }

  const queued = state.pending.get(room);
  if (queued?.length) {
    rs.messages.push(...queued);
    state.pending.delete(room);
  }
  // Whatever the room is already listening to, from the same second the join
  // was answered: the player is put straight to that point rather than starting
  // the track over for the person who just walked in.
  rs.jukebox = result.jukebox?.open ? { ...result.jukebox, receivedAt: Date.now() } : null;

  /*
   * `activate` is false for the rooms restored behind the one being read. A
   * join that switched the view would mean a reader watching the app hop
   * through every room they had open before settling on the right one, which is
   * both distracting and, if they started typing, a lost message.
   */
  if (activate) setActiveRoom(room);
  else rememberRooms();
  renderRoomList();

  if (result.producers?.length && state.info?.media?.enabled !== false && result.room.mediaEnabled !== false) {
    ensureMedia(room)
      .consumeExisting(result.producers)
      .catch((err) => notice(errorText(err), 'error', room));
  }
  // Skipped for a background join so a burst of them costs one refresh at the
  // end rather than one apiece; restoreRooms() asks once they are all in.
  if (refreshList) refreshRoomList();
}

/** Re-reads the room list, for the member counts beside each name. */
function refreshRoomList() {
  return state.conn
    .request('room.list')
    .then(({ rooms }) => {
      state.roomList = rooms;
      renderRoomList();
    })
    .catch(() => {});
}

async function leaveRoom(name) {
  const rs = state.rooms.get(name);
  try {
    rs?.media?.close();
    await state.conn.request('room.leave', { room: name });
    // We no longer receive presence for it, so take a fresh head count.
    state.conn
      .request('room.list')
      .then(({ rooms }) => {
        state.roomList = rooms;
        renderRoomList();
      })
      .catch(() => {});
  } catch {
    /* ignore */
  }
  state.rooms.delete(name);
  state.pending.delete(name);
  if (state.activeRoom === name) {
    const next = state.rooms.keys().next();
    state.activeRoom = next.done ? null : next.value;
  }
  rememberRooms();
  renderRoomList();
  renderAll();
}

/**
 * Shows or hides the "a new version is ready" strip.
 *
 * Dismissal is remembered against the build it was dismissed for, not as a flag:
 * saying "not now" to Tuesday's update should not silence Friday's. A build
 * announced after one was dismissed shows the banner again.
 */
function renderUpdateBanner() {
  const banner = $('update-banner');
  if (!banner) return;
  const pending = state.serverBuild && CLIENT_BUILD && state.serverBuild !== CLIENT_BUILD;
  banner.hidden = !pending || state.updateDismissed === state.serverBuild;
}

/**
 * Records which client the server is serving now.
 *
 * Arrives twice over: once in the handshake, for a tab opened before the update,
 * and again as an event whenever the files change under a server nobody had to
 * restart. Both go through here, so there is one place that decides what to do
 * about it.
 */
function noteServerBuild(build) {
  if (!build || build === state.serverBuild) return;
  state.serverBuild = build;
  renderUpdateBanner();
}

function setActiveRoom(room) {
  state.activeRoom = room;
  // Opening a channel closes whatever conversation was on screen: the two are
  // alternatives, not layers, and the transcript can only show one of them.
  state.activeDm = null;
  // Here rather than only on join, so a refresh comes back to the room that was
  // being read and not merely to the one that was opened last.
  rememberRooms();
  clearUnread(room);
  // On a phone the drawer covers the chat, so close it once a room is chosen.
  if (state.sidebarOpen) setSidebar(false);
  renderAll();
}

/**
 * Opens the conversation with `nickname`, creating it if this is the first time.
 *
 * The channel is left rather than backgrounded - `activeRoom` goes to null -
 * which is what puts the cameras, the jukebox and the room menu away without any
 * of them having to know that conversations exist. Nothing is *left*: the rooms
 * stay joined and their media keeps running, exactly as when switching between
 * two channels.
 */
function setActiveDm(nickname) {
  const dm = dmState(nickname);
  if (!dm) return;
  state.activeDm = dmKey(nickname);
  if (state.activeRoom) state.lastRoom = state.activeRoom;
  state.activeRoom = null;
  clearDmUnread(state.activeDm);
  if (state.sidebarOpen) setSidebar(false);
  renderAll();
  $('input-message').focus();
}

/**
 * Closes a conversation and forgets what was said in it.
 *
 * The messages go with it. They live nowhere else - the server relays private
 * messages and does not keep them - so this is the only copy, and a "close" that
 * quietly kept it would be the wrong promise for a control marked ×.
 */
function closeDm(key) {
  if (!state.dms.delete(key)) return;
  if (state.activeDm === key) {
    state.activeDm = null;
    // Back where it was opened from, if that room is still joined; otherwise any
    // room at all, rather than an empty screen.
    const next = state.rooms.keys().next();
    state.activeRoom = state.rooms.has(state.lastRoom)
      ? state.lastRoom
      : next.done
        ? null
        : next.value;
  }
  refreshUnreadTitle();
  renderAll();
}

function renderAll() {
  renderRoomList();
  renderDmList();
  renderRoomBar();
  renderMembers();
  renderMessages();
  renderVideoGrid();
  renderJukebox();
  renderMediaButtons();
  renderIdentity();
  renderConnection();
}

// ------------------------------------------------------------ member actions

/**
 * Capabilities that apply in a room. An operator scoped to a single room holds
 * their powers only inside it, so the room-specific set is used where available
 * and the global set is the fallback.
 */
function capsFor(room = state.activeRoom) {
  return (room && state.session?.roomCapabilities?.[room]) || state.session?.capabilities || {};
}

/**
 * Redraws everything a change to the hide list touches.
 *
 * The transcript, because that is the point; the member list, because it marks
 * who is hidden; and the room list, because a background room's unread count is
 * kept without the hidden lines and has to be recounted once they come back.
 */
function applyHiddenChange() {
  recountUnread();
  renderAll();
}

/**
 * Recomputes each background room's unread badge from what is actually visible.
 *
 * The badge is incremented as messages arrive, so it is only ever as right as
 * the hide list was at the time. Hiding somebody mid-conversation would
 * otherwise leave a room advertising six unread messages that are no longer
 * there to read.
 */
function recountUnread() {
  for (const [room, rs] of state.rooms) {
    if (room === state.activeRoom || !rs.unread) continue;
    const visible = rs.messages.filter(
      (entry) => !entry.history && !fromHiddenUser(entry) && (entry.kind === 'chat' || entry.kind === 'me'),
    ).length;
    rs.unread = Math.min(rs.unread, visible);
  }
  // Conversations for the same reason, and hiding somebody bears on them more
  // directly than on a room: hiding the only person in a conversation empties it.
  for (const [key, dm] of state.dms) {
    if (key === state.activeDm || !dm.unread) continue;
    const visible = dm.messages.filter(
      (entry) => !fromHiddenUser(entry) && entry.kind === 'private' && !entry.outgoing,
    ).length;
    dm.unread = Math.min(dm.unread, visible);
  }
  refreshUnreadTitle();
}

/**
 * The hide list, and the way off it.
 *
 * Reached from the room menu rather than from Settings because that is where
 * somebody goes looking for it: hiding happens in a room, from the member list,
 * and the question "who have I hidden?" arrives in the same place. The list
 * itself is not per room - it is this browser's, everywhere - which the hint
 * says out loud so nobody has to infer it.
 */
function openHiddenDialog() {
  const body = el('div', {});
  let closeModal;

  const draw = () => {
    const people = hiddenUsers();
    body.replaceChildren(
      el('p', {
        class: 'hint',
        text: people.length === 0 ? t('ui.hidden.empty') : t('ui.hidden.summary', { count: people.length }),
      }),
      ...(people.length > 0
        ? [
            el(
              'div',
              { class: 'hidden-list' },
              ...people.map((person) =>
                el(
                  'div',
                  { class: 'hidden-row' },
                  el('span', { class: 'hidden-name', text: person.nickname }),
                  el('button', {
                    type: 'button',
                    text: t('ui.hidden.unhide'),
                    'aria-label': t('ui.member.unhide', { nickname: person.nickname }),
                    onclick: () => {
                      unhideUser(person.nickname);
                      applyHiddenChange();
                      draw();
                    },
                  }),
                ),
              ),
            ),
            el('button', {
              type: 'button',
              class: 'danger',
              text: t('ui.hidden.clear'),
              onclick: () => {
                clearHidden();
                applyHiddenChange();
                draw();
              },
            }),
          ]
        : []),
    );
  };

  draw();
  closeModal = openModal({ title: t('ui.hidden.title'), body, hideConfirm: true });
  return closeModal;
}

function openMemberActions(member) {
  const room = state.activeRoom;
  const caps = capsFor(room);
  const isSelf = member.nickname === state.session?.nickname;
  const canModerate =
    !isSelf && member.role !== 'root' && (caps['user.kick'] || caps['user.ban'] || caps['user.forcemute']);
  const canOp = caps['operator.grant'] && !isSelf && member.role !== 'root' && !member.guest;

  const body = el(
    'div',
    {},
    el('p', {
      class: 'hint',
      text: t('ui.member.summary', {
        nickname: member.nickname,
        kind: member.guest ? t('ui.member.guest') : t('ui.member.registered'),
        role:
          member.role === 'root'
            ? t('ui.member.roleRoot')
            : member.role === 'operator'
              ? t('ui.member.roleOperator')
              : '',
      }),
    }),
  );

  const actions = el('div', { class: 'member-actions' });

  // No point offering to message yourself; the server rejects it.
  if (!isSelf) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.member.privateMessage'),
        onclick: () => {
          closeModal();
          // Opens the conversation rather than priming the box with a command:
          // the command still works, but it is no longer the way in.
          setActiveDm(member.nickname);
        },
      }),
    );

    /*
     * Hiding somebody. Offered to everyone, including guests, and needing no
     * capability: this changes nothing but what this browser draws. It is not
     * moderation and is not grouped with it - it sits up here with the private
     * message, among the things anybody may do about another person.
     */
    const hidden = isUserHidden(member.nickname);
    actions.append(
      el('button', {
        type: 'button',
        text: hidden
          ? t('ui.member.unhide', { nickname: member.nickname })
          : t('ui.member.hide', { nickname: member.nickname }),
        onclick: () => {
          closeModal();
          const now = toggleUser(member.nickname);
          applyHiddenChange();
          notice(
            now.hidden
              ? t('notice.userHidden', { nickname: member.nickname })
              : t('notice.userUnhidden', { nickname: member.nickname }),
            'info',
            room,
          );
        },
      }),
    );
  }

  if (caps['ban.list']) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.member.whois'),
        onclick: async () => {
          closeModal();
          await runCommand(`/whois ${member.nickname}`);
        },
      }),
    );
  }

  if (canModerate && caps['user.forcemute']) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.member.stopMic'),
        onclick: async () => {
          closeModal();
          await runCommand(`/mute ${member.nickname}`);
        },
      }),
      el('button', {
        type: 'button',
        text: t('ui.member.stopVideo'),
        onclick: async () => {
          closeModal();
          await runCommand(`/stopvideo ${member.nickname}`);
        },
      }),
    );
  }

  if (canModerate && caps['user.kick']) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.member.kick'),
        onclick: () => {
          closeModal();
          openKickDialog(member, room);
        },
      }),
    );
  }

  /*
   * Ban stops short of a fellow operator where kick and the two stop buttons
   * above do not - the server allows interrupting a peer but not locking them
   * out, see canActOn in src/core/permissions.js. Offering it anyway would put a
   * button next to three working ones that fails every time it is pressed, and
   * looks for all the world like the same kind of thing.
   */
  const canBanTarget = member.role !== 'operator' || state.session?.isRoot === true;
  if (canModerate && caps['user.ban'] && canBanTarget) {
    actions.append(
      el('button', {
        type: 'button',
        class: 'danger',
        text: t('ui.member.ban'),
        onclick: () => {
          closeModal();
          openBanDialog(member, room);
        },
      }),
    );
  }

  if (canOp) {
    const isOperator = member.role === 'operator';
    actions.append(
      el('button', {
        type: 'button',
        text: isOperator ? t('ui.member.removeOperator') : t('ui.member.makeOperator'),
        onclick: () => {
          closeModal();
          openOperatorDialog(member, isOperator);
        },
      }),
    );
  }

  body.append(actions);
  const closeModal = openModal({ title: member.nickname, body, hideConfirm: true });
}

function openKickDialog(member, room) {
  const reason = el('input', { placeholder: t('ui.kick.reasonPlaceholder') });
  const scope = el(
    'select',
    {},
    el('option', { value: 'room', text: t('ui.kick.fromRoom', { room }) }),
    state.session?.capabilities?.['user.kick'] && (state.session?.globalOperator || state.session?.isRoot)
      ? el('option', { value: 'global', text: t('ui.kick.fromServer') })
      : null,
  );
  const body = el(
    'div',
    {},
    el('label', { text: t('ui.kick.scope') }),
    scope,
    el('label', { text: t('ui.kick.reason') }),
    reason,
    el('p', { class: 'hint', text: t('ui.kick.hint') }),
  );

  openModal({
    title: t('ui.kick.title', { nickname: member.nickname }),
    body,
    confirmText: t('ui.kick.confirm'),
    onConfirm: async () => {
      // Structured request, so failures surface in the dialog.
      const result = await state.conn.request('mod.kick', {
        nickname: member.nickname,
        scope: scope.value === 'global' ? 'global' : room,
        reason: reason.value.trim() || null,
      });
      notice(
        t('notice.kicked', {
          nickname: result.kicked,
          scope:
            scope.value === 'global'
              ? t('notice.kickedFromServer')
              : t('notice.kickedFromRoom', { room }),
          messages: result.messagesRemoved
            ? t('notice.messagesAlsoRemoved', { count: result.messagesRemoved })
            : '',
        }),
        'moderation',
      );
    },
  });
}

/**
 * Mirrors the server's duration grammar so a typo is caught in the dialog.
 * Returns `{ ok, message }`; an empty value means an indefinite ban.
 */
function validateDuration(value) {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return { ok: true };
  if (/^(perm|perma|permanent|forever|inf|infinite|indefinite|never)$/.test(raw)) return { ok: true };
  if (/^\d+$/.test(raw)) return { ok: true };
  // One or more <number><unit> pairs, and nothing else.
  const pattern = /^(\d+(?:\.\d+)?\s*(?:s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days|w|week|weeks|mo|month|months|y|year|years)\s*)+$/;
  if (pattern.test(raw)) return { ok: true };
  return { ok: false, message: t('ui.ban.invalidDuration', { value }) };
}

function openBanDialog(member, room) {
  const duration = el('input', { placeholder: t('ui.ban.durationPlaceholder') });
  const reason = el('input', { placeholder: t('ui.kick.reasonPlaceholder') });
  const scope = el(
    'select',
    {},
    el('option', { value: 'room', text: t('ui.ban.fromRoom', { room }) }),
    state.session?.globalOperator || state.session?.isRoot
      ? el('option', { value: 'global', text: t('ui.ban.fromServer') })
      : null,
  );
  const preview = el('p', { class: 'hint' });
  const updatePreview = () => {
    const check = validateDuration(duration.value);
    preview.textContent = !check.ok
      ? check.message
      : duration.value.trim()
        ? t('ui.ban.timed', { duration: duration.value.trim() })
        : t('ui.ban.permanentWarning');
    preview.style.color = check.ok ? 'var(--muted)' : 'var(--danger)';
  };
  duration.addEventListener('input', updatePreview);

  const body = el(
    'div',
    {},
    el('label', { text: t('ui.kick.scope') }),
    scope,
    el('label', { text: t('ui.ban.duration') }),
    duration,
    preview,
    el('label', { text: t('ui.kick.reason') }),
    reason,
  );
  updatePreview();

  openModal({
    title: t('ui.ban.title', { nickname: member.nickname }),
    body,
    confirmText: t('ui.ban.confirm'),
    onConfirm: async () => {
      // Validate here: a malformed duration must never silently become a
      // permanent ban, which is what happened when this went through /ban.
      const check = validateDuration(duration.value);
      if (!check.ok) throw new Error(check.message);

      const result = await state.conn.request('mod.ban', {
        nickname: member.nickname,
        scope: scope.value === 'global' ? 'global' : room,
        duration: duration.value.trim() || null,
        reason: reason.value.trim() || null,
      });
      notice(result.summary, 'moderation');
    },
  });
}

function openOperatorDialog(member, isOperator) {
  const scope = el('input', { placeholder: t('ui.operator.scopePlaceholder'), value: '' });
  const body = el(
    'div',
    {},
    el('p', {
      class: 'hint',
      text: isOperator
        ? t('ui.operator.revokeHint', { nickname: member.nickname })
        : t('ui.operator.grantHint', { nickname: member.nickname }),
    }),
    el('label', { text: t('ui.operator.scope') }),
    scope,
  );

  openModal({
    title: isOperator
      ? t('ui.operator.revokeTitle', { nickname: member.nickname })
      : t('ui.operator.grantTitle', { nickname: member.nickname }),
    body,
    confirmText: isOperator ? t('ui.operator.revoke') : t('ui.operator.grant'),
    onConfirm: async () => {
      const room = scope.value.trim().replace(/^#+/, '');
      const result = await state.conn.request('admin.operator', {
        nickname: member.nickname,
        scope: room || 'global',
        grant: !isOperator,
      });
      const scopeText =
        result.scope === 'global'
          ? t('notice.scopeServerWide')
          : t('notice.scopeRoom', { room: result.scope });
      notice(
        result.operator
          ? t('notice.operatorGranted', { nickname: result.nickname, scope: scopeText })
          : t('notice.operatorRevoked', { nickname: result.nickname, scope: scopeText }),
        'moderation',
      );
    },
  });
}

// ------------------------------------------------------------ room actions

/**
 * Actions for the room you are looking at: topic, leaving, and removing it.
 * Removing is capability-gated and asks for confirmation, since it disconnects
 * everyone still inside and cannot be undone.
 */
/**
 * The menu behind the topbar's ⋯ button.
 *
 * A list of choices, like openRoomActions below: each one either opens its own
 * dialog or does its thing. Built as a modal so it inherits the focus trap and
 * the Escape handling rather than introducing a second kind of popup.
 *
 * The Ko-fi choice is a plain link, which is all Ko-fi's own widget amounts to:
 * its script offers `draw()`, which writes the link with `document.writeln` and
 * so only works while the page is still parsing - called from a menu it would
 * blank the document - and the markup it writes pulls in a Google font and an
 * image from two more origins for a button this app can draw itself. Same
 * address, same colour, same words, nothing fetched.
 */
const KOFI_URL = 'https://ko-fi.com/E5B7254AGA';

function openUserMenu() {
  const actions = el('div', { class: 'member-actions stacked' });
  let closeModal;

  actions.append(
    // Guests have no account to settle, and nothing behind this would work for
    // them: both halves of it are changes to a registered account.
    state.session && !state.session.guest
      ? el('button', {
          type: 'button',
          text: t('ui.account'),
          onclick: () => {
            closeModal();
            openAccountDialog();
          },
        })
      : null,
    el('button', {
      type: 'button',
      text: t('ui.theme'),
      onclick: () => {
        closeModal();
        openAppearanceDialog();
      },
    }),
    el(
      'a',
      {
        class: 'support',
        href: KOFI_URL,
        target: '_blank',
        rel: 'noopener noreferrer',
        text: `☕ ${t('ui.support')}`,
      },
    ),
    el('button', {
      type: 'button',
      class: 'danger',
      text: t('ui.signOut'),
      onclick: () => {
        closeModal();
        signOut();
      },
    }),
  );

  closeModal = openModal({
    title: t('ui.userMenu'),
    body: el('div', {}, actions),
    hideConfirm: true,
  });
}

/**
 * Appearance: follow the operating system, or pin light or dark, and whether to
 * run the fluid behind the transcript.
 *
 * The choice is applied as it is made, so it can be seen happening; the dialog's
 * button only closes it, which is why there is no Cancel to imply otherwise.
 */
function openAppearanceDialog() {
  const body = el('div', {});
  const select = el(
    'select',
    // The dialog title already reads "Appearance", so a visible label would only
    // repeat it; screen readers still get the name.
    { id: 'pref-theme', 'aria-label': t('ui.theme') },
    ...THEMES.map((value) =>
      el('option', {
        value,
        text: t(`ui.theme.${value}`),
        ...(getTheme() === value ? { selected: true } : {}),
      }),
    ),
  );
  const hint = el('p', { class: 'hint', text: themeHintText() });
  select.addEventListener('change', (event) => {
    setTheme(event.target.value);
    hint.textContent = themeHintText();
  });

  body.append(select, hint);

  // Offered only where it could actually run: a browser without WebGL2, or
  // somebody asking for reduced motion, would get a switch that does nothing.
  if (fluidSupported()) {
    const fluid = el('input', {
      type: 'checkbox',
      id: 'pref-fluid',
      ...(fluidEnabled() ? { checked: true } : {}),
    });
    fluid.addEventListener('change', (event) => setFluidEnabled(event.target.checked));
    body.append(
      el('label', { class: 'checkbox', for: 'pref-fluid' }, fluid, ` ${t('ui.theme.fluid')}`),
      el('p', { class: 'hint', text: t('ui.theme.fluidHint') }),
    );
  }

  openModal({
    title: t('ui.theme'),
    body,
    confirmText: t('ui.done'),
    hideCancel: true,
  });
}

/**
 * Replaces the stored session token, if there is one to replace.
 *
 * Changing a password or a nickname makes the old token stop resolving - it is
 * stamped with both - so a client holding one has to be handed the new one or
 * its next reconnection signs it out. Someone who did not ask to be kept signed
 * in has none stored, and must not quietly acquire one here.
 */
function refreshStoredToken(token) {
  if (!token) return;
  try {
    if (localStorage.getItem(TOKEN_KEY)) localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage may be unavailable; the live session is unaffected either way */
  }
}

/**
 * Account settings: the two things a registered person owns and may change.
 *
 * Two forms rather than one, each applying on its own, because they fail for
 * different reasons and succeed independently - a rejected nickname should not
 * discard a perfectly good new password typed beside it. So the dialog itself
 * has no confirm button; each half has its own, and reports into its own line.
 *
 * Both ask for the current password. The nickname is not a display name here:
 * it is half the credentials, and giving it up frees it for anyone else to
 * take, so it is guarded like the password it sits beside.
 */
function openAccountDialog() {
  const body = el('div', { class: 'account-form' });

  // ---- nickname
  const nickInput = el('input', {
    id: 'account-nick',
    value: state.session?.nickname ?? '',
    maxlength: '96',
    autocomplete: 'username',
  });
  const nickPassword = el('input', { type: 'password', autocomplete: 'current-password' });
  const nickStatus = el('p', { class: 'status', role: 'status' });
  const nickButton = el('button', { type: 'button', class: 'primary', text: t('ui.account.changeNickname') });

  nickButton.addEventListener('click', async () => {
    nickStatus.textContent = '';
    nickStatus.classList.remove('error');
    nickButton.disabled = true;
    try {
      const result = await state.conn.request('auth.changeNickname', {
        nickname: nickInput.value.trim(),
        currentPassword: nickPassword.value,
      });
      refreshStoredToken(result.token);
      nickPassword.value = '';
      nickStatus.textContent = t('ui.account.nicknameChanged', { nickname: result.nickname });
    } catch (err) {
      nickStatus.textContent = errorText(err);
      nickStatus.classList.add('error');
    } finally {
      nickButton.disabled = false;
    }
  });

  // ---- password
  const currentPassword = el('input', { type: 'password', autocomplete: 'current-password' });
  const newPassword = el('input', { type: 'password', autocomplete: 'new-password' });
  const repeatPassword = el('input', { type: 'password', autocomplete: 'new-password' });
  const passStatus = el('p', { class: 'status', role: 'status' });
  const passButton = el('button', { type: 'button', class: 'primary', text: t('ui.account.changePassword') });

  passButton.addEventListener('click', async () => {
    passStatus.textContent = '';
    passStatus.classList.remove('error');
    if (newPassword.value !== repeatPassword.value) {
      passStatus.textContent = t('ui.gate.passwordMismatch');
      passStatus.classList.add('error');
      return;
    }
    passButton.disabled = true;
    try {
      // The token is stamped with the password, so the old one stops resolving
      // the moment this succeeds - without the new one, this session's next
      // reconnection would be signed out.
      const result = await state.conn.request('auth.changePassword', {
        currentPassword: currentPassword.value,
        newPassword: newPassword.value,
      });
      refreshStoredToken(result.token);
      currentPassword.value = '';
      newPassword.value = '';
      repeatPassword.value = '';
      passStatus.textContent = t('ui.account.passwordChanged');
    } catch (err) {
      passStatus.textContent = errorText(err);
      passStatus.classList.add('error');
    } finally {
      passButton.disabled = false;
    }
  });

  body.append(
    el('h3', { text: t('ui.account.nickname') }),
    el('p', { class: 'hint', text: t('ui.account.nicknameHint') }),
    el('label', { for: 'account-nick', text: t('ui.field.nickname') }),
    nickInput,
    el('label', { text: t('ui.account.currentPassword') }),
    nickPassword,
    nickButton,
    nickStatus,
    el('hr', { class: 'account-rule' }),
    el('h3', { text: t('ui.account.password') }),
    el('label', { text: t('ui.account.currentPassword') }),
    currentPassword,
    el('label', { text: t('ui.account.newPassword') }),
    newPassword,
    el('label', { text: t('ui.field.repeatPassword') }),
    repeatPassword,
    passButton,
    passStatus,
  );

  openModal({ title: t('ui.account.title'), body, confirmText: t('ui.done'), hideCancel: true });
}

/** Spells out what "follow system" currently resolves to. */
function themeHintText() {
  if (getTheme() !== 'system') return '';
  return t('ui.theme.systemHint', { theme: t(`ui.theme.${effectiveTheme()}`).toLowerCase() });
}

function openRoomActions() {
  const room = state.activeRoom;
  if (!room) return;
  const rs = roomState(room);
  const caps = capsFor(room);
  const canDelete = caps['room.delete'] && !rs.info.persistent;
  const canTopic = caps['room.topic'];

  const body = el(
    'div',
    {},
    el('p', {
      class: 'hint',
      text:
        `${t('ui.room.summary', { room, count: rs.presence.length })} ` +
        `${rs.info.topic ? t('ui.room.topicIs', { topic: rs.info.topic }) : t('ui.room.noTopicSet')}` +
        `${rs.info.hidden ? ` ${t('ui.room.isHidden')}` : ''}` +
        `${rs.info.persistent ? ` ${t('ui.room.isPersistent')}` : ''}`,
    }),
  );

  const actions = el('div', { class: 'member-actions' });
  let closeModal;

  if (canTopic) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.room.setTopic'),
        onclick: () => {
          closeModal();
          openTopicDialog(room, rs.info.topic ?? '');
        },
      }),
    );
  }

  if (caps['room.topic']) {
    const isHidden = rs.info.hidden === true;
    actions.append(
      el('button', {
        type: 'button',
        text: isHidden ? t('ui.room.show') : t('ui.room.hide'),
        onclick: async () => {
          closeModal();
          try {
            const result = await state.conn.request('admin.room', { room, hidden: !isHidden });
            rs.info.hidden = result.hidden;
            renderRoomBar();
            renderRoomList();
            notice(
              result.hidden ? t('notice.roomNowHidden', { room }) : t('notice.roomNowListed', { room }),
              'moderation',
              room,
            );
          } catch (err) {
            notice(errorText(err), 'error', room);
          }
        },
      }),
    );
  }

  /*
   * The room's shared player. Starting one is a signed-in user's call - see
   * jukebox.control in permissions.js - and the only thing this menu carries is
   * that switch; the panel itself carries the rest once it is running.
   */
  if (caps['jukebox.control'] && jukeboxAvailable()) {
    const running = jukeboxOf(room) !== null;
    actions.append(
      el('button', {
        type: 'button',
        text: running ? t('ui.jukebox.stop') : t('ui.jukebox.start'),
        onclick: async () => {
          closeModal();
          try {
            await state.conn.request(running ? 'jukebox.close' : 'jukebox.open', { room });
          } catch (err) {
            notice(errorText(err), 'error', room);
          }
        },
      }),
    );
  }

  /*
   * The channel's own numbers, open to anybody in it. Not capability-gated:
   * these are the room's statistics rather than an operator's tool, and the
   * server checks membership when the report is asked for.
   */
  if (statsAvailable()) {
    actions.append(
      el('button', {
        type: 'button',
        text: t('ui.stats.open'),
        onclick: () => {
          closeModal();
          // Fresh numbers each time the panel is opened; the tab buttons then
          // work from that one report rather than asking again per tab.
          showStats('activity', { refresh: true });
        },
      }),
    );
  }

  /*
   * The hide list. Not room-specific and not gated: like the statistics above
   * it, this is something anybody in the room may open, and it is here because
   * this is the menu nearest to the member list the hiding was done from.
   */
  actions.append(
    el('button', {
      type: 'button',
      text: t('ui.hidden.manage'),
      onclick: () => {
        closeModal();
        openHiddenDialog();
      },
    }),
  );

  actions.append(
    el('button', {
      type: 'button',
      text: t('ui.room.leave'),
      onclick: async () => {
        closeModal();
        await leaveRoom(room);
      },
    }),
  );

  if (canDelete) {
    actions.append(
      el('button', {
        type: 'button',
        class: 'danger',
        text: t('ui.room.remove'),
        onclick: () => {
          closeModal();
          openDeleteRoomDialog(room, rs.presence.length);
        },
      }),
    );
  } else if (!rs.info.persistent) {
    actions.append(el('p', { class: 'hint', text: t('ui.room.onlyOperatorsRemove') }));
  }

  body.append(actions);
  closeModal = openModal({ title: `#${room}`, body, hideConfirm: true });
}

function openTopicDialog(room, current) {
  const input = el('input', { value: current, maxlength: 500, placeholder: t('ui.room.topicPlaceholder') });
  openModal({
    title: t('ui.room.topicTitle', { room }),
    body: el(
      'div',
      {},
      el('label', { text: t('ui.room.topicLabel') }),
      input,
      el('p', { class: 'hint', text: t('ui.room.topicClearHint') }),
    ),
    confirmText: t('ui.save'),
    onConfirm: async () => {
      await state.conn.request('room.topic', { room, topic: input.value.trim() || null });
    },
  });
}

function openDeleteRoomDialog(room, memberCount) {
  const confirmField = el('input', { placeholder: room, autocomplete: 'off' });
  const body = el(
    'div',
    {},
    el('p', {
      class: 'hint',
      text: t('ui.room.removeWarning', { room, count: memberCount }),
    }),
    el('label', { text: t('ui.room.removeConfirmLabel') }),
    confirmField,
  );

  openModal({
    title: t('ui.room.removeTitle', { room }),
    body,
    confirmText: t('ui.room.removeConfirmButton'),
    onConfirm: async () => {
      if (confirmField.value.trim().toLowerCase() !== room) {
        throw new Error(t('ui.room.removeMismatch', { room }));
      }
      const result = await state.conn.request('admin.room', { room, delete: true });
      notice(
        t('notice.roomRemovedResult', {
          room: result.room,
          members: result.removed ? t('notice.roomRemovedMembers', { count: result.removed }) : '',
        }),
        'moderation',
      );
    },
  });
}

// -------------------------------------------------------------- admin panel

const adminViews = {
  async operators(host) {
    const { operators } = await state.conn.request('admin.operators', {});
    const canGrant = state.session?.capabilities?.['operator.grant'];

    const nickname = el('input', { placeholder: t('ui.field.nickname') });
    const scope = el('input', { placeholder: t('ui.operator.scopePlaceholder') });
    const form = canGrant
      ? el(
          'div',
          { class: 'row-form' },
          el('div', {}, el('label', { text: t('ui.field.nickname') }), nickname),
          el('div', {}, el('label', { text: t('ui.admin.scope') }), scope),
          el('button', {
            type: 'button',
            class: 'primary',
            text: t('ui.admin.grantOperator'),
            onclick: async () => {
              try {
                await state.conn.request('admin.operator', {
                  nickname: nickname.value.trim(),
                  scope: scope.value.trim().replace(/^#+/, '') || 'global',
                  grant: true,
                });
                notice(
                  t('notice.operatorGranted', {
                    nickname: nickname.value.trim(),
                    scope: scope.value.trim()
                      ? t('notice.scopeRoom', { room: scope.value.trim().replace(/^#+/, '') })
                      : t('notice.scopeServerWide'),
                  }),
                  'moderation',
                );
                showAdmin('operators');
              } catch (err) {
                notice(errorText(err), 'error');
              }
            },
          }),
        )
      : el('p', { class: 'hint', text: t('ui.admin.rootOnlyOperators') });

    const rows = operators.map((op) =>
      el(
        'tr',
        {},
        el('td', {}, el('span', { class: `badge ${op.root ? 'root' : 'op'}`, text: op.root ? 'root' : 'op' }), ' ', op.nickname),
        el('td', {
          text: op.root
            ? t('ui.admin.serverWideRoot')
            : op.global
              ? t('ui.admin.serverWide')
              : op.rooms.map((r) => `#${r}`).join(', '),
        }),
        el('td', {
          text: op.online === null ? t('ui.admin.hidden') : op.online ? t('ui.admin.online') : t('ui.admin.offline'),
        }),
        el(
          'td',
          { class: 'actions' },
          !op.root && canGrant
            ? el('button', {
                type: 'button',
                class: 'danger',
                text: t('ui.admin.revoke'),
                onclick: async () => {
                  try {
                    if (op.global) {
                      await state.conn.request('admin.operator', { nickname: op.nickname, scope: 'global', grant: false });
                    }
                    for (const room of op.rooms) {
                      await state.conn.request('admin.operator', { nickname: op.nickname, scope: room, grant: false });
                    }
                    notice(
                      t('notice.operatorRevoked', {
                        nickname: op.nickname,
                        scope: t('notice.scopeServerWide'),
                      }),
                      'moderation',
                    );
                    showAdmin('operators');
                  } catch (err) {
                    notice(errorText(err), 'error');
                  }
                },
              })
            : null,
        ),
      ),
    );

    host.replaceChildren(
      form,
      el(
        'table',
        {},
        el(
          'thead',
          {},
          el(
            'tr',
            {},
            el('th', { text: t('ui.admin.who') }),
            el('th', { text: t('ui.admin.scope') }),
            el('th', { text: t('ui.admin.status') }),
            el('th', {}),
          ),
        ),
        el('tbody', {}, ...rows),
      ),
    );
  },

  /**
   * Every registered account, and root's one destructive control over them.
   *
   * Root and root's own account carry no Remove button: the server refuses both,
   * and offering a button that always fails is worse than not offering one.
   */
  async users(host) {
    const { users } = await state.conn.request('admin.users', {});

    const rows = users.map((account) => {
      const removable = !account.root && account.userId !== state.session?.userId;
      const scope = account.root
        ? t('ui.admin.serverWideRoot')
        : account.globalOperator
          ? t('ui.admin.serverWide')
          : account.operatorRooms.length > 0
            ? account.operatorRooms.map((room) => `#${room}`).join(', ')
            : '—';

      return el(
        'tr',
        {},
        el(
          'td',
          {},
          el('span', {
            class: `badge ${account.root ? 'root' : account.globalOperator || account.operatorRooms.length ? 'op' : 'user'}`,
            text: account.root ? 'root' : account.globalOperator || account.operatorRooms.length ? 'op' : 'user',
          }),
          ' ',
          account.nickname,
        ),
        el('td', { text: scope }),
        el(
          'td',
          {},
          account.sessions > 0
            ? t('ui.admin.userOnline', { count: account.sessions })
            : t('ui.admin.offline'),
          // Disabled sits with the status rather than in a column of its own:
          // it is the same question - can this account be here - and a column
          // that is blank for almost every row is a column of nothing.
          account.disabled ? el('span', { class: 'badge', text: t('ui.admin.userDisabled') }) : null,
        ),
        el('td', { text: stamp(account.createdAt) }),
        /*
         * "Last signed in" is only stamped by an actual sign-in, so an account
         * that registered and never came back reads as never - which is true, and
         * is why the creation date sits beside it. The two together are what root
         * is actually asking when it wonders whether an account is still in use.
         */
        el('td', { text: account.lastLoginAt ? stamp(account.lastLoginAt) : t('ui.admin.userNeverSignedIn') }),
        el(
          'td',
          { class: 'actions' },
          removable
            ? el('button', {
                type: 'button',
                class: 'danger',
                text: t('ui.admin.userRemove'),
                onclick: () => openRemoveUserDialog(account),
              })
            : null,
        ),
      );
    });

    host.replaceChildren(
      el('p', { class: 'hint', text: t('ui.admin.usersHint') }),
      el(
        'table',
        {},
        el(
          'thead',
          {},
          el(
            'tr',
            {},
            ...[
              t('ui.admin.who'),
              t('ui.admin.scope'),
              t('ui.admin.status'),
              t('ui.admin.userCreated'),
              t('ui.admin.userLastSignIn'),
              '',
            ].map((heading) => el('th', { text: heading })),
          ),
        ),
        el('tbody', {}, ...rows),
      ),
    );
  },

  async bans(host) {
    const { bans } = await state.conn.request('mod.bans', { scope: 'all' });
    if (bans.length === 0) {
      host.replaceChildren(el('p', { class: 'hint', text: t('ui.admin.noBans') }));
      return;
    }
    const rows = bans.map((ban) =>
      el(
        'tr',
        {},
        el('td', { text: `#${ban.id}` }),
        el('td', { text: ban.subjectType }),
        el('td', { text: String(ban.subject) }),
        el('td', { text: ban.scope }),
        el('td', { text: ban.permanent ? t('ui.admin.banIndefinite') : stamp(ban.expiresAt) }),
        el('td', { text: ban.by ?? '' }),
        el('td', { text: ban.reason ?? '' }),
        el(
          'td',
          { class: 'actions' },
          el('button', {
            type: 'button',
            text: t('ui.admin.lift'),
            onclick: async () => {
              try {
                await state.conn.request('mod.unban', { banId: ban.id, scope: ban.scope });
                notice(t('notice.banLifted', { id: ban.id }), 'moderation');
                showAdmin('bans');
              } catch (err) {
                notice(errorText(err), 'error');
              }
            },
          }),
        ),
      ),
    );
    host.replaceChildren(
      el(
        'table',
        {},
        el(
          'thead',
          {},
          el(
            'tr',
            {},
            ...[
              t('ui.admin.banId'),
              t('ui.admin.banType'),
              t('ui.admin.banSubject'),
              t('ui.admin.scope'),
              t('ui.admin.banExpires'),
              t('ui.admin.banBy'),
              t('ui.admin.banReason'),
              '',
            ].map((h) => el('th', { text: h })),
          ),
        ),
        el('tbody', {}, ...rows),
      ),
    );
  },

  async audit(host) {
    const { entries } = await state.conn.request('admin.audit', { limit: 100 });
    const text = entries
      .map(
        (e) =>
          `${new Date(e.at).toISOString().slice(0, 19).replace('T', ' ')}  ${(e.actor ?? 'system').padEnd(16)} ` +
          `${e.action.padEnd(22)} ${(e.target ?? '').padEnd(16)} ${e.scope ?? ''} ${e.detail ?? ''}`,
      )
      .join('\n');
    host.replaceChildren(el('pre', { text: text || t('ui.admin.auditEmpty') }));
  },

  async announce(host) {
    const text = el('textarea', { rows: 4, maxlength: 500, placeholder: t('ui.admin.announcePlaceholder') });
    const target = el('input', { placeholder: t('ui.admin.announceTargetPlaceholder') });
    host.replaceChildren(
      el('label', { text: t('ui.admin.announcement') }),
      text,
      el('label', { text: t('ui.admin.announceTarget') }),
      target,
      el('div', { class: 'modal-actions' }, el('button', {
        type: 'button',
        class: 'primary',
        text: t('ui.admin.sendAnnouncement'),
        onclick: async () => {
          try {
            const room = target.value.trim().replace(/^#+/, '');
            const result = await state.conn.request('admin.announce', {
              text: text.value.trim(),
              room: room || null,
            });
            notice(t('notice.announcementSent', { scope: result.scope, count: result.sent }), 'moderation');
            text.value = '';
          } catch (err) {
            notice(errorText(err), 'error');
          }
        },
      })),
    );
  },
};

/**
 * Confirms removing an account, and says plainly what that does.
 *
 * The nickname has to be typed out, as it does for removing a room: this cannot
 * be undone, and the two lists it is reached from are sorted by name, so a
 * mis-click lands on somebody adjacent to the one intended.
 *
 * The warning names the consequence people are least likely to expect - that the
 * nickname is released and anybody may then claim it - because that is the part
 * a reserved nickname is the answer to, and root can only reach for it if it
 * knows.
 */
function openRemoveUserDialog(account) {
  const confirmField = el('input', { placeholder: account.nickname, autocomplete: 'off' });
  const body = el(
    'div',
    {},
    el('p', {
      class: 'hint',
      text: account.sessions > 0
        ? t('ui.admin.userRemoveWarningOnline', { nickname: account.nickname, count: account.sessions })
        : t('ui.admin.userRemoveWarning', { nickname: account.nickname }),
    }),
    el('p', { class: 'hint', text: t('ui.admin.userRemoveNickname', { nickname: account.nickname }) }),
    el('label', { text: t('ui.admin.userRemoveConfirmLabel') }),
    confirmField,
  );

  openModal({
    title: t('ui.admin.userRemoveTitle', { nickname: account.nickname }),
    body,
    confirmText: t('ui.admin.userRemove'),
    onConfirm: async () => {
      if (confirmField.value.trim().toLowerCase() !== account.nickname.toLowerCase()) {
        throw new Error(t('ui.admin.userRemoveMismatch', { nickname: account.nickname }));
      }
      const result = await state.conn.request('admin.account', {
        nickname: account.nickname,
        delete: true,
      });
      notice(
        t('notice.userRemoved', {
          nickname: result.nickname,
          sessions: result.sessionsKicked,
          messages: result.messagesRemoved,
        }),
        'moderation',
      );
      showAdmin('users');
    },
  });
}

async function showAdmin(tab) {
  $('admin-backdrop').hidden = false;
  for (const button of document.querySelectorAll('[data-atab]')) {
    button.classList.toggle('active', button.dataset.atab === tab);
  }
  const host = $('admin-body');
  host.replaceChildren(el('p', { class: 'hint', text: t('ui.admin.loading') }));
  try {
    await adminViews[tab](host);
  } catch (err) {
    host.replaceChildren(el('p', { class: 'error', text: errorText(err) }));
  }
}

// --------------------------------------------------------- statistics panel

/**
 * The channel statistics panel.
 *
 * The report is fetched once and kept, so switching between the six tabs is
 * instant and does not ask the server to recompute the whole thing each time.
 * It is cached against the room it describes, which is what makes reopening the
 * panel in a *different* room fetch again rather than show the last one's
 * numbers under this one's name.
 */
let statsReport = null;

async function showStats(tab = 'activity', { refresh = false } = {}) {
  const room = state.activeRoom;
  if (!room) return;

  $('stats-backdrop').hidden = false;
  $('stats-title').textContent = t('ui.stats.titleFor', { room });
  for (const button of document.querySelectorAll('[data-stab]')) {
    button.classList.toggle('active', button.dataset.stab === tab);
  }

  const host = $('stats-body');
  const summary = $('stats-summary');

  if (refresh || statsReport?.room !== room) {
    host.replaceChildren(el('p', { class: 'hint', text: t('ui.admin.loading') }));
    summary.textContent = '';
    try {
      /*
       * The clock the chart should read in is this browser's, not the server's.
       * Hours are stored in UTC and shifted as the report is built, so this is
       * the only thing that decides whether the 24-hour chart, the peak hours
       * and the time-of-day leaderboards describe the reader's evening or
       * somebody else's. Sent as minutes east of UTC, which is the opposite sign
       * to what getTimezoneOffset() reports.
       *
       * Read afresh on every request rather than once at load: a laptop opened
       * in another country, or a browser left running across a daylight-saving
       * change, would otherwise keep reporting the offset it started with.
       */
      statsReport = await state.conn.request('stats.report', {
        room,
        utcOffsetMinutes: -new Date().getTimezoneOffset(),
      });
    } catch (err) {
      statsReport = null;
      summary.textContent = '';
      host.replaceChildren(el('p', { class: 'error', text: errorText(err) }));
      return;
    }
    // The panel may have been closed, or the room switched, while that was in
    // flight; rendering into it then would be answering a question nobody is
    // still asking.
    if ($('stats-backdrop').hidden || state.activeRoom !== room) return;
  }

  summary.textContent = statsSummary(statsReport);
  renderStats(host, statsReport, tab);
}

// ------------------------------------------------------------------ commands

async function runCommand(line) {
  try {
    const result = await state.conn.request('command', { line, room: state.activeRoom });
    if (typeof result?.result === 'string' && result.result) notice(result.result);
    return result;
  } catch (err) {
    notice(errorText(err), 'error');
    return null;
  }
}

/**
 * Sends a message, putting the text back in the composer if it does not get
 * through - a rejected message (too long, rate limited, disconnected) must never
 * cost the user what they typed.
 */
/**
 * Sends `text` wherever the reader is: to the room, or to the person.
 *
 * A conversation goes over chat.private, which is addressed to a person and
 * needs no room - a client showing a conversation has none on screen.
 *
 * A command still goes over chat.send, and runs against the room the
 * conversation was opened from: that is where the same command would have run a
 * moment earlier, since a private message used to be typed from inside a room.
 * So /help answers, and /topic changes the topic of the channel behind the
 * conversation rather than of nothing at all.
 *
 * `//` is not a command but the escape for a line that begins with a slash - an
 * attachment's path, most of the time. chat.private takes its text verbatim, so
 * the escape is undone here rather than travelling as part of the message: left
 * in, it would turn /uploads/… into a protocol-relative address pointing at a
 * host called "uploads", which previews as nothing.
 */
function deliver(text) {
  const dm = activeDmState();
  const command = text.startsWith('/') && !text.startsWith('//');
  if (!dm || command) {
    const room = state.activeRoom ?? (state.rooms.has(state.lastRoom) ? state.lastRoom : null);
    return state.conn.request('chat.send', { room, text });
  }
  return state.conn.request('chat.private', {
    to: dm.nickname,
    text: text.startsWith('//') ? text.slice(1) : text,
  });
}

async function sendMessage(text) {
  const input = $('input-message');
  try {
    await deliver(text);
  } catch (err) {
    notice(errorText(err), 'error');
    // Only restore if they have not started typing something else meanwhile.
    if (input.value === '') {
      input.value = text;
      input.focus();
      input.setSelectionRange(text.length, text.length);
    }
  }
}

// ------------------------------------------------------------ server events

/**
 * Reflects the connection state: header label, banner, and disabling the
 * controls that cannot work while the socket is down. Leaving the composer
 * enabled while offline is how typed messages used to get lost.
 */
function renderConnection() {
  const status = state.connection;
  const label = $('conn-state');
  label.textContent = t(`ui.conn.${status === 'reconnecting' ? 'reconnecting' : status}`);
  label.className = `conn ${status === 'open' ? 'open' : status === 'connecting' || status === 'reconnecting' ? '' : 'closed'}`;

  const online = status === 'open';
  const banner = $('conn-banner');
  banner.hidden = online;
  banner.classList.toggle('offline', status === 'closed');
  if (!online) {
    $('conn-banner-text').textContent =
      status === 'reconnecting'
        ? state.reconnectSeconds > 0
          ? t('ui.conn.lostRetryIn', { seconds: state.reconnectSeconds })
          : t('ui.conn.lostRetrying')
        : status === 'connecting'
          ? t('ui.conn.connectingBanner')
          : t('ui.conn.offline');
  }

  const input = $('input-message');
  input.disabled = !online;
  const max = state.info?.policy?.maxMessageLength;
  const dm = activeDmState();
  input.placeholder = !online
    ? t('ui.messagePlaceholderOffline')
    : // Who the next line will reach, which in a conversation is the one thing
      // worth saying here - it is the difference between this box and the one
      // that writes to a whole room.
      dm
      ? t('ui.dm.placeholder', { nickname: dm.nickname })
      : max
        ? t('ui.messagePlaceholderMax', { max })
        : t('ui.messagePlaceholder');
  document.querySelector('#form-send button[type="submit"]').disabled = !online;
  for (const id of ['btn-mic', 'btn-mute', 'btn-cam', 'btn-jukebox', 'btn-attach', 'btn-settings', 'btn-newroom', 'btn-admin']) {
    const button = $(id);
    if (!button) continue;
    if (online) button.disabled = false;
    else button.disabled = id !== 'btn-settings';
  }
  if (online) renderRoomBar();
}

let countdownTimer = null;

function startReconnectCountdown(seconds) {
  clearInterval(countdownTimer);
  state.reconnectSeconds = seconds;
  renderConnection();
  countdownTimer = setInterval(() => {
    state.reconnectSeconds -= 1;
    if (state.reconnectSeconds <= 0) {
      clearInterval(countdownTimer);
      state.reconnectSeconds = 0;
    }
    if (state.connection !== 'open') renderConnection();
  }, 1000);
}

/**
 * Restores the session after the socket comes back: re-authenticates, rejoins
 * the rooms that were open, and clears media (transports do not survive, so the
 * user has to re-enable devices).
 */
async function resumeSession() {
  const rooms = [...state.rooms.keys()];
  // Read before anything is rejoined: this is the room to come back to.
  const active = state.activeRoom;
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const info = await handshake(token);

    if (!info.session) {
      if (state.resume.mode === 'guest' && state.resume.nickname) {
        const claimed = await state.conn.request('nickname.claim', { nickname: state.resume.nickname });
        state.session = claimed.session;
      } else {
        showGate(t('error.sessionEnded'));
        return;
      }
    } else {
      state.session = info.session;
    }

    // Media cannot be resumed: the transports died with the old socket.
    for (const rs of state.rooms.values()) {
      rs.media?.close();
      rs.media = null;
      for (const key of [...rs.tiles.keys()]) removeTile(rs.info.name, key);
    }

    /*
     * Rejoined the same way a refresh restores them: the room being read first,
     * the others behind it. `force` because this tab still believes it is in all
     * of them - it is the server that has forgotten, so every one has to be
     * asked for again even though the local state looks complete.
     *
     * The stored list is merged in rather than only the rooms held in memory.
     * Normally they are the same. They differ when the socket went down during a
     * restore - a tier 3 handoff arriving while the page was still opening its
     * rooms - and without this the ones not yet reached would be quietly lost.
     */
    const storedRooms = loadRooms(state.session?.nickname ?? '').rooms;
    await restoreRooms({
      first: active,
      rooms: [...new Set([...rooms, ...storedRooms])],
      force: true,
    });
    renderAll();
    notice(t('notice.reconnected'), 'info');
  } catch (err) {
    if (err.code === 'banned' || err.code === 'nickname_in_use') showGate(errorText(err));
    else notice(t('error.sessionRestoreFailed', { error: errorText(err) }), 'error');
  }
}

/**
 * Renders a notice the server sent. Keyed notices are translated locally, which
 * is what lets one room show the same event in each member's own language.
 */
function renderServerNotice(payload) {
  if (!payload?.key) return payload?.text ?? '';
  const params = { ...(payload.params ?? {}) };

  if (payload.key === 'notice.youAreOperator' || payload.key === 'notice.youAreNoLongerOperator') {
    params.scopeText =
      params.scope && params.scope !== 'global'
        ? t('notice.scopeRoom', { room: params.scope })
        : t('notice.scopeServerWide');
  }

  // A ban carries a raw timestamp so the reader formats the expiry themselves.
  if (payload.key === 'notice.userBanned') {
    params.expiry = formatExpiry(params.expiresAt ?? null);
    params.where =
      params.scope && params.scope !== 'global'
        ? t('notice.scopeRoom', { room: params.scope })
        : t('notice.scopeServerWide');
    params.reasonText = params.reason ? `: ${params.reason}` : '';
    params.messagesText = params.messages
      ? t('notice.messagesAlsoRemoved', { count: params.messages })
      : '';
  }
  return t(payload.key, params);
}

function wireServerEvents(conn) {
  conn.on('state', ({ state: connState, delay }) => {
    // A closed socket that will be retried still reads as "reconnecting" to the
    // user; otherwise the banner flickers between the two on every attempt.
    const retrying = conn.shouldReconnect && conn.everOpened;
    state.connection = connState === 'closed' && retrying ? 'reconnecting' : connState;

    if (connState === 'reconnecting') {
      startReconnectCountdown(Math.round((delay ?? 0) / 1000));
    } else if (connState === 'open' || !retrying) {
      clearInterval(countdownTimer);
      state.reconnectSeconds = 0;
    }
    renderConnection();
  });

  /*
   * The handshake, which every connection gets - including a reconnection after
   * a restart, which is exactly when the server is most likely to be serving
   * something new.
   */
  conn.on('server.hello', ({ clientBuild }) => noteServerBuild(clientBuild));

  // And the announcement, for a server whose client files changed under it
  // without anybody being disconnected.
  conn.on('client.update', ({ build }) => noteServerBuild(build));

  // The socket came back: restore identity and rooms.
  conn.on('reopen', () => {
    resumeSession().catch((err) => notice(errorText(err), 'error'));
  });

  conn.on('chat.message', (message) => {
    const entry = { ...message, kind: message.kind === 'me' ? 'me' : 'chat' };
    pushMessage(message.room, entry);
    /*
     * The chime goes here rather than in pushMessage, and the difference matters:
     * pushMessage is also how replayed history and the server's own notices
     * arrive, so hooking it would ping for every mention in the last fifty lines
     * each time somebody joined a room. Only a message genuinely arriving now
     * reaches this handler.
     *
     * Any room, not just the one on screen - a mention in a room you are not
     * looking at is precisely the one worth a sound. mentionsMe() already
     * excludes your own lines.
     */
    if (mentionsMe(entry)) playNotification();
  });

  conn.on('chat.private', (message) => {
    const entry = { ...message, kind: 'private' };
    // Both directions belong to the same conversation, and which end of it the
    // other person is on depends on who sent it: `to` on the copy of your own
    // message that comes back, `from` on theirs.
    const dm = dmState(message.outgoing ? message.to : message.from);
    if (!dm) return;
    dm.messages.push(entry);
    if (dm.messages.length > 500) dm.messages.splice(0, dm.messages.length - 500);

    const open = state.activeDm === dmKey(dm.nickname);
    if (open) {
      // Appended rather than redrawn, so a transcript scrolled up stays put and
      // the new-messages badge does the telling - the same way a room does it.
      appendToTranscript(entry);
    } else if (!message.outgoing && !fromHiddenUser(entry)) {
      dm.unread = (dm.unread ?? 0) + 1;
      refreshUnreadTitle();
    }
    renderDmList();

    // `outgoing` is the copy of your own message the server sends back so you can
    // see what you sent; pinging on that would chime at you as you type. And a
    // private message is exactly the way round somebody hidden would try next.
    if (!message.outgoing && !fromHiddenUser(entry)) playNotification();
  });

  /**
   * A kicked or banned user's messages are withdrawn: drop everything they said
   * from the rooms in scope (all rooms when `room` is null).
   */
  conn.on('chat.purge', ({ room, nickname, userId, by, removed }) => {
    const targets = room ? [room] : [...state.rooms.keys()];
    let dropped = 0;
    for (const name of targets) {
      const rs = state.rooms.get(name);
      if (!rs) continue;
      const before = rs.messages.length;
      rs.messages = rs.messages.filter((entry) => {
        if (entry.kind === 'system' || entry.kind === 'private') return true;
        // Match on account where known, so a renamed user is still covered.
        if (userId && entry.userId) return entry.userId !== userId;
        return (entry.nickname ?? '').toLowerCase() !== String(nickname ?? '').toLowerCase();
      });
      dropped += before - rs.messages.length;
      if (name === state.activeRoom) renderMessages();
    }
    if (dropped > 0 || removed > 0) {
      pushMessage(room ?? state.activeRoom, {
        kind: 'system',
        level: 'moderation',
        body: t('notice.messagesRemoved', {
          nickname,
          by: by ? t('notice.messagesRemovedBy', { actor: by }) : '',
        }),
        at: Date.now(),
      });
    }
  });

  conn.on('system.notice', (payload) => {
    pushMessage(payload.room ?? state.activeRoom, {
      kind: 'system',
      level: payload.level ?? 'info',
      // The server sends a key plus parameters where it can, so each client
      // renders the sentence in its own language; `text` is the fallback.
      body: renderServerNotice(payload),
      at: payload.at,
    });
  });

  conn.on('system.announce', (payload) => {
    pushMessage(payload.room ?? state.activeRoom, {
      kind: 'system',
      level: 'announce',
      body: `${payload.by}: ${payload.text}`,
      at: payload.at,
    });

    /*
     * An announcement is the one system message somebody deliberately sent to be
     * read, and it can arrive in a room nobody is looking at - so it gets the
     * chime, on the same footing as a mention or a private message.
     *
     * Not for the person who sent it. They are looking at the dialog they just
     * sent it from, and the other two paths already take the same care:
     * mentionsMe() ignores your own lines, and a private message chimes only on
     * the copy that is not `outgoing`.
     *
     * The chime obeys the same throttle as everything else, so an announcement
     * landing in the second after a mention is one sound rather than two. That
     * is the point of the throttle and worth more than making this the loudest
     * thing on the page.
     */
    const mine = payload.by?.toLowerCase() === state.session?.nickname?.toLowerCase();
    if (!mine) playNotification();
  });

  conn.on('session.identity', (identity) => {
    state.session = { ...state.session, ...identity };
    state.conn
      .request('session.info', {})
      .then(({ session }) => {
        state.session = session;
        renderIdentity();
        renderMembers();
      })
      .catch(() => {});
  });

  conn.on('room.joined', (payload) => applyJoin(payload));
  conn.on('room.left', ({ room }) => {
    state.rooms.delete(room);
    if (state.activeRoom === room) {
      const next = state.rooms.keys().next();
      state.activeRoom = next.done ? null : next.value;
    }
    renderRoomList();
    renderAll();
  });

  // What each of these does to the list, and why it goes by identity rather than
  // by connection, is in client/roster.js.
  conn.on('room.presence.join', ({ room, member }) => {
    const rs = existingRoom(room);
    if (!rs) return; // not our room any more
    rs.presence = joinMember(rs.presence, member);
    if (room === state.activeRoom) renderMembers();
    renderRoomList(); // keeps the sidebar head count honest
    pushMessage(room, {
      kind: 'system',
      body: member.guest
        ? t('notice.joinedGuest', { nickname: member.nickname })
        : t('notice.joined', { nickname: member.nickname, count: 1 }),
      at: Date.now(),
    });
  });

  conn.on('room.presence.leave', ({ room, peerId, key, nickname, reason, by }) => {
    const rs = existingRoom(room);
    if (!rs) return;
    // Our own departure is already handled by room.kicked / room.banned / leave.
    if (peerId === state.session?.peerId) return;
    // The list goes by identity; the tiles below go by connection, because a
    // tile really does belong to the one session that was publishing it.
    rs.presence = leaveMember(rs.presence, key);
    for (const key of [...rs.tiles.keys()]) {
      if (key.startsWith(`${peerId}:`)) removeTile(room, key);
    }
    if (room === state.activeRoom) renderMembers();
    renderRoomList();
    const moderated = reason === 'kicked' || reason === 'banned';
    pushMessage(room, {
      kind: 'system',
      level: moderated ? 'moderation' : 'info',
      body:
        reason === 'kicked'
          ? t('notice.leftKicked', { nickname, actor: by ?? '' })
          : reason === 'banned'
            ? t('notice.leftBanned', { nickname, actor: by ?? '' })
            : t('notice.left', { nickname }),
      at: Date.now(),
    });
  });

  conn.on('room.presence.update', ({ room, member }) => {
    const rs = existingRoom(room);
    if (!rs) return;
    rs.presence = updateMember(rs.presence, member);
    if (room === state.activeRoom) renderMembers();
  });

  conn.on('room.presence.rename', ({ room, fromKey, toKey, from, to }) => {
    const rs = existingRoom(room);
    if (!rs) return;
    rs.presence = renameMember(rs.presence, { fromKey, toKey, to });
    if (room === state.activeRoom) renderMembers();
    pushMessage(room, { kind: 'system', body: t('notice.renamed', { from, to }), at: Date.now() });
  });

  conn.on('room.topic', ({ room, topic, by }) => {
    const rs = existingRoom(room);
    if (!rs) return;
    rs.info.topic = topic;
    if (room === state.activeRoom) renderRoomBar();
    pushMessage(room, {
      kind: 'system',
      body: topic ? t('notice.topicSet', { actor: by, topic }) : t('notice.topicCleared', { actor: by }),
      at: Date.now(),
    });
  });

  conn.on('room.updated', ({ room, mediaEnabled, by }) => {
    const rs = existingRoom(room);
    if (!rs) return;
    rs.info.mediaEnabled = mediaEnabled;
    if (room === state.activeRoom) renderRoomBar();
    pushMessage(room, {
      kind: 'system',
      level: 'moderation',
      body: mediaEnabled ? t('notice.mediaEnabled', { actor: by }) : t('notice.mediaDisabled', { actor: by }),
      at: Date.now(),
    });
  });

  conn.on('room.kicked', ({ room, by, reason }) => {
    state.rooms.get(room)?.media?.close();
    state.rooms.delete(room);
    if (state.activeRoom === room) {
      const next = state.rooms.keys().next();
      state.activeRoom = next.done ? null : next.value;
    }
    renderRoomList();
    renderAll();
    notice(
      t('notice.youWereKicked', { room, actor: by, reason: reason ? `: ${reason}` : '' }),
      'error',
    );
  });

  conn.on('room.banned', ({ room, by, reason, expiresAt }) => {
    state.rooms.get(room)?.media?.close();
    state.rooms.delete(room);
    if (state.activeRoom === room) {
      const next = state.rooms.keys().next();
      state.activeRoom = next.done ? null : next.value;
    }
    renderRoomList();
    renderAll();
    const until = expiresAt ? t('notice.untilDate', { date: stamp(expiresAt) }) : t('notice.indefinitely');
    notice(
      t('notice.youWereBanned', { room, actor: by, until, reason: reason ? `: ${reason}` : '' }),
      'error',
    );
  });

  conn.on('session.kicked', ({ by, reason }) => {
    showGate(t('notice.youWereDisconnected', { actor: by, reason: reason ? `: ${reason}` : '' }));
  });

  conn.on('session.banned', ({ by, reason, expiresAt }) => {
    const until = expiresAt ? t('notice.untilDate', { date: stamp(expiresAt) }) : t('notice.indefinitely');
    showGate(
      t('notice.youWereBannedServer', {
        actor: by ?? t('ui.role.operator'),
        until,
        reason: reason ? `: ${reason}` : '',
      }),
    );
  });

  // A room appeared or disappeared for everyone, not just its members.
  conn.on('room.created', ({ room, topic }) => {
    if (!state.roomList.some((r) => r.name === room)) {
      state.roomList = [...state.roomList, { name: room, title: room, topic, members: 0, mediaEnabled: true }];
      renderRoomList();
    }
  });

  conn.on('room.removed', ({ room, by, listingOnly }) => {
    state.roomList = state.roomList.filter((r) => r.name !== room);
    // A room that was merely hidden still exists and we may still be in it: only
    // its place in the public listing changed.
    if (listingOnly) {
      renderRoomList();
      return;
    }
    const wasJoined = state.rooms.has(room);
    state.rooms.get(room)?.media?.close();
    state.rooms.delete(room);
    if (state.activeRoom === room) {
      const next = state.rooms.keys().next();
      state.activeRoom = next.done ? null : next.value;
    }
    renderAll();
    if (wasJoined) notice(t('notice.roomRemoved', { room, actor: by }), 'moderation');
  });

  conn.on('room.closed', ({ room, by }) => {
    state.rooms.delete(room);
    if (state.activeRoom === room) {
      const next = state.rooms.keys().next();
      state.activeRoom = next.done ? null : next.value;
    }
    renderRoomList();
    renderAll();
    notice(t('notice.roomClosed', { room, actor: by }), 'moderation');
  });

  // ---- jukebox
  // One event for the whole thing: a snapshot is small, and a queue everyone
  // can add to and remove from is not worth the bugs that diffing it would buy.
  conn.on('jukebox.state', (snapshot) => applyJukeboxState(snapshot));

  // ---- media events
  conn.on('media.producer.new', (info) => {
    const rs = state.rooms.get(info.room);
    if (!rs) return;
    ensureMedia(info.room)
      .consume(info)
      .catch((err) => notice(errorText(err), 'error', info.room));
  });

  // The publisher turned their device: turn their picture to match.
  conn.on('media.producer.orientation', ({ room, producerId, orientation }) => {
    const rs = state.rooms.get(room);
    const media = rs?.media;
    const consumerId = media?.byProducer.get(producerId);
    const entry = consumerId ? media.consumers.get(consumerId) : null;
    if (!entry) return;
    entry.info.orientation = orientation;
    const tile = rs.tiles.get(tileKey(entry.info.peerId, entry.info.source));
    if (!tile) return;
    tile.orientation = orientation;
    refreshTileRotation(tile);
  });

  conn.on('media.producer.closed', ({ room, producerId }) => {
    state.rooms.get(room)?.media?.dropProducer(producerId);
  });

  conn.on('media.force_stopped', ({ room, kinds }) => {
    const rs = state.rooms.get(room);
    if (!rs?.media) return;
    for (const source of ['mic', 'camera', 'screen']) {
      const isTargeted =
        (source === 'mic' && kinds.includes('audio')) ||
        (source === 'camera' && kinds.includes('video')) ||
        (source === 'screen' && kinds.includes('screen'));
      if (isTargeted && rs.media.isPublishing(source)) {
        rs.media.unpublish(source).catch(() => {});
        removeLocalTrack(room, source);
      }
    }
    renderMediaButtons();
    notice(t('notice.forceStopped', { kinds: kinds.join('/') }), 'moderation', room);
  });

  conn.on('media.reset', ({ room, reason }) => {
    const rs = state.rooms.get(room);
    if (rs) {
      rs.media?.close();
      rs.media = null;
      for (const key of [...rs.tiles.keys()]) removeTile(room, key);
    }
    renderMediaButtons();
    notice(reason, 'error', room);
  });
}

// ----------------------------------------------------------------- gate flow

/**
 * Returns to the sign-in screen. `level` decides whether the message reads as a
 * failure or as a normal outcome such as signing out.
 */
function showGate(message, level = 'error') {
  for (const rs of state.rooms.values()) rs.media?.close();
  state.rooms.clear();
  state.activeRoom = null;
  state.session = null;
  state.resume = { mode: null, nickname: null };
  state.orphan = [];
  state.pending.clear();
  clearInterval(countdownTimer);
  refreshUnreadTitle();
  try {
    state.conn?.close(); // also stops reconnect attempts
  } catch {
    /* ignore */
  }
  state.conn = null;
  state.connection = 'closed';
  // The rooms are gone but the player is not in one of them: an iframe left
  // behind here goes on playing over the sign-in screen.
  teardownJukeboxPlayer();
  $('jukebox').hidden = true;
  $('app').hidden = true;
  $('gate').hidden = false;
  $('admin-backdrop').hidden = true;
  $('modal-backdrop').hidden = true;
  $('settings-backdrop').hidden = true;
  setSidebar(false);

  $('gate-error').textContent = level === 'error' ? (message ?? '') : '';
  $('gate-status').textContent = level === 'error' ? '' : (message ?? '');
}

/** Opens or closes the sidebar drawer (only visible on narrow screens). */
function setSidebar(open) {
  state.sidebarOpen = open;
  document.getElementById('sidebar').classList.toggle('open', open);
  const toggle = $('btn-sidebar');
  toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  toggle.setAttribute('aria-label', open ? 'Hide rooms and members' : 'Show rooms and members');
}

function showApp() {
  $('gate').hidden = true;
  $('app').hidden = false;
  renderAll();
}

/**
 * Mirrors the server's limits into the form controls, so the UI cannot invite
 * input the server will reject.
 */
function applyServerPolicy(policy) {
  if (!policy) return;

  // Offer the room this server actually has, instead of a hardcoded guess the
  // user would be refused for.
  const roomField = $('guest-room');
  if (policy.defaultRoom && !roomField.dataset.touched) {
    roomField.value = policy.defaultRoom;
    roomField.placeholder = policy.defaultRoom;
  }

  const message = $('input-message');
  if (policy.maxMessageLength) {
    message.setAttribute('maxlength', String(policy.maxMessageLength));
    message.placeholder = t('ui.messagePlaceholderMax', { max: policy.maxMessageLength });
  }
  if (policy.minPasswordLength) {
    for (const id of ['login-pass', 'reg-pass', 'reg-pass2']) {
      $(id).setAttribute('minlength', String(policy.minPasswordLength));
    }
  }
  if (policy.nicknameMaxLength) {
    for (const id of ['guest-nick', 'login-nick', 'reg-nick']) {
      $(id).setAttribute('maxlength', String(policy.nicknameMaxLength));
    }
  }
  if (policy.registrationEnabled === false) {
    document.querySelector('[data-tab="register"]').hidden = true;
  }
}

async function connect() {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  const conn = new Connection(`${scheme}://${location.host}/ws`);
  state.conn = conn;
  wireServerEvents(conn);
  await conn.connect();
  return conn;
}

async function handshake(token) {
  // Send the fingerprint with the handshake when it is ready in time; otherwise
  // report it as soon as it resolves, before any nickname is claimed.
  const fingerprint = await fingerprintWithin(1500);
  const info = await state.conn.request('hello', {
    ...(token ? { token } : {}),
    ...(fingerprint ? { fingerprint } : {}),
    // So the server words its own messages (errors, refusals) in our language.
    locale: getLocale(),
  });
  applyServerPolicy(info.policy);
  if (!fingerprint) {
    collectFingerprint()
      .then((late) => {
        if (late && state.conn?.state === 'open') {
          return state.conn.request('session.fingerprint', late).catch((err) => {
            if (err.code === 'banned') showGate(err.message);
          });
        }
        return null;
      })
      .catch(() => {});
  }
  state.info = info;
  state.roomList = info.rooms ?? [];
  if (info.session) state.session = info.session;
  if (info.tokenError) {
    localStorage.removeItem(TOKEN_KEY);
    $('gate-status').textContent = info.tokenError.message;
  }
  return info;
}

async function startSession({ mode, nickname, password, room, remember = true }) {
  const errorBox = $('gate-error');
  const statusBox = $('gate-status');
  errorBox.textContent = '';
  statusBox.textContent = t('ui.gate.connecting');

  try {
    if (!state.conn || state.conn.state !== 'open') await connect();
    await handshake(mode === 'guest' ? null : localStorage.getItem(TOKEN_KEY));

    if (mode === 'guest') {
      // Wait for the fingerprint before claiming the nickname: claiming first
      // would leave a window in which a ban could not record this browser.
      statusBox.textContent = t('ui.gate.checkingBrowser');
      const fingerprint = await fingerprintWithin(4000);
      statusBox.textContent = t('ui.gate.joining');
      const { session } = await state.conn.request('nickname.claim', {
        nickname,
        ...(fingerprint ? { fingerprint } : {}),
      });
      state.session = session;
      state.resume = { mode: 'guest', nickname: session.nickname };
      localStorage.removeItem(TOKEN_KEY);
    } else if (mode === 'login') {
      const result = await state.conn.request('auth.login', { nickname, password });
      state.session = result.session;
      state.resume = { mode: 'account', nickname: result.session.nickname };
      if (remember) localStorage.setItem(TOKEN_KEY, result.token);
    } else if (mode === 'register') {
      const result = await state.conn.request('auth.register', { nickname, password });
      state.session = result.session;
      state.resume = { mode: 'account', nickname: result.session.nickname };
      localStorage.setItem(TOKEN_KEY, result.token);
    } else if (mode === 'token') {
      if (!state.session) throw new Error(t('error.sessionExpired'));
      state.resume = { mode: 'account', nickname: state.session.nickname };
    }

    statusBox.textContent = '';
    showApp();
    const target = String(room || defaultRoomName()).replace(/^#+/, '').toLowerCase();
    if (target) await joinRoom(target);
    // Anything else this nickname had open. Nothing is restored for a nickname
    // that has never been here, and nothing at all for a different one - see
    // session-rooms.js on why a stored list is not shared between people.
    await restoreRooms({ first: target });
    $('input-message').focus();
  } catch (err) {
    statusBox.textContent = '';
    errorBox.textContent = errorText(err);
    if (err.code === 'nickname_registered') {
      selectTab('login');
      $('login-nick').value = nickname ?? '';
      $('login-pass').focus();
    }
  }
}

function selectTab(name) {
  for (const tab of document.querySelectorAll('.gate .tab')) {
    tab.classList.toggle('active', tab.dataset.tab === name);
  }
  for (const pane of ['guest', 'login', 'register']) {
    $(`form-${pane}`).classList.toggle('active', pane === name);
  }
  $('gate-error').textContent = '';
}

// -------------------------------------------------------------------- wiring

/** Fills a <select> with the available languages. */
function fillLocaleSelect(select) {
  if (!select) return;
  select.replaceChildren(
    ...LOCALES.map((entry) => el('option', { value: entry.code, text: entry.name })),
  );
  select.value = getLocale();
}

/**
 * Re-renders everything after a language change: static markup, the live views,
 * and the server is told so its own messages switch language too.
 */
function applyLocaleEverywhere() {
  applyTranslations();
  fillLocaleSelect($('gate-locale'));
  fillLocaleSelect($('dev-locale'));
  applyServerPolicy(state.info?.policy);
  // Its labels are written by hand rather than by data-i18n, so applyTranslations
  // above does not reach them.
  renderMasterVolume();
  renderAll();
  if (state.conn?.isOpen) {
    state.conn.request('session.locale', { locale: getLocale() }).catch(() => {});
  }
}

function wireUi() {
  for (const tab of document.querySelectorAll('.gate .tab')) {
    tab.addEventListener('click', () => selectTab(tab.dataset.tab));
  }

  // ---- language
  fillLocaleSelect($('gate-locale'));
  fillLocaleSelect($('dev-locale'));
  for (const id of ['gate-locale', 'dev-locale']) {
    $(id).addEventListener('change', (event) => setLocale(event.target.value));
  }
  onLocaleChange(() => applyLocaleEverywhere());

  $('form-guest').addEventListener('submit', (event) => {
    event.preventDefault();
    startSession({
      mode: 'guest',
      nickname: $('guest-nick').value.trim(),
      room: $('guest-room').value.trim(), // blank means the server's default room
    });
  });

  $('form-login').addEventListener('submit', (event) => {
    event.preventDefault();
    startSession({
      mode: 'login',
      nickname: $('login-nick').value.trim(),
      password: $('login-pass').value,
      remember: $('login-remember').checked,
      // room omitted: startSession falls back to the server's default room
    });
  });

  $('form-register').addEventListener('submit', (event) => {
    event.preventDefault();
    const password = $('reg-pass').value;
    if (password !== $('reg-pass2').value) {
      $('gate-error').textContent = t('ui.gate.passwordMismatch');
      return;
    }
    startSession({ mode: 'register', nickname: $('reg-nick').value.trim(), password });
  });

  // Live nickname availability feedback for guests.
  let checkTimer = null;
  $('guest-nick').addEventListener('input', () => {
    clearTimeout(checkTimer);
    const value = $('guest-nick').value.trim();
    const hint = $('guest-hint');
    if (value.length < 2) {
      hint.textContent = t('ui.gate.guestHint');
      hint.style.color = ''; // back to the neutral hint colour
      return;
    }
    checkTimer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/nickname/${encodeURIComponent(value)}`);
        const result = await response.json();
        hint.textContent = result.available
          ? t('ui.gate.nicknameAvailable', { nickname: result.nickname })
          : (result.message ?? t('ui.gate.nicknameUnavailable'));
        hint.style.color = result.available ? 'var(--accent-2)' : 'var(--warn)';
      } catch {
        /* ignore */
      }
    }, 350);
  });

  $('form-send').addEventListener('submit', (event) => {
    event.preventDefault();
    const input = $('input-message');
    const text = input.value;
    if (!text.trim()) return;
    // A conversation is somewhere to write, so the room is only required when
    // there is no conversation on screen either.
    if (!state.activeRoom && !state.activeDm) {
      notice(t('notice.joinRoomFirst'), 'error');
      return;
    }
    input.value = '';
    sendMessage(text);
  });

  // Mic starts/stops the audio stream; Mute pauses it without leaving.
  $('btn-mic').addEventListener('click', () => toggleMicrophone());
  $('btn-mute').addEventListener('click', () => toggleMute());
  $('btn-cam').addEventListener('click', () => toggleSource('camera'));
  $('btn-room-actions').addEventListener('click', () => openRoomActions());

  // ---- settings dialog: device selection, quality, screen share
  $('btn-settings').addEventListener('click', () => openSettings());
  $('settings-close').addEventListener('click', () => closeSettings());

  $('dev-audio-input').addEventListener('change', async (event) => {
    updateSettings({ audioInput: event.target.value });
    stopMicTest();
    await republish('mic');
  });
  $('dev-video-input').addEventListener('change', async (event) => {
    updateSettings({ videoInput: event.target.value });
    await republish('camera');
  });
  $('dev-audio-output').addEventListener('change', async (event) => {
    updateSettings({ audioOutput: event.target.value });
    await applyOutputToAll();
  });
  $('dev-quality').addEventListener('change', async (event) => {
    updateSettings({ videoQuality: event.target.value });
    await republish('camera');
  });
  watchOrientation();
  renderMasterVolume();
  $('master-volume').addEventListener('input', (event) => {
    const level = gainForSlider(event.target.value);
    // Moving the slider off zero un-mutes: reaching for the volume is asking to
    // hear something, and leaving it silent would look like a broken slider.
    updateSettings({ masterVolume: level, ...(level > 0 ? { masterMuted: false } : {}) });
    renderMasterVolume();
    applyVolumeToAll();
  });
  // The icon is the mute for everything incoming - the room and the jukebox.
  $('btn-master-mute').addEventListener('click', () => {
    const { masterMuted: muted = false, masterVolume = 1 } = getSettings();
    const next = !(muted || masterVolume === 0);
    // Un-muting from a level of zero has to give the level back too, or nothing
    // is heard and the button appears not to work.
    updateSettings({ masterMuted: next, ...(!next && masterVolume === 0 ? { masterVolume: 1 } : {}) });
    renderMasterVolume();
    applyVolumeToAll();
  });
  $('btn-jukebox').addEventListener('click', () => {
    updateSettings({ showYouTube: !wantsYouTube() });
    // renderJukebox does the whole job in both directions: it hides the panel and
    // tears the player down when off, and builds it back up when on - and it
    // redraws this button on the way through.
    renderJukebox();
  });

  $('dev-notify').addEventListener('change', (event) => {
    updateSettings({ notificationSound: event.target.checked });
    /*
     * Switching it on plays it, the way the appearance dialog applies a theme as
     * it is chosen: a sound you cannot hear until the next time somebody happens
     * to name you is a setting nobody can check. `force` because the throttle and
     * the setting itself should not swallow the one deliberate press.
     */
    if (event.target.checked) playNotification({ force: true });
  });

  /*
   * Push to talk. The button opens the microphone while it is held, by pointer
   * or by keyboard, and closes it on release - including when the pointer leaves
   * the button or the window loses focus, so it can never be left open by a
   * gesture that ended somewhere else.
   */
  const talk = $('btn-talk');
  const startTalking = () => {
    if (state.activeRoom) setMicMuted(state.activeRoom, false);
  };
  const stopTalking = () => {
    if (state.activeRoom && getSettings().pushToTalk) setMicMuted(state.activeRoom, true);
  };
  talk.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    startTalking();
    try {
      // Keeps the release with this button even if the pointer slides off it.
      // Attempted after opening the microphone, never before: capturing can
      // throw when the pointer is already gone, and that must not swallow the
      // press and leave the button dead.
      talk.setPointerCapture?.(event.pointerId);
    } catch {
      /* the release still arrives through pointerup or the window losing focus */
    }
  });
  for (const event of ['pointerup', 'pointercancel', 'lostpointercapture']) {
    talk.addEventListener(event, stopTalking);
  }
  talk.addEventListener('keydown', (event) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (!event.repeat) startTalking();
  });
  talk.addEventListener('keyup', (event) => {
    if (event.key === ' ' || event.key === 'Enter') stopTalking();
  });
  talk.addEventListener('blur', stopTalking);
  window.addEventListener('blur', stopTalking);

  $('dev-ptt').addEventListener('change', async (event) => {
    const on = event.target.checked;
    updateSettings({ pushToTalk: on });
    // Switching it on closes the microphone immediately: with push to talk the
    // resting state is muted, and leaving it open would be the opposite of what
    // was just asked for. Switching it off leaves the microphone as it is, and
    // the Mute button comes back showing that state.
    if (on && state.activeRoom) await setMicMuted(state.activeRoom, true);
    renderMediaButtons();
  });

  $('btn-test-mic').addEventListener('click', () => (micTest ? stopMicTest() : startMicTest()));

  // Unlock device enumeration without publishing anything.
  $('btn-device-access').addEventListener('click', async () => {
    const button = $('btn-device-access');
    button.disabled = true;
    const granted = await requestDeviceAccess();
    button.disabled = false;
    if (granted) await openSettings();
    else {
      $('settings-hint').textContent = t('ui.settings.accessRefused');
    }
  });

  $('btn-screen').addEventListener('click', async () => {
    await toggleSource('screen');
    const live = state.activeRoom
      ? Boolean(state.rooms.get(state.activeRoom)?.media?.isPublishing('screen'))
      : false;
    $('btn-screen').classList.toggle('on', live);
    $('btn-screen').querySelector('span').textContent = live
      ? t('ui.settings.stopSharing')
      : t('ui.settings.shareScreen');
  });

  // Re-check the device list when hardware is plugged in or removed.
  navigator.mediaDevices?.addEventListener?.('devicechange', () => {
    if (!$('settings-backdrop').hidden) openSettings();
    else pruneMissingDevices();
  });

  $('btn-newroom').addEventListener('click', () => {
    const input = el('input', { placeholder: t('ui.newRoom.namePlaceholder'), maxlength: 32 });
    const hidden = el('input', { type: 'checkbox', id: 'new-room-hidden' });
    const canCreate = (capsFor() ?? {})['room.create'];
    const body = el(
      'div',
      {},
      el('label', { text: t('ui.newRoom.name') }),
      input,
      canCreate ? el('label', { class: 'checkbox' }, hidden, ` ${t('ui.newRoom.hidden')}`) : null,
      canCreate ? el('p', { class: 'hint', text: t('ui.newRoom.hiddenHint') }) : null,
    );
    openModal({
      title: t('ui.newRoom.title'),
      body,
      confirmText: t('ui.newRoom.join'),
      onConfirm: async () => {
        const name = input.value.trim();
        if (!name) throw new Error(t('ui.newRoom.nameRequired'));
        await joinRoom(name, { hidden: hidden.checked });
      },
    });
  });

  // ---- jukebox
  renderJukeboxVolume();
  $('jukebox-volume').addEventListener('input', (event) => {
    // Moving the slider off zero un-mutes: reaching for the volume is asking to
    // hear something, and leaving it silent would look like a broken slider.
    const level = gainForSlider(event.target.value, JUKEBOX_MIN_DB);
    updateSettings({ jukeboxVolume: level, ...(level > 0 ? { jukeboxMuted: false } : {}) });
    renderJukeboxVolume();
    applyJukeboxVolume();
  });
  $('jukebox-mute').addEventListener('click', () => {
    const { jukeboxMuted = false, jukeboxVolume = 1 } = getSettings();
    // Un-muting from a level of zero has to give the level back too, or nothing
    // is heard and the button appears not to work.
    const muted = !(jukeboxMuted || jukeboxVolume === 0);
    updateSettings({ jukeboxMuted: muted, ...(!muted && jukeboxVolume === 0 ? { jukeboxVolume: 0.6 } : {}) });
    renderJukeboxVolume();
    applyJukeboxVolume();
  });
  wireJukeboxResize();
  wireStageResize();
  $('btn-jukebox-add').addEventListener('click', () => {
    if (state.activeRoom) openJukeboxAddDialog(state.activeRoom);
  });
  // The browser refused to start the sound by itself; this is the gesture that
  // lets it. Any other click on the page counts too - see below.
  $('jukebox-blocked').addEventListener('click', () => jukeboxPlayer?.unblock());
  for (const event of ['pointerdown', 'keydown']) {
    document.addEventListener(event, () => jukeboxPlayer?.unblock(), true);
  }

  $('btn-user-menu').addEventListener('click', () => openUserMenu());

  $('btn-new-messages').addEventListener('click', () => {
    scrollTranscriptToBottom();
    $('input-message').focus();
  });
  // Scrolling back down by hand counts as having caught up.
  $('messages').addEventListener('scroll', () => {
    state.stickToBottom = transcriptAtBottom();
    if (state.missed > 0 && state.stickToBottom) setMissedMessages(0);
  });

  // Attachments: the visible button opens the hidden file input.
  const fileInput = $('file-attach');
  fileInput.setAttribute('accept', ACCEPTED_TYPES);
  for (const id of ['file-attach', 'camera-photo', 'camera-video']) {
    const input = $(id);
    input.addEventListener('change', async () => {
      const files = [...input.files];
      // Cleared first so choosing the same file twice in a row still fires.
      input.value = '';
      await attachFiles(files);
    });
  }

  // Where a camera can actually be reached, the paperclip offers the choice
  // rather than growing a second button beside it: the composer on a phone has
  // room for the message and little else.
  //
  // Two conditions, and both are needed. The `capture` property exists only
  // where the browser implements media capture at all - Android's Chrome and
  // Firefox have it, desktop Chrome does not even reflect the attribute - so it
  // is the honest test for "tapping this reaches a camera app". The pointer is
  // asked as well, because a browser that reflects the property while running on
  // a laptop would otherwise be given a menu whose two camera entries both open
  // a file dialog.
  const hasCamera =
    'capture' in $('camera-photo') && window.matchMedia?.('(pointer: coarse)').matches === true;
  const attachButton = $('btn-attach');
  if (hasCamera) {
    attachButton.setAttribute('aria-haspopup', 'true');
    attachButton.setAttribute('aria-expanded', 'false');
    attachButton.setAttribute('aria-controls', 'attach-menu');
  }
  attachButton.addEventListener('click', (event) => {
    // Nothing to choose between without a camera, and a menu of one entry is a
    // worse file picker than the file picker.
    if (!hasCamera) {
      fileInput.click();
      return;
    }
    event.stopPropagation(); // or the document handler below closes it again
    setAttachMenu($('attach-menu').hidden);
  });
  for (const choice of document.querySelectorAll('.attach-choice')) {
    choice.addEventListener('click', () => {
      const input = { file: fileInput, photo: $('camera-photo'), video: $('camera-video') }[
        choice.dataset.attach
      ];
      // Closed first: the camera app takes the screen for as long as it takes to
      // frame a shot, and coming back to a menu still hanging open reads as a
      // tap that did not register.
      setAttachMenu(false);
      input?.click();
    });
  }
  document.addEventListener('click', (event) => {
    if ($('attach-menu').hidden) return;
    if (event.target.closest('#attach-menu, #btn-attach')) return;
    setAttachMenu(false);
  });

  // Pasting a screenshot into the message box attaches it, which is how most
  // pictures reach a chat room.
  $('input-message').addEventListener('paste', (event) => {
    const files = [...(event.clipboardData?.files ?? [])];
    if (files.length === 0) return;
    event.preventDefault();
    attachFiles(files);
  });

  // Tile sizing depends on the space available. A ResizeObserver catches every
  // cause - window resize, the sidebar drawer, and a background tab that had no
  // layout until it was shown - which plain resize events miss. Watched with the
  // room itself is everything in it that takes space the stage would otherwise
  // have: the connection banner as it comes and goes, the room bar as a topic
  // wraps, the composer as the attachment line appears or its buttons wrap.
  //
  // The stage is watched too, as a backstop for a layout gone stale however it
  // got there. That is safe despite layoutVideoGrid() sizing the stage, because
  // it measures only the room and the rest of what is in it: a second run off
  // the back of its own resize reads the same numbers, writes the same values,
  // and resizes nothing - so it settles rather than looping.
  if (typeof ResizeObserver === 'function') {
    const observer = new ResizeObserver(() => layoutVideoGrid());
    for (const selector of ['.main', '.banner', '.roombar', '.composer', '.video-grid']) {
      observer.observe(document.querySelector(selector));
    }
  } else {
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => layoutVideoGrid(), 80);
    });
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) layoutVideoGrid();
  });

  // ---- sidebar drawer (narrow screens)
  $('btn-sidebar').addEventListener('click', () => {
    setSidebar(!state.sidebarOpen);
    layoutVideoGrid(); // the drawer changes the width available to the grid
  });
  $('update-banner-refresh').addEventListener('click', () => {
    /*
     * A plain reload. Everything needed to come back to the same place is
     * already stored - the token, the rooms, which one was being read - so this
     * costs the reader nothing but the moment it takes.
     */
    location.reload();
  });

  $('update-banner-dismiss').addEventListener('click', () => {
    state.updateDismissed = state.serverBuild;
    renderUpdateBanner();
  });

  $('conn-banner-retry').addEventListener('click', () => {
    $('conn-banner-text').textContent = t('ui.conn.lostRetrying');
    state.conn?.retryNow();
  });

  // ---- stage arrangement
  // Moving the cameras can move the jukebox with them - three columns do not
  // fit where two did - so the split is re-read alongside the tiles.
  $('btn-stage-layout').addEventListener('click', () => {
    setStageLayout(effectiveStageLayout() === 'beside' ? 'above' : 'beside');
    renderStageToggle();
    refreshJukeboxSplit();
    // The stage's own split turns with it, from a height to a width.
    refreshStageShare();
    layoutVideoGrid();
  });
  // Only while nobody has chosen: turning a laptop's window from tall to wide
  // moves the stage beside the transcript by itself, and relabels the toggle.
  onAutoStageChange(() => {
    applyStageLayout();
    renderStageToggle();
    refreshJukeboxSplit();
    refreshStageShare();
    layoutVideoGrid();
  });

  // ---- emotes
  applyEmoteMetrics();
  const hymy = emoteFor('hymy');
  if (hymy) $('btn-emotes').append(emoteNode(hymy, { still: true }));
  $('btn-emotes').addEventListener('click', (event) => {
    event.stopPropagation(); // or the document handler below closes it again
    setEmotePicker($('emote-picker').hidden);
  });
  document.addEventListener('click', (event) => {
    if ($('emote-picker').hidden) return;
    if (event.target.closest('#emote-picker, #btn-emotes')) return;
    setEmotePicker(false);
  });

  $('btn-admin').addEventListener('click', () => showAdmin('operators'));
  $('admin-close').addEventListener('click', () => {
    $('admin-backdrop').hidden = true;
  });
  for (const button of document.querySelectorAll('[data-atab]')) {
    button.addEventListener('click', () => showAdmin(button.dataset.atab));
  }

  $('stats-close').addEventListener('click', () => {
    $('stats-backdrop').hidden = true;
  });
  for (const button of document.querySelectorAll('[data-stab]')) {
    button.addEventListener('click', () => showStats(button.dataset.stab));
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      $('modal-cancel').click();
      $('admin-backdrop').hidden = true;
      $('stats-backdrop').hidden = true;
      closeSettings();
      closeTileControls();
      if (!$('emote-picker').hidden) setEmotePicker(false);
      if (!$('attach-menu').hidden) setAttachMenu(false);
      if (state.sidebarOpen) setSidebar(false);
    }
  });

  // Anything that is not a tile puts the open tile's controls away, so they do
  // not sit over someone's face until the tile is tapped again.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.tile')) closeTileControls();
  });
}

// ---------------------------------------------------------------- bootstrap

async function boot() {
  applyTranslations();
  wireUi();
  selectTab('guest');

  // Adopt the server's policy before the user types anything, so the join form
  // matches this deployment (default room, limits, whether registration is on).
  $('guest-room').addEventListener('input', (event) => {
    event.target.dataset.touched = '1';
  });
  fetch('/api/info')
    .then((response) => response.json())
    .then(({ policy }) => applyServerPolicy(policy))
    .catch(() => {});

  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  // Try to restore a signed-in session automatically.
  $('gate-status').textContent = t('ui.gate.restoring');
  try {
    await connect();
    const info = await handshake(token);
    if (info.session) {
      state.session = info.session;
      showApp();
      /*
       * The rooms this browser had open, rather than the default one. A refresh
       * used to cost every room but one, which on a busy account meant finding
       * three or four of them again by hand each time the page was reloaded.
       */
      const { rooms } = loadRooms(info.session.nickname);
      if (rooms.length > 0) {
        await restoreRooms();
      } else {
        const home = defaultRoomName();
        if (home) await joinRoom(home);
      }
      $('input-message').focus();
    } else {
      $('gate-status').textContent = '';
    }
  } catch (err) {
    $('gate-status').textContent = '';
    $('gate-error').textContent = errorText(err);
  }
}

/**
 * The console banner.
 *
 * Written as an array of single-quoted lines rather than a template literal,
 * because the art contains a backtick as well as backslashes - a template
 * literal would end early on the one and need escaping for the others, and
 * String.raw cannot express an escaped backtick without leaving the backslash
 * in the output. Quoted lines keep what is printed identical to the source.
 */
const BANNER = [
  '                            _                          ',
  ' _      _____  ____  ____  (_)____ _________  ____ ___ ',
  '| | /| / / _ \\/ __ \\/ __ \\/ / ___// ___/ __ \\/ __ `__ \\',
  '| |/ |/ /  __/ /_/ / /_/ / (__  )/ /__/ /_/ / / / / / /',
  '|__/|__/\\___/ .___/ .___/_/____(_)___/\\____/_/ /_/ /_/ ',
  '           /_/   /_/',
];

function printBanner() {
  // One call rather than one per line: a console groups repeated identical
  // calls and collapses them, which would take the middle out of the drawing.
  try {
    console.log(BANNER.join('\n'));
  } catch {
    // A browser with no console, or one that has had it taken away. Nothing
    // here is worth failing a page load for.
  }
}

printBanner();

// Before anything is drawn: the stylesheet already has the operating system's
// preference covered, so this only matters when an explicit choice was saved.
applyTheme();
// While following the system, a change to it repaints the browser's own chrome;
// the page's colours are the stylesheet's job and need no help.
onSystemThemeChange(() => applyTheme());

applyStageLayout();

// Decoration behind the transcript, if it is switched on and the browser can
// manage it. It does nothing at all otherwise, so there is nothing to check.
setupFluid($('messages-fluid'), $('messages'));

boot();
